import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import {
    ArrowLeft,
    Edit,
    Save,
    X,
    FileText,
    DollarSign,
    Upload,
    Receipt,
    ClipboardCheck,
    Wrench,
    CreditCard,
    Clock,
    MapPin,
    Phone,
    Mail,
    User,
    Users,
    Calendar as CalendarIcon,
    CheckCircle2,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Image as ImageIcon,
    Download,
    Eye,
    Trash2,
    Plus,
    Search,
    Loader2,
    ExternalLink,
    Copy,
    Link,
    Star,
    XCircle,
    PauseCircle,
    PlayCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useGoogleMaps, initializeMap, createMarker, createSearchBox, reverseGeocode } from '../hooks/useGoogleMaps';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import { useCalendarHolidays } from '../hooks/useHolidays';
import QuotationPreparationModal from '../components/QuotationPreparationModal';
import AdditionalWorkModal from '../components/AdditionalWorkModal';
import QuotationApprovalDialog from '../components/QuotationApprovalDialog';
import { StageName, BookingStatus, PRE_SV_STAGES, QuotationType, QuotationStatus, DocumentTypeCode, DocumentReferenceType, PaymentLinkStatus } from '../constants/stageConstants';
import ProjectRequests from '../components/project/ProjectRequests';
import ProjectNotifications from '../components/project/ProjectNotifications';

const ProjectViewEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { holidayDates, isHoliday, getHolidayName, onMonthChange: onHolidayMonthChange } = useCalendarHolidays();
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [mapModalOpen, setMapModalOpen] = useState(false);
    const [allEntities, setAllEntities] = useState([]); // All available entities from master
    const [originalProjectData, setOriginalProjectData] = useState(null); // Store original data for cancel
    const [documents, setDocuments] = useState([]); // Project documents from API
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [downloadingDocId, setDownloadingDocId] = useState(null); // Track which document is downloading
    const [deletingDocId, setDeletingDocId] = useState(null); // Track which document is being deleted
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false); // Confirmation dialog for deletion
    const [documentToDelete, setDocumentToDelete] = useState(null); // Document pending deletion

    // Quotation state
    const [quotations, setQuotations] = useState([]); // Project quotations from API
    const [loadingQuotations, setLoadingQuotations] = useState(false);
    const [quotationModalOpen, setQuotationModalOpen] = useState(false);
    const [quotationType, setQuotationType] = useState(QuotationType.BaseWork); // base_work or additional_work
    const [selectedQuotation, setSelectedQuotation] = useState(null); // For viewing quotation details
    const [quotationDetailsOpen, setQuotationDetailsOpen] = useState(false); // Quotation details modal
    const [quotationDocumentUploadOpen, setQuotationDocumentUploadOpen] = useState(false); // Quotation document upload dialog
    const [quotationForUpload, setQuotationForUpload] = useState(null); // Quotation to upload document for
    const [editingQuotation, setEditingQuotation] = useState(null); // Quotation being edited
    const [additionalWorkModalOpen, setAdditionalWorkModalOpen] = useState(false); // Additional work modal
    const [quotationDeleteConfirmOpen, setQuotationDeleteConfirmOpen] = useState(false); // Delete quotation confirmation
    const [quotationToDelete, setQuotationToDelete] = useState(null); // Quotation pending deletion
    const [deletingQuotation, setDeletingQuotation] = useState(false); // Delete in progress
    const [quotationApprovalOpen, setQuotationApprovalOpen] = useState(false); // Quotation approval dialog for OEM
    const [quotationForApproval, setQuotationForApproval] = useState(null); // Quotation being approved by OEM
    const [uploadingQuotationDoc, setUploadingQuotationDoc] = useState(false);
    const quotationDocFileInputRef = useRef(null);
    const [quotationDocuments, setQuotationDocuments] = useState([]); // Documents for selected quotation
    const [loadingQuotationDocuments, setLoadingQuotationDocuments] = useState(false);

    // Upload dialog state
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [filesToUpload, setFilesToUpload] = useState([]); // Files with their categories
    const [isUploading, setIsUploading] = useState(false);

    // Hardware upload state
    const [hardwareUploading, setHardwareUploading] = useState(false);
    const hardwareFileInputRef = useRef(null);
    const uploadFileInputRef = useRef(null);

    // Image viewer state
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imageUrls, setImageUrls] = useState({}); // Store SAS URLs for images

    // Payment state
    const [paymentSummary, setPaymentSummary] = useState(null);
    const [loadingPayments, setLoadingPayments] = useState(false);
    const [generateLinkOpen, setGenerateLinkOpen] = useState(false);
    const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
    const [generatingLink, setGeneratingLink] = useState(false);
    const [recordingPayment, setRecordingPayment] = useState(false);
    const [linkForm, setLinkForm] = useState({ amount: '', description: '', expiryHours: 72 });
    const [paymentForm, setPaymentForm] = useState({
        amount: '', paymentType: 'neft_online', paymentDate: new Date().toISOString().split('T')[0],
        transactionReference: '', chequeNumber: '', chequeDate: '', chequeBankName: '', upiId: '', notes: ''
    });

    // Site visits state
    const [siteVisitsData, setSiteVisitsData] = useState(null);
    const [loadingSiteVisits, setLoadingSiteVisits] = useState(false);

    // History/timeline state
    const [timelineEvents, setTimelineEvents] = useState([]);
    const [loadingTimeline, setLoadingTimeline] = useState(false);

    // Bills state
    const [bills, setBills] = useState([]);
    const [loadingBills, setLoadingBills] = useState(false);
    const [uploadingBill, setUploadingBill] = useState(false);
    const [billDialogOpen, setBillDialogOpen] = useState(false);
    const [billForm, setBillForm] = useState({ name: '', notes: '', file: null });
    const [editingBillId, setEditingBillId] = useState(null);
    const [editBillNotes, setEditBillNotes] = useState('');
    const billFileRef = useRef(null);

    // Installation data state
    const [installationData, setInstallationData] = useState(null);
    const [loadingInstallation, setLoadingInstallation] = useState(false);
    const [changeSupervisorOpen, setChangeSupervisorOpen] = useState(false);
    const [allSupervisors, setAllSupervisors] = useState([]);
    const [selectedNewSupervisorId, setSelectedNewSupervisorId] = useState('');
    const [savingSupervisor, setSavingSupervisor] = useState(false);
    const [progressReports, setProgressReports] = useState([]);
    const [loadingProgressReports, setLoadingProgressReports] = useState(false);
    const [togglingOverride, setTogglingOverride] = useState(false);
    const [radiusInput, setRadiusInput] = useState('');
    const [savingRadius, setSavingRadius] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingProject, setDeletingProject] = useState(false);
    const [reportPhotosOpen, setReportPhotosOpen] = useState(null); // report id whose photos are shown

    // On-Hold state
    const [holdDialogOpen, setHoldDialogOpen] = useState(false);
    const [holdReason, setHoldReason] = useState('');
    const [holdingProject, setHoldingProject] = useState(false);
    const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
    const [resumingProject, setResumingProject] = useState(false);

    // Sign-off tab state
    const [signoffDetails, setSignoffDetails] = useState(null);
    const [signoffLoading, setSignoffLoading] = useState(false);
    const [signoffRating, setSignoffRating] = useState(0);
    const [signoffHoverRating, setSignoffHoverRating] = useState(0);
    const [signoffNotes, setSignoffNotes] = useState('');
    const [signoffSubmitting, setSignoffSubmitting] = useState(false);
    const [signoffItemPhotos, setSignoffItemPhotos] = useState({});
    const [signoffPhotosExpanded, setSignoffPhotosExpanded] = useState({});
    const [signoffEnlargedPhoto, setSignoffEnlargedPhoto] = useState(null);
    // Team management state
    const [projectTeams, setProjectTeams] = useState([]);
    const [loadingTeams, setLoadingTeams] = useState(false);
    const [addTeamOpen, setAddTeamOpen] = useState(false);
    const [allTeams, setAllTeams] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [addingTeam, setAddingTeam] = useState(false);
    const [removingTeamId, setRemovingTeamId] = useState(null);
    const [expandedTeamId, setExpandedTeamId] = useState(null);
    const [teamMembersMap, setTeamMembersMap] = useState({}); // { teamId: members[] }
    const [loadingMembersTeamId, setLoadingMembersTeamId] = useState(null);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [loadingAttendance, setLoadingAttendance] = useState(false);

    const [sv1ChecklistOpen, setSv1ChecklistOpen] = useState(false);
    const [sv2ChecklistOpen, setSv2ChecklistOpen] = useState(false);
    const [svItemPhotos, setSvItemPhotos] = useState({}); // { responseId: [{ id, url, fileName }] }
    const [svPhotosExpanded, setSvPhotosExpanded] = useState({}); // { responseId: true/false }
    const [instExpandedEntities, setInstExpandedEntities] = useState({});
    const [instExpandedUnits, setInstExpandedUnits] = useState({});
    const [svLoadingPhotos, setSvLoadingPhotos] = useState(null); // responseId being loaded
    const [svEnlargedPhoto, setSvEnlargedPhoto] = useState(null); // { url, fileName }

    // Edit SV1 dialog state
    const [editSv1Open, setEditSv1Open] = useState(false);
    const [editSv1Date, setEditSv1Date] = useState(null);
    const [editSv1SlotId, setEditSv1SlotId] = useState('');
    const [editSv1SupervisorId, setEditSv1SupervisorId] = useState('');
    const [editSv1Slots, setEditSv1Slots] = useState([]);
    const [editSv1Supervisors, setEditSv1Supervisors] = useState([]);
    const [editSv1LoadingSlots, setEditSv1LoadingSlots] = useState(false);
    const [editSv1LoadingSupervisors, setEditSv1LoadingSupervisors] = useState(false);
    const [editSv1Saving, setEditSv1Saving] = useState(false);
    const [editSv1CalendarMonth, setEditSv1CalendarMonth] = useState(new Date());
    const [editSv1MonthAvailability, setEditSv1MonthAvailability] = useState({ availableDates: [], unavailableDates: [] });
    const [editSv1CalendarOpen, setEditSv1CalendarOpen] = useState(false);

    // Edit SV2 dialog state
    const [editSv2Open, setEditSv2Open] = useState(false);
    const [editSv2Date, setEditSv2Date] = useState(null);
    const [editSv2SlotId, setEditSv2SlotId] = useState('');
    const [editSv2SupervisorId, setEditSv2SupervisorId] = useState('');
    const [editSv2Slots, setEditSv2Slots] = useState([]);
    const [editSv2Supervisors, setEditSv2Supervisors] = useState([]);
    const [editSv2LoadingSlots, setEditSv2LoadingSlots] = useState(false);
    const [editSv2LoadingSupervisors, setEditSv2LoadingSupervisors] = useState(false);
    const [editSv2Saving, setEditSv2Saving] = useState(false);
    const [editSv2CalendarMonth, setEditSv2CalendarMonth] = useState(new Date());
    const [editSv2MonthAvailability, setEditSv2MonthAvailability] = useState({ availableDates: [], unavailableDates: [] });
    const [editSv2CalendarOpen, setEditSv2CalendarOpen] = useState(false);

    // Edit project (Overview) date picker state
    const [editStartDateOpen, setEditStartDateOpen] = useState(false);
    const [editCompletionDateOpen, setEditCompletionDateOpen] = useState(false);
    const [editStartCalendarMonth, setEditStartCalendarMonth] = useState(new Date());
    const [editCompletionCalendarMonth, setEditCompletionCalendarMonth] = useState(new Date());

    // Propose SV2 dialog state
    const [proposeSv2Open, setProposeSv2Open] = useState(false);
    const [proposeSv2Date, setProposeSv2Date] = useState(null);
    const [proposeSv2SlotId, setProposeSv2SlotId] = useState('');
    const [proposeSv2SupervisorId, setProposeSv2SupervisorId] = useState('');
    const [proposeSv2Slots, setProposeSv2Slots] = useState([]);
    const [proposeSv2Supervisors, setProposeSv2Supervisors] = useState([]);
    const [proposeSv2LoadingSlots, setProposeSv2LoadingSlots] = useState(false);
    const [proposeSv2LoadingSupervisors, setProposeSv2LoadingSupervisors] = useState(false);
    const [proposeSv2Submitting, setProposeSv2Submitting] = useState(false);
    const [proposeSv2CalendarMonth, setProposeSv2CalendarMonth] = useState(new Date());
    const [proposeSv2MonthAvailability, setProposeSv2MonthAvailability] = useState({ availableDates: [], unavailableDates: [] });

    // Map state
    const [map, setMap] = useState(null);
    const [searchBox, setSearchBox] = useState(null);
    const [mapType, setMapType] = useState('roadmap');
    const markerRef = useRef(null);
    const isSelectingPlaceRef = useRef(false);
    const mapLoaded = useGoogleMaps();

    // Handle tab query param from URL (e.g., ?tab=payments)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [location.search]);

    // Project data from API
    const [projectData, setProjectData] = useState({
        id: id,
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        location: '',
        sqft: 0,
        city: '',
        pincode: '',
        oemName: '',
        currentStage: '',
        preferredStartDate: '',
        preferredCompletionDate: '',
        estimatedDays: 0,
        latitude: '',
        longitude: '',
        hasExtraWork: false,
        extraWorkNotes: '',
        entities: []
    });

    const isAdmin = user?.role === 'admin';
    const isOEM = user?.role === 'oem';
    const isSupervisor = user?.role === 'supervisor';
    const isAligner = user?.role === 'aligner';
    const isReadOnlyViewer = isSupervisor || isAligner;

    // OEM can edit only at New Project stage
    const canOEMEdit = isOEM && projectData.currentStage === StageName.NewProject;
    // Admin can edit only before SV2 Completed
    const ADMIN_EDITABLE_STAGES = [
        StageName.NewProject,
        StageName.QuotationRejected,
        StageName.QuotationApprovalPending,
        StageName.QuotationApproved,
        StageName.Sv1Scheduled,
        StageName.Sv1Completed,
        StageName.Sv2Proposed,
        StageName.Sv2Scheduled,
    ];
    const canAdminEdit = isAdmin && ADMIN_EDITABLE_STAGES.includes(projectData.currentStage);
    const canEdit = canAdminEdit || canOEMEdit;
    // Admin can manage additional-work quotations at any stage (they are created after SV2)
    const canManageAdditionalWork = isAdmin;

    // OEM cannot delete documents or create quotations
    const canDeleteDocuments = isAdmin;
    const canCreateQuotation = isAdmin;

    const fetchRealProjectData = async () => {
        try {
            setLoading(true);
            // Fetch real project data from API
            const data = await api.get(`/api/projects/${id}`);

            console.log('Fetched project data:', data);

            // Log coordinates specifically
            console.log('Project coordinates from API:', {
                latitude: data.latitude,
                longitude: data.longitude,
                latitudeType: typeof data.latitude,
                longitudeType: typeof data.longitude,
                siteAddress: data.siteAddress
            });

            // Convert coordinates properly
            const latValue = data.latitude !== null && data.latitude !== undefined ? String(data.latitude) : '';
            const lngValue = data.longitude !== null && data.longitude !== undefined ? String(data.longitude) : '';

            console.log('Converted coordinates:', { latValue, lngValue });

            // Transform API data to match projectData structure
            setProjectData({
                id: data.id,
                clientName: data.clientName,
                clientPhone: data.clientPhone || '',
                clientEmail: data.clientEmail || '',
                location: data.siteAddress || data.cityName || '',
                sqft: data.sqft || 0,
                city: data.cityName || '',
                pincode: data.pincode || '',
                oemName: data.oemName || '',
                currentStage: data.stageName || 'New Project',
                preferredStartDate: data.preferredStartDate || null,
                preferredCompletionDate: data.preferredCompletionDate || null,
                estimatedDays: data.estimatedDays || 0,
                latitude: latValue,
                longitude: lngValue,
                hasExtraWork: data.hasExtraWork || false,
                extraWorkNotes: data.extraWorkNotes || '',
                entities: data.entities || [],
                quotations: [],
                quotation: {
                    totalAmount: 0,
                    baseWork: 0,
                    extraWork: 0,
                    gstPercentage: 18,
                    gstAmount: 0,
                    status: 'Pending',
                    approvedDate: null,
                    approvedBy: null
                },
                documents: [],
                sv1: {
                    status: 'Pending',
                    scheduledDate: null,
                    completedDate: null,
                    supervisor: null,
                    supervisorPhone: null,
                    duration: null,
                    photos: 0,
                    remarks: null
                },
                sv2: {
                    status: 'Pending',
                    scheduledDate: null,
                    completedDate: null,
                    supervisor: null,
                    supervisorPhone: null,
                    duration: null,
                    photos: 0,
                    remarks: null
                },
                installation: {
                    status: 'Pending',
                    progress: 0,
                    team: null,
                    aligner: null,
                    alignerPhone: null,
                    startDate: null,
                    estimatedCompletionDate: null,
                    daysElapsed: 0,
                    totalDays: 0,
                    checklistCompleted: 0,
                    checklistTotal: 0,
                    attendanceDays: 0,
                    photosUploaded: 0
                },
                hardwareListUploaded: data.hardwareListUploaded || false,
                isOnHold: data.isOnHold || false,
                onHoldReason: data.onHoldReason || '',
                payments: [],
                timeline: [{
                    date: data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : 'N/A',
                    event: 'Project Created',
                    user: 'System'
                }]
            });

            setLoading(false);
        } catch (error) {
            console.error('Error fetching project data:', error);
            toast.error('Failed to load project data');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRealProjectData();
    }, [id]);

    // Fetch all available entities from master
    useEffect(() => {
        const fetchEntities = async () => {
            try {
                const response = await api.getEntities();
                const entitiesData = Array.isArray(response?.items) ? response.items :
                    Array.isArray(response?.Items) ? response.Items : [];
                setAllEntities(entitiesData);
            } catch (error) {
                console.error('Error fetching entities:', error);
                setAllEntities([]);
            }
        };

        fetchEntities();
    }, []);

    // Fetch documents when Documents tab is active
    useEffect(() => {
        const fetchDocuments = async () => {
            if (activeTab !== 'documents' || !id) return;

            setLoadingDocuments(true);
            try {
                const response = await api.get(`/api/projects/${id}/documents?includeDocumentTypeCodes=hardware_list,project_document`);
                console.log('Fetched documents:', response);
                const docs = response.documents || [];
                setDocuments(docs);

                // Preload SAS URLs for all images to use as thumbnails
                const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
                const images = docs.filter(doc => imageTypes.includes(doc.mimeType?.toLowerCase()));

                // Fetch SAS URLs for thumbnails in parallel
                const urlPromises = images.map(async (img) => {
                    try {
                        const url = await getDocumentSasUrl(img.id);
                        return { id: img.id, url };
                    } catch (error) {
                        console.error(`Failed to get URL for ${img.id}:`, error);
                        return { id: img.id, url: null };
                    }
                });

                const urlResults = await Promise.all(urlPromises);
                const urlMap = {};
                urlResults.forEach(result => {
                    if (result.url) {
                        urlMap[result.id] = result.url;
                    }
                });
                setImageUrls(urlMap);
            } catch (error) {
                console.error('Error fetching documents:', error);
                toast.error('Failed to load documents');
                setDocuments([]);
            } finally {
                setLoadingDocuments(false);
            }
        };

        fetchDocuments();
    }, [id, activeTab]);

    // Fetch quotations when quotation tab is active
    useEffect(() => {
        const fetchQuotations = async () => {
            if (activeTab !== 'quotation' || !id) return;

            setLoadingQuotations(true);
            try {
                const response = await api.get(`/api/projects/${id}/quotations`);
                console.log('Fetched quotations:', response);
                setQuotations(response.quotations || []);
            } catch (error) {
                console.error('Error fetching quotations:', error);
                toast.error('Failed to load quotations');
            } finally {
                setLoadingQuotations(false);
            }
        };

        fetchQuotations();
    }, [id, activeTab]);

    // Fetch payment summary when payments tab is active
    useEffect(() => {
        if (activeTab === 'payments' && id && (isAdmin || isOEM)) {
            fetchPaymentSummary();
        }
    }, [id, activeTab]);

    const fetchPaymentSummary = async () => {
        setLoadingPayments(true);
        try {
            const response = await api.get(`/api/projects/${id}/payments`);
            setPaymentSummary(response);
        } catch (error) {
            console.error('Error fetching payment summary:', error);
            toast.error('Failed to load payment data');
        } finally {
            setLoadingPayments(false);
        }
    };

    const handleDeleteQuotation = async () => {
        if (!quotationToDelete) return;
        setDeletingQuotation(true);
        try {
            await api.delete(`/api/projects/${id}/quotations/${quotationToDelete.id}`);
            toast.success('Quotation deleted successfully');
            const response = await api.get(`/api/projects/${id}/quotations`);
            setQuotations(response.quotations || []);
        } catch (error) {
            console.error('Error deleting quotation:', error);
            toast.error(error.message || 'Failed to delete quotation');
        } finally {
            setDeletingQuotation(false);
            setQuotationDeleteConfirmOpen(false);
            setQuotationToDelete(null);
        }
    };

    const handleGeneratePaymentLink = async () => {
        if (!linkForm.amount || parseFloat(linkForm.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        setGeneratingLink(true);
        try {
            const response = await api.post(`/api/projects/${id}/payments/generate-link`, {
                projectId: id,
                amount: parseFloat(linkForm.amount),
                description: linkForm.description || undefined,
                expiryHours: linkForm.expiryHours || 72
            });
            toast.success('Payment link generated successfully!');
            setGenerateLinkOpen(false);
            setLinkForm({ amount: '', description: '', expiryHours: 72 });
            fetchPaymentSummary();
        } catch (error) {
            console.error('Error generating payment link:', error);
            toast.error(error.message || 'Failed to generate payment link');
        } finally {
            setGeneratingLink(false);
        }
    };

    const handleCancelPaymentLink = async (transactionId) => {
        if (!window.confirm('Are you sure you want to cancel this payment link? This cannot be undone.')) return;
        try {
            await api.post(`/api/projects/${id}/payments/${transactionId}/cancel`);
            toast.success('Payment link cancelled');
            fetchPaymentSummary();
        } catch (error) {
            toast.error(error.message || 'Failed to cancel payment link');
        }
    };

    const handleMarkPaymentLinkPaid = async (transactionId) => {
        if (!window.confirm('Are you sure you want to mark this payment link as paid? This will record a payment entry.')) return;
        try {
            await api.post(`/api/projects/${id}/payments/${transactionId}/mark-paid`);
            toast.success('Payment link marked as paid');
            fetchPaymentSummary();
        } catch (error) {
            toast.error(error.message || 'Failed to mark payment as paid');
        }
    };

    const handleRecordPayment = async () => {
        if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        if (!paymentForm.paymentType) {
            toast.error('Please select a payment type');
            return;
        }
        if (!paymentForm.paymentDate) {
            toast.error('Please enter a payment date');
            return;
        }
        setRecordingPayment(true);
        try {
            await api.post(`/api/projects/${id}/payments/record`, {
                projectId: id,
                amount: parseFloat(paymentForm.amount),
                paymentType: paymentForm.paymentType,
                paymentDate: paymentForm.paymentDate,
                transactionReference: paymentForm.transactionReference || undefined,
                chequeNumber: paymentForm.chequeNumber || undefined,
                chequeDate: paymentForm.chequeDate || undefined,
                chequeBankName: paymentForm.chequeBankName || undefined,
                upiId: paymentForm.upiId || undefined,
                notes: paymentForm.notes || undefined
            });
            toast.success('Payment recorded successfully!');
            setRecordPaymentOpen(false);
            setPaymentForm({
                amount: '', paymentType: 'neft_online', paymentDate: new Date().toISOString().split('T')[0],
                transactionReference: '', chequeNumber: '', chequeDate: '', chequeBankName: '', upiId: '', notes: ''
            });
            fetchPaymentSummary();
        } catch (error) {
            console.error('Error recording payment:', error);
            toast.error(error.message || 'Failed to record payment');
        } finally {
            setRecordingPayment(false);
        }
    };

    // Stages before SV1 scheduling (Site Visits tab not relevant)
    const showSiteVisitsTab = !PRE_SV_STAGES.includes(projectData.currentStage);

    // Show installation tab when project is at installation stage or beyond (including On Hold)
    const INSTALLATION_STAGES = [
        StageName.InstallationScheduled,
        StageName.InstallationInProgress,
        StageName.InstallationCompleted,
        StageName.ClientReview,
        StageName.PendingOemSignoff,
        StageName.PendingAdminClosure,
        StageName.ProjectCompleted,
        StageName.Delivered,
        StageName.OnHold  // Show installation tab even when on hold
    ];
    const showInstallationTab = INSTALLATION_STAGES.includes(projectData.currentStage);

    // Stages where OEM payment tab should show (from QuotationApproved onwards)
    const PRE_PAYMENT_STAGES = [StageName.NewProject, StageName.QuotationPending, StageName.QuotationApprovalPending, StageName.QuotationRejected];
    const showPaymentsTab = !PRE_PAYMENT_STAGES.includes(projectData.currentStage);

    // Show sign-off tab for OEM (at pending_oem_signoff and beyond) and admin (at pending_admin_closure and beyond)
    const SIGNOFF_STAGES = [
        StageName.PendingOemSignoff,
        StageName.PendingAdminClosure,
        StageName.ProjectCompleted,
    ];
    const showSignoffTab = SIGNOFF_STAGES.includes(projectData.currentStage) && (isOEM || isAdmin);

    // Fetch installation details when tab is active
    useEffect(() => {
        if (activeTab === 'installation' && id && showInstallationTab && !installationData && !loadingInstallation) {
            const fetchInstallation = async () => {
                setLoadingInstallation(true);
                try {
                    const data = await api.request(`/api/projects/${id}/complete`);
                    const details = data.installationDetails || data.InstallationDetails;
                    const teamInfo = data.teamInfo || data.TeamInfo;
                    if (details) {
                        // Flatten all items from entities for counts
                        const allItems = (details.entities || []).flatMap(e => e.units || []).flatMap(u => (u.groups || []).flatMap(g => g.items || []));
                        setInstallationData({
                            status: details.status || 'Scheduled',
                            progress: details.checklistProgressPercentage || 0,
                            team: details.teamName || teamInfo?.teamName || projectData.installation?.team || 'N/A',
                            scheduledDate: details.scheduledDate || data.scheduledDate,
                            startDate: details.startedAt,
                            completedDate: details.completedAt,
                            checklistCompleted: allItems.filter(c => c.isApproved === true).length,
                            checklistTotal: allItems.length,
                            entities: details.entities || [],
                            photos: details.photos || [],
                            teamInfo: teamInfo,
                            supervisorId: details.supervisorId,
                            supervisorName: details.supervisorName,
                            allowProgressReportAnytime: details.allowProgressReportAnytime || false,
                            locationValidationRadius: details.locationValidationRadius ?? null
                        });
                        setRadiusInput(details.locationValidationRadius != null ? String(details.locationValidationRadius) : '');
                    }
                } catch (error) {
                    console.error('Error fetching installation details:', error);
                } finally {
                    setLoadingInstallation(false);
                }
            };
            fetchInstallation();
        }
    }, [activeTab, id, showInstallationTab]);

    // Fetch site visits when tab is active
    useEffect(() => {
        if (activeTab === 'sitevisits' && id && (isAdmin || isOEM || (isSupervisor && showSiteVisitsTab))) {
            fetchSiteVisits();
        }
    }, [id, activeTab]);

    // Fetch bills when tab is active
    useEffect(() => {
        if (activeTab === 'bills' && id && (isAdmin || isOEM)) {
            fetchBills();
        }
    }, [id, activeTab]);

    // Fetch history/timeline when tab is active
    useEffect(() => {
        if (activeTab === 'history' && id && timelineEvents.length === 0 && !loadingTimeline) {
            const fetchHistory = async () => {
                setLoadingTimeline(true);
                try {
                    const response = await api.get(`/api/projects/${id}/history`);
                    setTimelineEvents(response.timeline || []);
                } catch (error) {
                    console.error('Error fetching project history:', error);
                }
                setLoadingTimeline(false);
            };
            fetchHistory();
        }
    }, [id, activeTab]);

    // Fetch sign-off details when tab is active
    useEffect(() => {
        if (activeTab === 'signoff' && id && showSignoffTab && !signoffDetails && !signoffLoading) {
            const fetchSignoff = async () => {
                setSignoffLoading(true);
                try {
                    let details;
                    if (isAdmin) {
                        details = await api.getAdminSignoffDetails(id);
                    } else {
                        details = await api.getOemSignoffDetails(id);
                    }
                    setSignoffDetails(details);
                } catch (error) {
                    console.error('Error loading signoff details:', error);
                    toast.error('Failed to load sign-off details');
                } finally {
                    setSignoffLoading(false);
                }
            };
            fetchSignoff();
        }
    }, [activeTab, id, showSignoffTab]);

    // Sign-off photo loading
    const loadSignoffItemPhotos = async (responseId) => {
        if (signoffItemPhotos[responseId]) {
            setSignoffPhotosExpanded(prev => ({ ...prev, [responseId]: !prev[responseId] }));
            return;
        }
        try {
            const token = localStorage.getItem('alignworks_token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const docsRes = await fetch(`${api.baseUrl}/api/projects/${id}/documents?referenceId=${responseId}`, { headers });
            if (!docsRes.ok) throw new Error('Failed to fetch documents');
            const data = await docsRes.json();
            const docsList = data.documents || data || [];
            const photos = [];
            for (const doc of docsList) {
                const streamRes = await fetch(`${api.baseUrl}/api/projects/${id}/documents/${doc.id}/stream`, { headers });
                if (streamRes.ok) {
                    const blob = await streamRes.blob();
                    photos.push({ id: doc.id, url: URL.createObjectURL(blob), fileName: doc.fileName });
                }
            }
            setSignoffItemPhotos(prev => ({ ...prev, [responseId]: photos }));
            setSignoffPhotosExpanded(prev => ({ ...prev, [responseId]: true }));
        } catch (error) {
            console.error('Error loading signoff photos:', error);
            toast.error('Failed to load photos');
        }
    };

    // OEM approve sign-off
    const handleApproveSignoff = async () => {
        if (signoffRating < 1) {
            toast.error('Please select a rating (1-5)');
            return;
        }
        setSignoffSubmitting(true);
        try {
            await api.oemApproveSignoff(id, signoffRating, signoffNotes);
            toast.success(`Project rated ${signoffRating}/5 and moved to admin closure`);
            setSignoffDetails(null); // Reset so it reloads on the new stage
            await fetchRealProjectData(); // Properly refresh project data
        } catch (error) {
            console.error('Error approving signoff:', error);
            toast.error(error.message || 'Failed to approve sign-off');
        } finally {
            setSignoffSubmitting(false);
        }
    };

    // Admin close project
    const handleAdminCloseProject = async () => {
        setSignoffSubmitting(true);
        try {
            await api.adminCloseProject(id, signoffNotes);
            toast.success('Project closed successfully');
            setSignoffDetails(null); // Reset so it reloads on the new stage
            await fetchRealProjectData(); // Properly refresh project data
        } catch (error) {
            console.error('Error closing project:', error);
            toast.error(error.message || 'Failed to close project');
        } finally {
            setSignoffSubmitting(false);
        }
    };

    const fetchBills = async () => {
        setLoadingBills(true);
        try {
            const response = await api.getProjectBills(id);
            setBills(response.items || []);
        } catch (error) {
            console.error('Error fetching bills:', error);
            toast.error('Failed to load bills');
        } finally {
            setLoadingBills(false);
        }
    };

    const handleViewBill = async (bill) => {
        try {
            const response = await api.getBillDownloadUrl(id, bill.id);
            window.open(response.downloadUrl, '_blank');
        } catch (error) {
            console.error('Error viewing bill:', error);
            toast.error('Failed to open bill');
        }
    };

    const handleDownloadBill = async (bill) => {
        try {
            toast.loading('Downloading ' + bill.fileName + '...');
            const token = localStorage.getItem('alignworks_token');
            const response = await fetch(`http://localhost:5133/api/projects/${id}/bills/${bill.id}/stream`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Download failed');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = bill.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            toast.dismiss();
            toast.success('Downloaded ' + bill.fileName);
        } catch (error) {
            toast.dismiss();
            console.error('Error downloading bill:', error);
            toast.error('Failed to download bill');
        }
    };

    const handleUploadBill = async () => {
        if (!billForm.name.trim()) {
            toast.error('Please enter a bill name');
            return;
        }
        if (!billForm.file) {
            toast.error('Please select a file to upload');
            return;
        }
        const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
        if (!allowed.includes(billForm.file.type)) {
            toast.error('Only PDF and image files are allowed');
            return;
        }
        setUploadingBill(true);
        try {
            const formData = new FormData();
            formData.append('file', billForm.file);
            formData.append('notes', billForm.notes.trim());
            formData.append('billName', billForm.name.trim());
            await api.uploadProjectBill(id, formData);
            toast.success('Bill uploaded successfully');
            setBillForm({ name: '', notes: '', file: null });
            setBillDialogOpen(false);
            if (billFileRef.current) billFileRef.current.value = '';
            fetchBills();
        } catch (error) {
            toast.error(error.message || 'Failed to upload bill');
        } finally {
            setUploadingBill(false);
        }
    };

    const handleUpdateBillNotes = async (billId) => {
        try {
            await api.updateProjectBill(id, billId, { notes: editBillNotes });
            toast.success('Bill notes updated');
            setEditingBillId(null);
            fetchBills();
        } catch (error) {
            toast.error(error.message || 'Failed to update bill');
        }
    };

    const handleDeleteBill = async (billId) => {
        if (!window.confirm('Are you sure you want to delete this bill?')) return;
        try {
            await api.deleteProjectBill(id, billId);
            toast.success('Bill deleted');
            fetchBills();
        } catch (error) {
            toast.error(error.message || 'Failed to delete bill');
        }
    };

    const fetchSiteVisits = async () => {
        setLoadingSiteVisits(true);
        try {
            const response = await api.get(`/api/projects/${id}/site-visits`);
            setSiteVisitsData(response);
        } catch (error) {
            console.error('Error fetching site visits:', error);
            toast.error('Failed to load site visit data');
        } finally {
            setLoadingSiteVisits(false);
        }
    };

    // Toggle photos for a checklist item
    const toggleSvItemPhotos = async (responseId) => {
        if (svPhotosExpanded[responseId]) {
            setSvPhotosExpanded(prev => ({ ...prev, [responseId]: false }));
            return;
        }
        // If already loaded, just expand
        if (svItemPhotos[responseId]) {
            setSvPhotosExpanded(prev => ({ ...prev, [responseId]: true }));
            return;
        }
        // Fetch photos for this response
        setSvLoadingPhotos(responseId);
        try {
            const docs = await api.request(`/api/projects/${id}/documents?referenceId=${responseId}`);
            const docsList = docs.documents || docs.Documents || docs || [];
            const token = localStorage.getItem('alignworks_token');
            const photos = [];
            for (const doc of docsList) {
                const docId = doc.id || doc.Id;
                try {
                    const res = await fetch(`${api.baseUrl}/api/projects/${id}/documents/${docId}/stream`, {
                        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                    });
                    if (res.ok) {
                        const blob = await res.blob();
                        photos.push({ id: docId, url: URL.createObjectURL(blob), fileName: doc.fileName || doc.FileName });
                    }
                } catch (e) { /* skip failed */ }
            }
            setSvItemPhotos(prev => ({ ...prev, [responseId]: photos }));
            setSvPhotosExpanded(prev => ({ ...prev, [responseId]: true }));
        } catch (error) {
            console.error('Error loading checklist photos:', error);
        } finally {
            setSvLoadingPhotos(null);
        }
    };

    // Toggle progress report override
    const handleToggleOverride = async () => {
        setTogglingOverride(true);
        try {
            const result = await api.toggleProgressOverride(id);
            setInstallationData(prev => ({
                ...prev,
                allowProgressReportAnytime: result.allowProgressReportAnytime
            }));
            toast.success(result.message);
        } catch (error) {
            toast.error('Failed to toggle override');
        } finally {
            setTogglingOverride(false);
        }
    };

    // Update location validation radius
    const handleUpdateRadius = async () => {
        setSavingRadius(true);
        try {
            const radius = radiusInput.trim() === '' ? null : parseInt(radiusInput, 10);
            if (radius !== null && (isNaN(radius) || radius < 10 || radius > 5000)) {
                toast.error('Radius must be between 10 and 5000 meters, or empty for default (50m)');
                setSavingRadius(false);
                return;
            }
            const result = await api.updateLocationRadius(id, radius);
            setInstallationData(prev => ({
                ...prev,
                locationValidationRadius: result.locationValidationRadius
            }));
            setRadiusInput(result.locationValidationRadius != null ? String(result.locationValidationRadius) : '');
            toast.success(result.message);
        } catch (error) {
            toast.error('Failed to update location radius');
        } finally {
            setSavingRadius(false);
        }
    };

    // Fetch progress reports for admin view
    const fetchProgressReports = async () => {
        setLoadingProgressReports(true);
        try {
            const reports = await api.getProjectProgressReports(id);
            // Resolve photo streams into object URLs
            const token = localStorage.getItem('alignworks_token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const resolved = await Promise.all((reports || []).map(async (report) => {
                const photoUrls = [];
                for (const doc of (report.photoDocuments || [])) {
                    try {
                        const res = await fetch(`${api.baseUrl}/api/projects/${id}/documents/${doc.id}/stream`, { headers });
                        if (res.ok) {
                            const blob = await res.blob();
                            photoUrls.push(URL.createObjectURL(blob));
                        }
                    } catch (e) { /* skip failed photo */ }
                }
                return { ...report, resolvedPhotos: photoUrls };
            }));
            setProgressReports(resolved);
        } catch (error) {
            console.error('Error fetching progress reports:', error);
        } finally {
            setLoadingProgressReports(false);
        }
    };

    // Fetch attendance records for admin view
    const fetchAttendance = async () => {
        setLoadingAttendance(true);
        try {
            const records = await api.getProjectAttendance(id);
            // Resolve selfie streams (check-in and check-out)
            const token = localStorage.getItem('alignworks_token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const resolved = await Promise.all((records || []).map(async (record) => {
                let selfieObjectUrl = null;
                let checkOutSelfieObjectUrl = null;
                try {
                    const res = await fetch(`${api.baseUrl}/api/projects/${id}/attendance/${record.id}/selfie`, { headers });
                    if (res.ok) {
                        const blob = await res.blob();
                        selfieObjectUrl = URL.createObjectURL(blob);
                    }
                } catch (e) { /* skip */ }
                if (record.checkOutSelfieUrl) {
                    try {
                        const res = await fetch(`${api.baseUrl}/api/projects/${id}/attendance/${record.id}/selfie?type=checkout`, { headers });
                        if (res.ok) {
                            const blob = await res.blob();
                            checkOutSelfieObjectUrl = URL.createObjectURL(blob);
                        }
                    } catch (e) { /* skip */ }
                }
                return { ...record, selfieObjectUrl, checkOutSelfieObjectUrl };
            }));
            setAttendanceRecords(resolved);
        } catch (error) {
            console.error('Error fetching attendance:', error);
        } finally {
            setLoadingAttendance(false);
        }
    };

    // Open change supervisor dialog
    const handleOpenChangeSupervisor = async () => {
        setChangeSupervisorOpen(true);
        setSelectedNewSupervisorId(installationData?.supervisorId || '');
        try {
            const result = await api.getSupervisors();
            setAllSupervisors(result?.users || result || []);
        } catch (error) {
            console.error('Error fetching supervisors:', error);
            toast.error('Failed to load supervisors');
        }
    };

    // Save supervisor change
    const handleSaveSupervisorChange = async () => {
        if (!selectedNewSupervisorId) return;
        setSavingSupervisor(true);
        try {
            await api.updateProject(id, { supervisorId: selectedNewSupervisorId });
            setInstallationData(prev => ({
                ...prev,
                supervisorId: selectedNewSupervisorId,
                supervisorName: allSupervisors.find(s => (s.userId || s.id) === selectedNewSupervisorId)?.name || 'Updated'
            }));
            toast.success('Supervisor updated successfully');
            setChangeSupervisorOpen(false);
        } catch (error) {
            console.error('Error updating supervisor:', error);
            toast.error('Failed to update supervisor');
        } finally {
            setSavingSupervisor(false);
        }
    };

    // Fetch teams assigned to this project
    const fetchProjectTeams = async () => {
        setLoadingTeams(true);
        try {
            const teams = await api.getProjectTeams(id);
            setProjectTeams(Array.isArray(teams) ? teams : []);
        } catch (error) {
            console.error('Error fetching project teams:', error);
            setProjectTeams([]);
        } finally {
            setLoadingTeams(false);
        }
    };

    // Fetch project teams when installation tab is active
    useEffect(() => {
        if (activeTab === 'installation' && id && showInstallationTab && isAdmin) {
            fetchProjectTeams();
        }
    }, [activeTab, id, showInstallationTab]);

    // Open Add Team dialog
    const handleOpenAddTeam = async () => {
        setAddTeamOpen(true);
        setSelectedTeamId('');
        try {
            const res = await api.request('/api/teams?page=1&pageSize=100');
            const teamList = res?.teams || res?.data || (Array.isArray(res) ? res : []);
            // Filter out teams already assigned
            const assignedIds = new Set(projectTeams.map(t => t.teamId));
            setAllTeams(teamList.filter(t => !assignedIds.has(t.id)));
        } catch (error) {
            console.error('Error fetching teams:', error);
            toast.error('Failed to load teams');
        }
    };

    // Add team to project
    const handleAddTeam = async () => {
        if (!selectedTeamId) return;
        setAddingTeam(true);
        try {
            await api.addTeamToProject(id, selectedTeamId);
            toast.success('Team added to project');
            setAddTeamOpen(false);
            fetchProjectTeams();
        } catch (error) {
            console.error('Error adding team:', error);
            toast.error(error?.message || 'Failed to add team');
        } finally {
            setAddingTeam(false);
        }
    };

    // Remove team from project
    const handleRemoveTeam = async (teamId, teamName) => {
        setRemovingTeamId(teamId);
        try {
            await api.removeTeamFromProject(id, teamId);
            toast.success(`Team '${teamName}' removed`);
            fetchProjectTeams();
        } catch (error) {
            console.error('Error removing team:', error);
            toast.error(error?.message || 'Failed to remove team');
        } finally {
            setRemovingTeamId(null);
        }
    };

    // Toggle expand team to show members
    const handleToggleTeamMembers = async (teamId) => {
        if (expandedTeamId === teamId) {
            setExpandedTeamId(null);
            return;
        }
        setExpandedTeamId(teamId);
        if (!teamMembersMap[teamId]) {
            setLoadingMembersTeamId(teamId);
            try {
                const members = await api.getTeamMembersForTeam(teamId);
                setTeamMembersMap(prev => ({ ...prev, [teamId]: Array.isArray(members) ? members : [] }));
            } catch (error) {
                console.error('Error fetching team members:', error);
                toast.error('Failed to load team members');
                setTeamMembersMap(prev => ({ ...prev, [teamId]: [] }));
            } finally {
                setLoadingMembersTeamId(null);
            }
        }
    };

    // Edit SV1 helpers
    const fetchEditSv1MonthAvailability = async (month) => {
        try {
            const year = month.getFullYear();
            const monthNum = month.getMonth() + 1;
            const data = await api.getMonthAvailability(year, monthNum);
            const available = [];
            const unavailable = [];
            data.dates?.forEach(dateInfo => {
                const d = new Date(dateInfo.date);
                if (dateInfo.hasAvailability) available.push(d);
                else unavailable.push(d);
            });
            setEditSv1MonthAvailability({ availableDates: available, unavailableDates: unavailable });
        } catch (error) {
            console.error('Error fetching month availability:', error);
        }
    };

    const openEditSv1 = () => {
        if (!siteVisitsData?.sv1) return;
        const existingDate = new Date(siteVisitsData.sv1.scheduledDate + 'T00:00:00');
        setEditSv1Date(existingDate);
        setEditSv1SlotId(String(siteVisitsData.sv1.slotId || ''));
        setEditSv1SupervisorId(siteVisitsData.sv1.supervisorId || '');
        setEditSv1Slots([]);
        setEditSv1Supervisors([]);
        setEditSv1CalendarMonth(existingDate);
        setEditSv1Open(true);
        fetchEditSv1MonthAvailability(existingDate);
        fetchEditSv1Slots(existingDate);
        if (siteVisitsData.sv1.slotId) {
            fetchEditSv1Supervisors(existingDate, siteVisitsData.sv1.slotId);
        }
    };

    const fetchEditSv1Slots = async (date) => {
        if (!date) return;
        setEditSv1LoadingSlots(true);
        setEditSv1Slots([]);
        try {
            const data = await api.getSlotAvailability(date);
            setEditSv1Slots(data.slots || []);
        } catch (error) {
            console.error('Error fetching slots:', error);
        } finally {
            setEditSv1LoadingSlots(false);
        }
    };

    const fetchEditSv1Supervisors = async (date, slotId) => {
        if (!date || !slotId) return;
        setEditSv1LoadingSupervisors(true);
        setEditSv1Supervisors([]);
        try {
            const data = await api.getAvailableSupervisors(date, slotId);
            setEditSv1Supervisors(data.availableSupervisors || []);
        } catch (error) {
            console.error('Error fetching supervisors:', error);
        } finally {
            setEditSv1LoadingSupervisors(false);
        }
    };

    const handleEditSv1SlotSelect = (slot) => {
        if (!slot.isAvailable || !editSv1Date) return;
        setEditSv1SlotId(slot.slotId.toString());
        setEditSv1SupervisorId('');
        fetchEditSv1Supervisors(editSv1Date, slot.slotId);
    };

    const getEditSv1SlotBadge = (slot) => {
        if (!slot.isAvailable || slot.availableSupervisors === 0) {
            return <Badge variant="destructive" className="ml-2">0 available</Badge>;
        } else if (slot.availableSupervisors === 1) {
            return <Badge className="ml-2 bg-yellow-500 text-white hover:bg-yellow-600">1 available</Badge>;
        } else {
            return <Badge className="ml-2 bg-green-500 text-white hover:bg-green-600">{slot.availableSupervisors} available</Badge>;
        }
    };

    const handleEditSv1Save = async () => {
        if (!editSv1Date || !editSv1SlotId || !editSv1SupervisorId) {
            toast.error('Please select date, slot and supervisor');
            return;
        }
        setEditSv1Saving(true);
        try {
            const dateStr = `${editSv1Date.getFullYear()}-${String(editSv1Date.getMonth() + 1).padStart(2, '0')}-${String(editSv1Date.getDate()).padStart(2, '0')}`;
            await api.updateSv1Booking(
                siteVisitsData.sv1.bookingId,
                dateStr,
                parseInt(editSv1SlotId),
                editSv1SupervisorId
            );
            toast.success('SV1 booking updated successfully');
            setEditSv1Open(false);
            fetchSiteVisits();
        } catch (error) {
            console.error('Error updating SV1:', error);
            toast.error('Failed to update SV1 booking');
        } finally {
            setEditSv1Saving(false);
        }
    };

    // Propose SV2 helpers
    const fetchProposeSv2MonthAvailability = async (month) => {
        try {
            const year = month.getFullYear();
            const monthNum = month.getMonth() + 1;
            const data = await api.getMonthAvailability(year, monthNum);
            const available = [];
            const unavailable = [];
            data.dates?.forEach(dateInfo => {
                const d = new Date(dateInfo.date);
                if (dateInfo.hasAvailability) available.push(d);
                else unavailable.push(d);
            });
            setProposeSv2MonthAvailability({ availableDates: available, unavailableDates: unavailable });
        } catch (error) {
            console.error('Error fetching month availability:', error);
        }
    };

    const openProposeSv2 = () => {
        setProposeSv2Date(null);
        setProposeSv2SlotId('');
        setProposeSv2SupervisorId('');
        setProposeSv2Slots([]);
        setProposeSv2Supervisors([]);
        setProposeSv2CalendarMonth(new Date());
        setProposeSv2Open(true);
        fetchProposeSv2MonthAvailability(new Date());
    };

    const fetchProposeSv2Slots = async (date) => {
        if (!date) return;
        setProposeSv2LoadingSlots(true);
        setProposeSv2Slots([]);
        setProposeSv2SlotId('');
        setProposeSv2SupervisorId('');
        setProposeSv2Supervisors([]);
        try {
            const data = await api.getSlotAvailability(date);
            setProposeSv2Slots(data.slots || []);
        } catch (error) {
            console.error('Error fetching slots:', error);
        } finally {
            setProposeSv2LoadingSlots(false);
        }
    };

    const fetchProposeSv2Supervisors = async (date, slotId) => {
        if (!date || !slotId) return;
        setProposeSv2LoadingSupervisors(true);
        setProposeSv2Supervisors([]);
        setProposeSv2SupervisorId('');
        try {
            const data = await api.getAvailableSupervisors(date, slotId);
            const sups = data.availableSupervisors || [];
            setProposeSv2Supervisors(sups);
            if (sups.length === 1) {
                setProposeSv2SupervisorId(String(sups[0].supervisorId));
            }
        } catch (error) {
            console.error('Error fetching supervisors:', error);
        } finally {
            setProposeSv2LoadingSupervisors(false);
        }
    };

    const handleProposeSv2SlotSelect = (slot) => {
        if (!slot.isAvailable || !proposeSv2Date) return;
        setProposeSv2SlotId(slot.slotId.toString());
        setProposeSv2SupervisorId('');
        fetchProposeSv2Supervisors(proposeSv2Date, slot.slotId);
    };

    const handleProposeSv2Submit = async () => {
        if (!proposeSv2Date || !proposeSv2SlotId || !proposeSv2SupervisorId) {
            toast.error('Please select date, slot and supervisor');
            return;
        }
        setProposeSv2Submitting(true);
        try {
            const dateStr = `${proposeSv2Date.getFullYear()}-${String(proposeSv2Date.getMonth() + 1).padStart(2, '0')}-${String(proposeSv2Date.getDate()).padStart(2, '0')}`;
            await api.proposeSiteVisit2(id, dateStr, parseInt(proposeSv2SlotId), proposeSv2SupervisorId);
            toast.success('SV2 proposed. Awaiting OEM approval.');
            setProposeSv2Open(false);
            fetchRealProjectData();
            fetchSiteVisits();
        } catch (error) {
            console.error('Error proposing SV2:', error);
            toast.error(error.message || 'Failed to propose Site Visit 2');
        } finally {
            setProposeSv2Submitting(false);
        }
    };

    // Edit SV2 helpers
    const fetchEditSv2MonthAvailability = async (month) => {
        try {
            const year = month.getFullYear();
            const monthNum = month.getMonth() + 1;
            const data = await api.getMonthAvailability(year, monthNum);
            const available = [];
            const unavailable = [];
            data.dates?.forEach(dateInfo => {
                const d = new Date(dateInfo.date);
                if (dateInfo.hasAvailability) available.push(d);
                else unavailable.push(d);
            });
            setEditSv2MonthAvailability({ availableDates: available, unavailableDates: unavailable });
        } catch (error) {
            console.error('Error fetching month availability:', error);
        }
    };

    const openEditSv2 = () => {
        if (!siteVisitsData?.sv2) return;
        const existingDate = new Date(siteVisitsData.sv2.scheduledDate + 'T00:00:00');
        setEditSv2Date(existingDate);
        setEditSv2SlotId(String(siteVisitsData.sv2.slotId || ''));
        setEditSv2SupervisorId(siteVisitsData.sv2.supervisorId || '');
        setEditSv2Slots([]);
        setEditSv2Supervisors([]);
        setEditSv2CalendarMonth(existingDate);
        setEditSv2Open(true);
        fetchEditSv2MonthAvailability(existingDate);
        fetchEditSv2Slots(existingDate);
        if (siteVisitsData.sv2.slotId) {
            fetchEditSv2Supervisors(existingDate, siteVisitsData.sv2.slotId);
        }
    };

    const fetchEditSv2Slots = async (date) => {
        if (!date) return;
        setEditSv2LoadingSlots(true);
        setEditSv2Slots([]);
        try {
            const data = await api.getSlotAvailability(date);
            setEditSv2Slots(data.slots || []);
        } catch (error) {
            console.error('Error fetching slots:', error);
        } finally {
            setEditSv2LoadingSlots(false);
        }
    };

    const fetchEditSv2Supervisors = async (date, slotId) => {
        if (!date || !slotId) return;
        setEditSv2LoadingSupervisors(true);
        setEditSv2Supervisors([]);
        try {
            const data = await api.getAvailableSupervisors(date, slotId);
            setEditSv2Supervisors(data.availableSupervisors || []);
        } catch (error) {
            console.error('Error fetching supervisors:', error);
        } finally {
            setEditSv2LoadingSupervisors(false);
        }
    };

    const handleEditSv2SlotSelect = (slot) => {
        if (!slot.isAvailable || !editSv2Date) return;
        setEditSv2SlotId(slot.slotId.toString());
        setEditSv2SupervisorId('');
        fetchEditSv2Supervisors(editSv2Date, slot.slotId);
    };

    const getEditSv2SlotBadge = (slot) => {
        if (!slot.isAvailable || slot.availableSupervisors === 0) {
            return <Badge variant="destructive" className="ml-2">0 available</Badge>;
        } else if (slot.availableSupervisors === 1) {
            return <Badge className="ml-2 bg-yellow-500 text-white hover:bg-yellow-600">1 available</Badge>;
        } else {
            return <Badge className="ml-2 bg-green-500 text-white hover:bg-green-600">{slot.availableSupervisors} available</Badge>;
        }
    };

    const handleEditSv2Save = async () => {
        if (!editSv2Date || !editSv2SlotId || !editSv2SupervisorId) {
            toast.error('Please select date, slot and supervisor');
            return;
        }
        setEditSv2Saving(true);
        try {
            const dateStr = `${editSv2Date.getFullYear()}-${String(editSv2Date.getMonth() + 1).padStart(2, '0')}-${String(editSv2Date.getDate()).padStart(2, '0')}`;
            await api.updateSv2Booking(
                siteVisitsData.sv2.bookingId,
                dateStr,
                parseInt(editSv2SlotId),
                editSv2SupervisorId
            );
            toast.success('SV2 booking updated successfully');
            setEditSv2Open(false);
            fetchSiteVisits();
        } catch (error) {
            console.error('Error updating SV2:', error);
            toast.error('Failed to update SV2 booking');
        } finally {
            setEditSv2Saving(false);
        }
    };

    // Fetch quotation documents when quotation details modal opens
    useEffect(() => {
        if (quotationDetailsOpen && selectedQuotation) {
            fetchQuotationDocuments(selectedQuotation.id);
        }
    }, [quotationDetailsOpen, selectedQuotation]);

    // Toggle entity selection (used in edit mode)
    const toggleEntity = (entityId) => {
        setProjectData(prev => {
            const existing = prev.entities.find(e => e.id === entityId);

            if (existing) {
                // Remove entity
                return {
                    ...prev,
                    entities: prev.entities.filter(e => e.id !== entityId)
                };
            } else {
                // Add entity with default quantity 1
                const entityDetails = allEntities.find(e => e.id === entityId);
                return {
                    ...prev,
                    entities: [...prev.entities, {
                        id: entityId,
                        name: entityDetails?.entityName || '',
                        quantity: 1
                    }]
                };
            }
        });
    };

    // Update entity quantity (used in edit mode)
    const updateEntityQuantity = (entityId, quantity) => {
        const parsedQuantity = parseInt(quantity);
        // Ensure quantity is at least 1
        const validQuantity = parsedQuantity && parsedQuantity > 0 ? parsedQuantity : 1;

        setProjectData(prev => ({
            ...prev,
            entities: prev.entities.map(e =>
                e.id === entityId ? { ...e, quantity: validQuantity } : e
            )
        }));
    };

    // Validate all required fields
    const validateFields = () => {
        if (!projectData.clientName?.trim()) {
            toast.error('Client name is required');
            return false;
        }

        // Validate phone number if provided
        if (projectData.clientPhone && projectData.clientPhone.trim() && projectData.clientPhone !== 'N/A') {
            const phoneValue = projectData.clientPhone.trim();
            // Extract only digits from phone number
            const digitsOnly = phoneValue.replace(/\D/g, '');

            // Check if it has exactly 10 digits
            if (digitsOnly.length !== 10) {
                toast.error('Phone number must contain exactly 10 digits');
                return false;
            }

            // Check if it contains only valid characters
            const phoneRegex = /^[\d\s\+\-\(\)]+$/;
            if (!phoneRegex.test(phoneValue)) {
                toast.error('Phone number can only contain digits, +, -, spaces, and parentheses');
                return false;
            }
        }

        // Validate email if provided
        if (projectData.clientEmail && projectData.clientEmail.trim() && projectData.clientEmail !== 'N/A') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(projectData.clientEmail.trim())) {
                toast.error('Please enter a valid email address');
                return false;
            }
        }

        if (!projectData.sqft || projectData.sqft <= 0) {
            toast.error('Valid square footage is required');
            return false;
        }

        // Validate entities - must have at least one with quantity > 0
        if (!projectData.entities || projectData.entities.length === 0) {
            toast.error('Please select at least one entity');
            return false;
        }

        const hasValidEntity = projectData.entities.some(e => e.quantity && e.quantity > 0);
        if (!hasValidEntity) {
            toast.error('At least one entity must have quantity greater than 0');
            return false;
        }

        if (!projectData.preferredStartDate) {
            toast.error('Preferred start date is required');
            return false;
        }
        if (!projectData.preferredCompletionDate) {
            toast.error('Preferred completion date is required');
            return false;
        }
        // Validate dates logic
        const startDate = new Date(projectData.preferredStartDate);
        const endDate = new Date(projectData.preferredCompletionDate);
        if (endDate <= startDate) {
            toast.error('Completion date must be after start date');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        // Validate all fields
        if (!validateFields()) {
            return;
        }

        setSaving(true);

        try {
            const updatePayload = {
                ClientName: projectData.clientName,
                ClientPhone: (projectData.clientPhone && projectData.clientPhone.trim()) ? projectData.clientPhone.trim() : null,
                ClientEmail: (projectData.clientEmail && projectData.clientEmail.trim()) ? projectData.clientEmail.trim() : null,
                SiteAddress: (projectData.location && projectData.location.trim()) ? projectData.location.trim() : null,
                CityId: projectData.cityId || null,
                Pincode: (projectData.pincode && projectData.pincode.trim()) ? projectData.pincode.trim() : null,
                Sqft: parseInt(projectData.sqft) || 0,
                PreferredStartDate: projectData.preferredStartDate || null,
                PreferredCompletionDate: projectData.preferredCompletionDate || null,
                EstimatedDays: projectData.estimatedDays || null,
                Latitude: projectData.latitude ? parseFloat(projectData.latitude) : null,
                Longitude: projectData.longitude ? parseFloat(projectData.longitude) : null,
                HasExtraWork: projectData.hasExtraWork || false,
                ExtraWorkNotes: (projectData.extraWorkNotes && projectData.extraWorkNotes.trim()) ? projectData.extraWorkNotes.trim() : null,
                Entities: projectData.entities.map(e => ({
                    EntityId: e.id,
                    Quantity: e.quantity
                }))
            };

            console.log('Update Payload:', updatePayload);

            await api.put(`/api/projects/${id}`, updatePayload);

            toast.success('Project updated successfully');
            setIsEditMode(false);
            setOriginalProjectData(null); // Clear original data after successful save

            // Reload project data
            window.location.reload();
        } catch (error) {
            console.error('Error updating project:', error);
            toast.error(error.message || 'Failed to update project');
        } finally {
            setSaving(false);
        }
    };

    // Initialize Google Maps when modal opens
    useEffect(() => {
        // Only initialize map if data has been loaded (not loading anymore)
        if (mapModalOpen && mapLoaded && !map && !loading) {
            // Add small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                const mapContainer = document.getElementById('edit-project-map');

                if (!mapContainer) {
                    console.error('Map container not found');
                    return;
                }

                try {
                    // Initialize the map
                    const newMap = initializeMap('edit-project-map', {
                        center: {
                            lat: projectData.latitude ? parseFloat(projectData.latitude) : 20.5937,
                            lng: projectData.longitude ? parseFloat(projectData.longitude) : 78.9629
                        },
                        zoom: projectData.latitude && projectData.longitude ? 15 : 5,
                        mapTypeId: mapType
                    });

                    console.log('Initializing map with coordinates:', {
                        lat: projectData.latitude,
                        lng: projectData.longitude,
                        hasCoordinates: !!(projectData.latitude && projectData.longitude)
                    });

                    // Check if initialization was successful
                    if (!newMap) {
                        console.error('Failed to initialize map');
                        toast.error('Failed to load map. Please try again.');
                        return;
                    }

                    // Only create search box if in edit mode
                    if (isEditMode && canEdit) {
                        const searchInput = document.getElementById('edit-location-search');
                        if (searchInput) {
                            const newSearchBox = createSearchBox(searchInput, newMap);
                            if (newSearchBox) {
                                // Handle place changed
                                newSearchBox.addListener('places_changed', () => {
                                    // Set flag to prevent modal from closing
                                    isSelectingPlaceRef.current = true;

                                    const places = newSearchBox.getPlaces();
                                    if (places && places.length > 0) {
                                        const place = places[0];
                                        if (place.geometry && place.geometry.location) {
                                            updateMarker(newMap, place.geometry.location, place.formatted_address);
                                            newMap.setCenter(place.geometry.location);
                                            newMap.setZoom(17);

                                            // Clear search input after selection
                                            if (searchInput) {
                                                searchInput.value = '';
                                            }
                                        }
                                    }

                                    // Reset flag after a short delay
                                    setTimeout(() => {
                                        isSelectingPlaceRef.current = false;
                                    }, 500);
                                });

                                setSearchBox(newSearchBox);
                            } else {
                                console.error('Failed to create search box');
                            }
                        }

                        // Handle map click (only in edit mode)
                        newMap.addListener('click', async (e) => {
                            updateMarker(newMap, e.latLng);

                            // Reverse geocode
                            try {
                                const address = await reverseGeocode(e.latLng);
                                setProjectData(prev => ({
                                    ...prev,
                                    location: address
                                }));
                            } catch (error) {
                                console.error('Geocoding error:', error);
                            }
                        });
                    }

                    // If we have existing coordinates, place a marker and center map
                    console.log('Checking coordinates for marker:', {
                        latitude: projectData.latitude,
                        longitude: projectData.longitude,
                        latType: typeof projectData.latitude,
                        lngType: typeof projectData.longitude,
                        latTruthy: !!projectData.latitude,
                        lngTruthy: !!projectData.longitude,
                        latLength: projectData.latitude ? projectData.latitude.length : 0,
                        lngLength: projectData.longitude ? projectData.longitude.length : 0
                    });

                    // Check for non-empty string coordinates (not just truthy)
                    if (projectData.latitude && projectData.longitude &&
                        projectData.latitude !== '' && projectData.longitude !== '' &&
                        projectData.latitude !== 'null' && projectData.longitude !== 'null') {
                        console.log('Coordinates exist, creating marker...');
                        const position = {
                            lat: parseFloat(projectData.latitude),
                            lng: parseFloat(projectData.longitude)
                        };
                        console.log('Parsed position:', position);

                        // Validate parsed coordinates are valid numbers
                        if (!isNaN(position.lat) && !isNaN(position.lng)) {
                            updateMarker(newMap, position);
                            // Ensure map is centered on the marker
                            newMap.setCenter(position);
                            newMap.setZoom(15);
                        } else {
                            console.error('Invalid coordinates - NaN after parsing');
                        }
                    } else {
                        console.log('No valid coordinates - marker not created', {
                            lat: projectData.latitude,
                            lng: projectData.longitude
                        });
                    }

                    setMap(newMap);
                } catch (error) {
                    console.error('Error initializing map:', error);
                    toast.error('Failed to load map. Please try again.');
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [mapModalOpen, mapLoaded, map, mapType, projectData.latitude, projectData.longitude, isEditMode, canEdit, loading]);

    // Cleanup map when modal closes
    useEffect(() => {
        if (!mapModalOpen && map) {
            // Reset map state when modal closes
            setMap(null);
            setSearchBox(null);
            if (markerRef.current) {
                markerRef.current.setMap(null);
                markerRef.current = null;
            }
        }
    }, [mapModalOpen]);

    // Fix Google Places autocomplete dropdown z-index and pointer events
    useEffect(() => {
        if (mapModalOpen && searchBox) {
            // Wait a bit for the pac-container to be created by Google
            const timer = setTimeout(() => {
                const pacContainers = document.querySelectorAll('.pac-container');
                pacContainers.forEach(container => {
                    container.style.zIndex = '9999';
                    container.style.pointerEvents = 'auto';

                    // Prevent dialog from closing when interacting with pac-container
                    const preventClose = (e) => {
                        e.stopPropagation();
                    };

                    container.addEventListener('mousedown', preventClose, true);
                    container.addEventListener('click', preventClose, true);
                    container.addEventListener('touchstart', preventClose, true);

                    // Mark this container as processed
                    container.setAttribute('data-dialog-ignore', 'true');
                });
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [mapModalOpen, searchBox]);

    const updateMarker = (mapInstance, location, address = null) => {
        // Remove existing marker if it exists
        if (markerRef.current) {
            markerRef.current.setMap(null);
        }

        // Handle both Google Maps LatLng objects and plain objects
        const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
        const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

        console.log('Placing marker at:', { lat, lng });

        // Create position object for marker
        const position = { lat, lng };

        // Create new marker (only draggable in edit mode)
        const newMarker = createMarker(mapInstance, position, {
            draggable: isEditMode && canEdit
        });

        if (!newMarker) {
            console.error('Failed to create marker');
            return;
        }

        console.log('Marker created successfully');

        // Update coordinates
        setProjectData(prev => ({
            ...prev,
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6),
            ...(address && { location: address })
        }));

        // Handle marker drag (only in edit mode)
        if (isEditMode && canEdit) {
            newMarker.addListener('dragend', async (e) => {
                const newPos = e.latLng;
                const newLat = typeof newPos.lat === 'function' ? newPos.lat() : newPos.lat;
                const newLng = typeof newPos.lng === 'function' ? newPos.lng() : newPos.lng;

                setProjectData(prev => ({
                    ...prev,
                    latitude: newLat.toFixed(6),
                    longitude: newLng.toFixed(6)
                }));

                // Reverse geocode
                try {
                    const address = await reverseGeocode(newPos);
                    setProjectData(prev => ({
                        ...prev,
                        location: address
                    }));
                } catch (error) {
                    console.error('Geocoding error:', error);
                }
            });
        }

        markerRef.current = newMarker;
    };

    // Clear location
    const handleClearLocation = () => {
        if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
        }
        setProjectData(prev => ({
            ...prev,
            latitude: '',
            longitude: '',
            location: ''
        }));

        // Reset map to India center
        if (map) {
            map.setCenter({ lat: 20.5937, lng: 78.9629 });
            map.setZoom(5);
        }
    };

    // Get user's current location
    const getUserLocation = () => {
        if (!navigator.geolocation) {
            toast.error('Geolocation is not supported by your browser');
            return;
        }

        if (!map) {
            toast.error('Map is not initialized');
            return;
        }

        toast.loading('Getting your location...');

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                toast.dismiss();
                const location = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };

                updateMarker(map, location);
                map.setCenter(location);
                map.setZoom(17);

                // Reverse geocode
                try {
                    const address = await reverseGeocode(location);
                    setProjectData(prev => ({
                        ...prev,
                        location: address
                    }));
                    toast.success('Location updated');
                } catch (error) {
                    console.error('Geocoding error:', error);
                    toast.success('Location updated (address unavailable)');
                }
            },
            (error) => {
                toast.dismiss();
                toast.error('Unable to retrieve your location');
                console.error('Geolocation error:', error);
            }
        );
    };

    // Toggle map type
    const toggleMapType = () => {
        const newType = mapType === 'roadmap' ? 'satellite' : 'roadmap';
        setMapType(newType);
        if (map) {
            map.setMapTypeId(newType);
        }
    };

    const handleViewMap = () => {
        setMapModalOpen(true);
    };

    const handleSaveLocation = () => {
        setMapModalOpen(false);
        toast.success('Location updated. Remember to save changes.');
    };

    // Handle entering edit mode
    const handleEditMode = () => {
        // Save current data before editing
        setOriginalProjectData(JSON.parse(JSON.stringify(projectData)));
        setIsEditMode(true);
    };

    // Handle cancel edit mode
    const handleCancelEdit = () => {
        if (originalProjectData) {
            // Restore original data
            setProjectData(originalProjectData);
            setOriginalProjectData(null);
            toast('Changes discarded');
        }
        setIsEditMode(false);
    };

    // Get SAS URL for a document
    const getDocumentSasUrl = async (documentId) => {
        try {
            const response = await api.get(`/api/projects/${id}/documents/${documentId}/download`);
            return response.downloadUrl;
        } catch (error) {
            console.error('Error getting document URL:', error);
            throw error;
        }
    };

    // Handle document view - open images in viewer, others in new tab
    const handleViewDocument = async (documentId) => {
        try {
            const doc = documents.find(d => d.id === documentId);
            if (!doc) return;

            const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];

            if (imageTypes.includes(doc.mimeType?.toLowerCase())) {
                // Open image viewer for images
                const imageDocuments = documents.filter(d => imageTypes.includes(d.mimeType?.toLowerCase()));
                const imageIndex = imageDocuments.findIndex(d => d.id === documentId);
                setCurrentImageIndex(imageIndex);
                setImageViewerOpen(true);

                // Preload current image URL if not already loaded
                if (!imageUrls[documentId]) {
                    const url = await getDocumentSasUrl(documentId);
                    setImageUrls(prev => ({ ...prev, [documentId]: url }));
                }
            } else {
                // Open PDFs and documents in new tab
                const url = await getDocumentSasUrl(documentId);
                window.open(url, '_blank');
            }
        } catch (error) {
            console.error('Error viewing document:', error);
            toast.error('Failed to open document');
        }
    };

    // Handle file selection for upload
    const handleUploadFileChange = (e) => {
        const files = Array.from(e.target.files);

        // Validation rules
        const allowedMimeTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'image/bmp',
            'image/svg+xml'
        ];
        const maxFileSizeMB = 10;
        const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

        const validFiles = [];
        const invalidFiles = [];

        files.forEach(file => {
            if (file.size > maxFileSizeBytes) {
                invalidFiles.push(`${file.name} exceeds ${maxFileSizeMB}MB limit`);
                return;
            }
            if (!allowedMimeTypes.includes(file.type)) {
                invalidFiles.push(`${file.name} has invalid type`);
                return;
            }
            validFiles.push(file);
        });

        if (invalidFiles.length > 0) {
            toast.error(invalidFiles.join('\n'));
        }

        if (validFiles.length > 0) {
            const filesWithCategory = validFiles.map(file => ({
                file,
                subfolder: 'general'
            }));
            setFilesToUpload(prev => [...prev, ...filesWithCategory]);
        }
    };

    // Remove file from upload list
    const removeUploadFile = (index) => {
        const updatedFiles = filesToUpload.filter((_, i) => i !== index);
        setFilesToUpload(updatedFiles);

        // Clear file input if all files removed
        if (updatedFiles.length === 0 && uploadFileInputRef.current) {
            uploadFileInputRef.current.value = '';
        }
    };

    // Handle document upload
    const handleUploadDocuments = async () => {
        if (filesToUpload.length === 0) {
            toast.error('Please select at least one file');
            return;
        }

        setIsUploading(true);
        let successCount = 0;
        let failCount = 0;

        try {
            for (const fileObj of filesToUpload) {
                try {
                    const formData = new FormData();
                    formData.append('file', fileObj.file);
                    formData.append('documentTypeCode', 'project_document');
                    formData.append('subfolder', fileObj.subfolder);

                    // Don't set Content-Type header - let browser set it with boundary
                    await api.post(`/api/projects/${id}/documents/upload`, formData);
                    successCount++;
                } catch (error) {
                    console.error('Error uploading file:', fileObj.file.name, error);
                    console.error('File details:', {
                        name: fileObj.file.name,
                        type: fileObj.file.type,
                        size: fileObj.file.size,
                        subfolder: fileObj.subfolder
                    });
                    toast.error(`Failed to upload ${fileObj.file.name}: ${error.message}`);
                    failCount++;
                }
            }

            if (successCount > 0) {
                toast.success(`${successCount} document(s) uploaded successfully`);
                // Refresh documents list
                const response = await api.get(`/api/projects/${id}/documents?includeDocumentTypeCodes=hardware_list,project_document`);
                setDocuments(response.documents || []);
            }

            if (failCount > 0) {
                toast.error(`${failCount} document(s) failed to upload`);
            }

            // Close dialog and reset
            setUploadDialogOpen(false);
            setFilesToUpload([]);
            if (uploadFileInputRef.current) {
                uploadFileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Error uploading documents:', error);
            toast.error('Failed to upload documents');
        } finally {
            setIsUploading(false);
        }
    };

    // Handle hardware document upload
    const handleHardwareUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setHardwareUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('documentTypeCode', DocumentTypeCode.HardwareList);

            await api.post(`/api/projects/${id}/documents/upload`, formData);
            toast.success('Hardware list uploaded successfully');

            // Update local state
            setProjectData(prev => ({ ...prev, hardwareListUploaded: true }));

            // Refresh documents list
            const response = await api.get(`/api/projects/${id}/documents?includeDocumentTypeCodes=hardware_list,project_document`);
            setDocuments(response.documents || []);
        } catch (error) {
            console.error('Error uploading hardware list:', error);
            toast.error('Failed to upload hardware list: ' + (error.message || 'Unknown error'));
        } finally {
            setHardwareUploading(false);
            if (hardwareFileInputRef.current) {
                hardwareFileInputRef.current.value = '';
            }
        }
    };

    // Handle document download - fetch from backend stream as blob
    const handleDownloadDocument = async (documentId, fileName) => {
        try {
            // Show loading state
            setDownloadingDocId(documentId);
            toast.loading('Downloading ' + fileName + '...');

            // Get auth token from localStorage
            const token = localStorage.getItem('alignworks_token');

            // Fetch file from backend stream endpoint with auth header
            const response = await fetch(`http://localhost:5133/api/projects/${id}/documents/${documentId}/stream`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Download failed');
            }

            // Get the blob data
            const blob = await response.blob();

            // Create a temporary blob URL
            const blobUrl = window.URL.createObjectURL(blob);

            // Create invisible link and trigger download
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL
            window.URL.revokeObjectURL(blobUrl);

            toast.dismiss();
            toast.success('Downloaded ' + fileName);
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.dismiss();
            toast.error('Failed to download document');
        } finally {
            setDownloadingDocId(null);
        }
    };

    // Handle document deletion with confirmation
    const handleDeleteDocument = async (documentId, fileName) => {
        try {
            setDeletingDocId(documentId);
            toast.loading('Deleting ' + fileName + '...');

            // Check if this is a hardware doc before deleting
            const docToRemove = documents.find(doc => doc.id === documentId);
            const isHardwareDoc = docToRemove && (docToRemove.subfolder === 'hardware' || docToRemove.documentType === 'Hardware List');

            await api.delete(`/api/projects/${id}/documents/${documentId}`);

            // Remove from local state
            const updatedDocs = documents.filter(doc => doc.id !== documentId);
            setDocuments(updatedDocs);

            // If hardware doc deleted, check if any remain and update flag
            if (isHardwareDoc) {
                const remainingHwDocs = updatedDocs.filter(doc => doc.subfolder === 'hardware' || doc.documentType === 'Hardware List');
                if (remainingHwDocs.length === 0) {
                    setProjectData(prev => ({ ...prev, hardwareListUploaded: false }));
                }
            }

            toast.dismiss();
            toast.success('Document deleted successfully');
            setDeleteConfirmOpen(false);
            setDocumentToDelete(null);
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.dismiss();
            toast.error('Failed to delete document');
        } finally {
            setDeletingDocId(null);
        }
    };

    // Show delete confirmation dialog
    const confirmDeleteDocument = (documentId, fileName) => {
        setDocumentToDelete({ id: documentId, fileName, type: 'project' });
        setDeleteConfirmOpen(true);
    };

    // Format file size for display (API returns size in MB)
    const formatFileSize = (fileSizeMB) => {
        if (!fileSizeMB) return 'Unknown size';
        if (fileSizeMB < 1) return (fileSizeMB * 1024).toFixed(2) + ' KB';
        return fileSizeMB.toFixed(2) + ' MB';
    };

    // Format subfolder name for display
    const formatSubfolder = (subfolder) => {
        if (!subfolder) return 'General';
        return subfolder.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    // Handle phone number input - allow only valid phone characters
    const handlePhoneChange = (e) => {
        const value = e.target.value;
        // Allow only numbers, +, -, spaces, and parentheses
        const phoneRegex = /^[\d\s\+\-\(\)]*$/;

        // Extract only digits to count them
        const digitsOnly = value.replace(/\D/g, '');

        // Allow input only if it contains valid characters and has 10 or fewer digits
        if (phoneRegex.test(value) && digitsOnly.length <= 10) {
            setProjectData({ ...projectData, clientPhone: value });
        }
    };

    // Custom handler to prevent modal from closing when clicking on Google Places dropdown
    const handleMapModalOpenChange = (open) => {
        setMapModalOpen(open);
    };

    // Prevent dialog from closing when clicking on Google Places dropdown
    const handleInteractOutside = (e) => {
        // Check if click is on pac-container or its children
        const target = e.target;
        const pacContainer = target.closest('.pac-container');

        if (pacContainer) {
            e.preventDefault();
            return;
        }

        // Check if any pac-container is visible
        const pacContainers = document.querySelectorAll('.pac-container');
        for (const container of pacContainers) {
            if (container && container.offsetParent !== null) {
                e.preventDefault();
                return;
            }
        }

        // Check if we're selecting a place
        if (isSelectingPlaceRef.current) {
            e.preventDefault();
            return;
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            'Completed': 'bg-green-100 text-green-800 border-green-300',
            'In Progress': 'bg-yellow-100 text-yellow-800 border-yellow-300',
            'Scheduled': 'bg-blue-100 text-blue-800 border-blue-300',
            'Pending': 'bg-gray-100 text-gray-800 border-gray-300',
            'Approved': 'bg-green-100 text-green-800 border-green-300',
            'Rejected': 'bg-red-100 text-red-800 border-red-300',
            'Paid': 'bg-green-100 text-green-800 border-green-300',
            'draft': 'bg-orange-100 text-orange-800 border-orange-300',
            'Draft': 'bg-orange-100 text-orange-800 border-orange-300',
            'sent': 'bg-blue-100 text-blue-800 border-blue-300',
            'Sent': 'bg-blue-100 text-blue-800 border-blue-300',
            'approved': 'bg-green-100 text-green-800 border-green-300',
            'rejected': 'bg-red-100 text-red-800 border-red-300',
            'expired': 'bg-gray-100 text-gray-600 border-gray-300',
            'Expired': 'bg-gray-100 text-gray-600 border-gray-300'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Format quotation status for display
    const formatQuotationStatus = (status) => {
        const statusMap = {
            'draft': 'Draft',
            'sent': 'Sent to OEM',
            'approved': 'Approved',
            'rejected': 'Rejected',
            'expired': 'Expired'
        };
        return statusMap[status?.toLowerCase()] || status;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading project details...</p>
                </div>
            </div>
        );
    }

    // Fetch documents for a quotation
    const fetchQuotationDocuments = async (quotationId) => {
        if (!quotationId) return;

        setLoadingQuotationDocuments(true);
        try {
            console.log('Fetching documents for quotation ID:', quotationId);
            console.log('Project ID:', id);

            const params = {
                referenceType: 'quotation',
                referenceId: quotationId
            };
            console.log('Params to send:', params);

            const response = await api.get(`/api/projects/${id}/documents`, {
                params: params
            });

            console.log('Full URL would be:', `/api/projects/${id}/documents?referenceType=quotation&referenceId=${quotationId}`);

            console.log('Fetched quotation documents - Full Response:', response);
            console.log('Documents count:', response.documents?.length);
            console.log('Documents:', response.documents);

            setQuotationDocuments(response.documents || []);
        } catch (error) {
            console.error('Error fetching quotation documents:', error);
            toast.error('Failed to load quotation documents');
        } finally {
            setLoadingQuotationDocuments(false);
        }
    };

    // Handle viewing quotation document with SAS token
    const handleViewQuotationDocument = async (documentId) => {
        try {
            const url = await getDocumentSasUrl(documentId);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error viewing quotation document:', error);
            toast.error('Failed to open document');
        }
    };

    // Handle downloading quotation document
    const handleDownloadQuotationDocument = async (documentId, fileName) => {
        try {
            toast.loading('Downloading ' + fileName + '...');

            // Get auth token from localStorage
            const token = localStorage.getItem('alignworks_token');

            // Fetch file from backend stream endpoint with auth header
            const response = await fetch(`http://localhost:5133/api/projects/${id}/documents/${documentId}/stream`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Download failed');
            }

            // Get the blob data
            const blob = await response.blob();

            // Create a temporary blob URL
            const blobUrl = window.URL.createObjectURL(blob);

            // Create invisible link and trigger download
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL
            window.URL.revokeObjectURL(blobUrl);

            toast.dismiss();
            toast.success('Downloaded ' + fileName);
        } catch (error) {
            console.error('Error downloading quotation document:', error);
            toast.dismiss();
            toast.error('Failed to download document');
        }
    };

    // Handle deleting quotation document
    const handleDeleteQuotationDocument = async (documentId, fileName) => {
        try {
            setDeletingDocId(documentId);
            toast.loading('Deleting ' + fileName + '...');

            await api.delete(`/api/projects/${id}/documents/${documentId}`);

            // Remove from local state
            setQuotationDocuments(prev => prev.filter(doc => doc.id !== documentId));

            toast.dismiss();
            toast.success('Document deleted successfully');
            setDeleteConfirmOpen(false);
            setDocumentToDelete(null);
        } catch (error) {
            console.error('Error deleting quotation document:', error);
            toast.dismiss();
            toast.error('Failed to delete document');
        } finally {
            setDeletingDocId(null);
        }
    };

    // Confirm delete for quotation document
    const confirmDeleteQuotationDocument = (documentId, fileName) => {
        setDocumentToDelete({ id: documentId, fileName, type: 'quotation' });
        setDeleteConfirmOpen(true);
    };

    // Handle quotation document upload
    const handleQuotationDocumentUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type (PDFs only for quotations)
        if (file.type !== 'application/pdf') {
            toast.error('Please upload PDF files only');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setUploadingQuotationDoc(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentTypeCode', DocumentTypeCode.QuotationPdf);
        formData.append('referenceType', DocumentReferenceType.Quotation);
        formData.append('referenceId', quotationForUpload.id);

        try {
            const response = await api.post(
                `/api/projects/${id}/documents/upload`,
                formData
            );

            toast.success('Document uploaded successfully');

            // Refresh quotation documents
            await fetchQuotationDocuments(quotationForUpload.id);

            // Refresh quotations list
            const quotationsResponse = await api.get(`/api/projects/${id}/quotations`);
            setQuotations(quotationsResponse.quotations || []);

            // Update selected quotation if it's the one we just uploaded to
            if (selectedQuotation && selectedQuotation.id === quotationForUpload.id) {
                const updated = quotationsResponse.quotations?.find(q => q.id === quotationForUpload.id);
                if (updated) {
                    setSelectedQuotation(updated);
                }
            }

            // Close dialog and reset
            setQuotationDocumentUploadOpen(false);
            setQuotationForUpload(null);
            if (quotationDocFileInputRef.current) {
                quotationDocFileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Error uploading quotation document:', error);
            toast.error(error.response?.data?.message || 'Failed to upload document');
        } finally {
            setUploadingQuotationDoc(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top Bar - Sticky */}
            <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-2 sm:gap-4">
                            <Button
                                variant="ghost"
                                onClick={() => navigate(-1)}
                                size="sm"
                            >
                                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                                <span className="inline">Back</span>
                            </Button>
                            <div className="min-w-0 flex-1">
                                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{projectData.clientName}</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3">
                            <Badge className={getStatusColor(projectData.currentStage)}>
                                <span className="text-xs">{projectData.currentStage}</span>
                            </Badge>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    {/* Vertical Tabs List - Horizontal on Mobile */}
                    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                        {/* Mobile: Horizontal scrolling tabs */}
                        <TabsList className="flex lg:flex-col overflow-x-auto lg:overflow-visible h-fit lg:w-48 bg-white border shadow-sm p-2 gap-1">
                            <TabsTrigger value="overview" className="justify-start w-auto lg:w-full shrink-0">
                                <FileText className="w-4 h-4 mr-2" />
                                <span className="inline">Overview</span>
                            </TabsTrigger>
                            {!isReadOnlyViewer && (
                                <TabsTrigger value="quotation" className="justify-start w-auto lg:w-full shrink-0">
                                    <DollarSign className="w-4 h-4 mr-2" />
                                    <span className="inline">Quotation</span>
                                </TabsTrigger>
                            )}
                            <TabsTrigger value="documents" className="justify-start w-auto lg:w-full shrink-0">
                                <Upload className="w-4 h-4 mr-2" />
                                <span className="inline">Documents</span>
                            </TabsTrigger>
                            <TabsTrigger value="requests" className="justify-start w-auto lg:w-full shrink-0">
                                <AlertCircle className="w-4 h-4 mr-2" />
                                <span className="inline">Requests</span>
                            </TabsTrigger>
                            {isAdmin && projectData.currentStage !== StageName.NewProject &&
                                projectData.currentStage !== StageName.QuotationPending &&
                                projectData.currentStage !== StageName.QuotationApprovalPending &&
                                projectData.currentStage !== StageName.QuotationRejected && (
                                    <>
                                        <TabsTrigger value="bills" className="justify-start w-auto lg:w-full shrink-0">
                                            <Receipt className="w-4 h-4 mr-2" />
                                            <span className="inline">Bills</span>
                                        </TabsTrigger>
                                        <TabsTrigger value="sitevisits" className="justify-start w-auto lg:w-full shrink-0">
                                            <ClipboardCheck className="w-4 h-4 mr-2" />
                                            <span className="inline">Site Visits</span>
                                        </TabsTrigger>
                                        <TabsTrigger value="installation" className="justify-start w-auto lg:w-full shrink-0">
                                            <Wrench className="w-4 h-4 mr-2" />
                                            <span className="inline">Installation</span>
                                        </TabsTrigger>
                                        <TabsTrigger value="payments" className="justify-start w-auto lg:w-full shrink-0">
                                            <CreditCard className="w-4 h-4 mr-2" />
                                            <span className="inline">Payments</span>
                                        </TabsTrigger>
                                    </>
                                )}
                            {/* Site Visits tab for admin/OEM at quotation_pending or rejected stage */}
                            {(isAdmin || isOEM) && (projectData.currentStage === StageName.QuotationApprovalPending || projectData.currentStage === StageName.QuotationRejected) && (
                                <TabsTrigger value="sitevisits" className="justify-start w-auto lg:w-full shrink-0">
                                    <ClipboardCheck className="w-4 h-4 mr-2" />
                                    <span className="inline">Site Visits</span>
                                </TabsTrigger>
                            )}
                            {/* Site Visits tab for OEM/Supervisor (read-only, after SV1 scheduled) */}
                            {(isOEM || isSupervisor) && showSiteVisitsTab && (
                                <TabsTrigger value="sitevisits" className="justify-start w-auto lg:w-full shrink-0">
                                    <ClipboardCheck className="w-4 h-4 mr-2" />
                                    <span className="inline">Site Visits</span>
                                </TabsTrigger>
                            )}
                            {/* Bills tab for OEM (read-only) */}
                            {isOEM && showSiteVisitsTab && (
                                <TabsTrigger value="bills" className="justify-start w-auto lg:w-full shrink-0">
                                    <Receipt className="w-4 h-4 mr-2" />
                                    <span className="inline">Bills</span>
                                </TabsTrigger>
                            )}
                            {/* Installation tab for OEM/Supervisor (after installation scheduled) */}
                            {(isOEM || isSupervisor) && showInstallationTab && (
                                <TabsTrigger value="installation" className="justify-start w-auto lg:w-full shrink-0">
                                    <Wrench className="w-4 h-4 mr-2" />
                                    <span className="inline">Installation</span>
                                </TabsTrigger>
                            )}
                            {/* Payment tab for OEM (from Quotation Approved stage onwards) */}
                            {isOEM && showPaymentsTab && (
                                <TabsTrigger value="payments" className="justify-start w-auto lg:w-full shrink-0">
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    <span className="inline">Payments</span>
                                </TabsTrigger>
                            )}
                            {!isReadOnlyViewer && (
                                <TabsTrigger value="history" className="justify-start w-auto lg:w-full shrink-0">
                                    <Clock className="w-4 h-4 mr-2" />
                                    <span className="inline">History</span>
                                </TabsTrigger>
                            )}
                            {/* Sign-off tab for OEM and Admin */}
                            {showSignoffTab && (
                                <TabsTrigger value="signoff" className="justify-start w-auto lg:w-full shrink-0">
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                    <span className="inline">Sign Off</span>
                                </TabsTrigger>
                            )}
                            {/* Notifications tab for Admin */}
                            {isAdmin && (
                                <TabsTrigger value="notifications" className="justify-start w-auto lg:w-full shrink-0">
                                    <Mail className="w-4 h-4 mr-2" />
                                    <span className="inline">Notifications</span>
                                </TabsTrigger>
                            )}
                        </TabsList>

                        {/* Tab Content */}
                        <div className="flex-1">
                            {/* Overview Tab */}
                            <TabsContent value="overview" className="space-y-6 mt-0">
                                {/* On-Hold Banner */}
                                {projectData.isOnHold && (
                                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
                                        <PauseCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="font-semibold text-amber-800">Project is On Hold</p>
                                            {projectData.onHoldReason && (
                                                <p className="text-sm text-amber-700 mt-1">Reason: {projectData.onHoldReason}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {/* Client Information */}
                                <Card>
                                    <CardHeader>
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <CardTitle>Client Information</CardTitle>
                                            {/* Edit/Save Buttons */}
                                            <div className="flex flex-wrap gap-2">
                                                {canEdit && (
                                                    <>
                                                        {!isEditMode ? (
                                                            <Button onClick={handleEditMode} className="bg-teal-600 hover:bg-teal-700">
                                                                <Edit className="w-4 h-4 mr-2" />
                                                                Edit Project
                                                            </Button>
                                                        ) : (
                                                            <>
                                                                <Button
                                                                    onClick={handleSave}
                                                                    className="bg-teal-600 hover:bg-teal-700"
                                                                    disabled={saving}
                                                                >
                                                                    <Save className="w-4 h-4 mr-2" />
                                                                    {saving ? 'Saving...' : 'Save Changes'}
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    onClick={handleCancelEdit}
                                                                    disabled={saving}
                                                                >
                                                                    <X className="w-4 h-4 mr-2" />
                                                                    Cancel
                                                                </Button>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                                {isAdmin && !isEditMode && [StageName.NewProject, StageName.QuotationRejected, StageName.QuotationApprovalPending, StageName.QuotationApproved].includes(projectData.currentStage) && (
                                                    <Button
                                                        variant="outline"
                                                        className="text-red-600 hover:bg-red-50 border-red-300"
                                                        onClick={() => setDeleteDialogOpen(true)}
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Delete
                                                    </Button>
                                                )}
                                                {/* Put On Hold — only during Installation In Progress, admin or supervisor */}
                                                {(isAdmin || isSupervisor) && !isEditMode && projectData.currentStage === StageName.InstallationInProgress && !projectData.isOnHold && (
                                                    <Button
                                                        variant="outline"
                                                        className="text-orange-600 hover:bg-orange-50 border-orange-300"
                                                        onClick={() => { setHoldReason(''); setHoldDialogOpen(true); }}
                                                    >
                                                        <PauseCircle className="w-4 h-4 mr-2" />
                                                        Put On Hold
                                                    </Button>
                                                )}
                                                {/* Resume — only when on hold, admin only */}
                                                {isAdmin && !isEditMode && projectData.isOnHold && (
                                                    <Button
                                                        variant="outline"
                                                        className="text-green-600 hover:bg-green-50 border-green-300"
                                                        onClick={() => setResumeDialogOpen(true)}
                                                    >
                                                        <PlayCircle className="w-4 h-4 mr-2" />
                                                        Resume Project
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2 field-row">
                                                <Label>Client Name *</Label>
                                                {isEditMode && canEdit ? (
                                                    <Input
                                                        value={projectData.clientName}
                                                        onChange={(e) => setProjectData({ ...projectData, clientName: e.target.value })}
                                                        placeholder="Enter client name"
                                                    />
                                                ) : (
                                                    <div className="flex items-center gap-2 text-gray-900">
                                                        <User className="w-4 h-4 text-gray-500" />
                                                        {projectData.clientName}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="space-y-2 field-row">
                                                <Label>Phone</Label>
                                                {isEditMode && canEdit ? (
                                                    <Input
                                                        type="tel"
                                                        value={projectData.clientPhone || ''}
                                                        onChange={handlePhoneChange}
                                                        placeholder="Enter 10 digit phone number"
                                                    />
                                                ) : (
                                                    <div className="flex items-center gap-2 text-gray-900">
                                                        <Phone className="w-4 h-4 text-gray-500" />
                                                        {projectData.clientPhone || 'N/A'}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="space-y-2 field-row">
                                                <Label>Email</Label>
                                                {isEditMode && canEdit ? (
                                                    <Input
                                                        type="email"
                                                        value={projectData.clientEmail || ''}
                                                        onChange={(e) => setProjectData({ ...projectData, clientEmail: e.target.value })}
                                                        placeholder="Enter email address (e.g., client@example.com)"
                                                    />
                                                ) : (
                                                    <div className="flex items-center gap-2 text-gray-900">
                                                        <Mail className="w-4 h-4 text-gray-500" />
                                                        {projectData.clientEmail || 'N/A'}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="space-y-2 md:col-span-2">
                                                <Label>Location</Label>
                                                <div className="flex items-center gap-2">
                                                    {isEditMode && canEdit ? (
                                                        <Input
                                                            value={projectData.location || ''}
                                                            onChange={(e) => setProjectData({ ...projectData, location: e.target.value })}
                                                            className="flex-1"
                                                            placeholder="Enter site address"
                                                        />
                                                    ) : (
                                                        <div className="flex items-center gap-2 text-gray-900 flex-1">
                                                            <MapPin className="w-4 h-4 text-gray-500" />
                                                            {projectData.location || 'N/A'}
                                                        </div>
                                                    )}
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={handleViewMap}
                                                        className="shrink-0"
                                                    >
                                                        <MapPin className="w-4 h-4 mr-1" />
                                                        {isEditMode ? 'Edit Map' : 'View Map'}
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="space-y-2 field-row">
                                                <Label>Square Footage *</Label>
                                                {isEditMode && canEdit ? (
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        value={projectData.sqft || ''}
                                                        onChange={(e) => setProjectData({ ...projectData, sqft: e.target.value })}
                                                        placeholder="Enter square footage"
                                                    />
                                                ) : (
                                                    <p className="text-gray-900">{projectData.sqft || 0} sqft</p>
                                                )}
                                            </div>
                                            <div className="space-y-2 field-row">
                                                <Label>Pincode</Label>
                                                {isEditMode && canEdit ? (
                                                    <Input
                                                        value={projectData.pincode || ''}
                                                        onChange={(e) => setProjectData({ ...projectData, pincode: e.target.value })}
                                                        placeholder="Enter pincode"
                                                    />
                                                ) : (
                                                    <p className="text-gray-900">{projectData.pincode || 'N/A'}</p>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Project Details */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Project Details</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2 field-row">
                                                <Label>OEM</Label>
                                                <p className="text-gray-900">{projectData.oemName}</p>
                                            </div>
                                            <div className="space-y-2 field-row">
                                                <Label>Current Stage</Label>
                                                <div>
                                                    <Badge className={getStatusColor(projectData.currentStage)}>
                                                        {projectData.currentStage}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2 field-row">
                                                <Label>Preferred Start Date *</Label>
                                                {isEditMode && canEdit ? (
                                                    <Popover open={editStartDateOpen} onOpenChange={setEditStartDateOpen}>
                                                        <PopoverTrigger asChild>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                className="w-full justify-start text-left font-normal h-11"
                                                            >
                                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                                {projectData.preferredStartDate
                                                                    ? format(new Date(projectData.preferredStartDate), 'PPP')
                                                                    : <span className="text-muted-foreground">Pick a date</span>}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0" align="start">
                                                            <Calendar
                                                                mode="single"
                                                                selected={projectData.preferredStartDate ? new Date(projectData.preferredStartDate) : undefined}
                                                                onSelect={(date) => {
                                                                    if (!date) return;
                                                                    if (isHoliday(date)) {
                                                                        toast.error(`${getHolidayName(date) || format(date, 'yyyy-MM-dd')} is a holiday`);
                                                                        return;
                                                                    }
                                                                    setProjectData({ ...projectData, preferredStartDate: format(date, 'yyyy-MM-dd') });
                                                                    setEditStartDateOpen(false);
                                                                }}
                                                                disabled={(date) => isHoliday(date)}
                                                                month={editStartCalendarMonth}
                                                                onMonthChange={(month) => { setEditStartCalendarMonth(month); onHolidayMonthChange(month); }}
                                                                modifiers={{ holiday: holidayDates }}
                                                                modifiersClassNames={{ holiday: 'holiday-day' }}
                                                                initialFocus
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                ) : (
                                                    <p className="text-gray-900">
                                                        {projectData.preferredStartDate
                                                            ? new Date(projectData.preferredStartDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                                            : 'Not set'}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="space-y-2 field-row">
                                                <Label>Preferred Completion Date *</Label>
                                                {isEditMode && canEdit ? (
                                                    <Popover open={editCompletionDateOpen} onOpenChange={setEditCompletionDateOpen}>
                                                        <PopoverTrigger asChild>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                className="w-full justify-start text-left font-normal h-11"
                                                            >
                                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                                {projectData.preferredCompletionDate
                                                                    ? format(new Date(projectData.preferredCompletionDate), 'PPP')
                                                                    : <span className="text-muted-foreground">Pick a date</span>}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0" align="start">
                                                            <Calendar
                                                                mode="single"
                                                                selected={projectData.preferredCompletionDate ? new Date(projectData.preferredCompletionDate) : undefined}
                                                                onSelect={(date) => {
                                                                    if (!date) return;
                                                                    if (isHoliday(date)) {
                                                                        toast.error(`${getHolidayName(date) || format(date, 'yyyy-MM-dd')} is a holiday`);
                                                                        return;
                                                                    }
                                                                    setProjectData({ ...projectData, preferredCompletionDate: format(date, 'yyyy-MM-dd') });
                                                                    setEditCompletionDateOpen(false);
                                                                }}
                                                                disabled={(date) => {
                                                                    const minDate = projectData.preferredStartDate ? new Date(projectData.preferredStartDate) : null;
                                                                    return (minDate && date < minDate) || isHoliday(date);
                                                                }}
                                                                month={editCompletionCalendarMonth}
                                                                onMonthChange={(month) => { setEditCompletionCalendarMonth(month); onHolidayMonthChange(month); }}
                                                                modifiers={{ holiday: holidayDates }}
                                                                modifiersClassNames={{ holiday: 'holiday-day' }}
                                                                initialFocus
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                ) : (
                                                    <p className="text-gray-900">
                                                        {projectData.preferredCompletionDate
                                                            ? new Date(projectData.preferredCompletionDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                                            : 'Not set'}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="space-y-2 field-row">
                                                <Label>Estimated Duration</Label>
                                                <p className="text-gray-900">{projectData.estimatedDays || 0} days</p>
                                            </div>
                                        </div>

                                        {/* Extra Work Section */}
                                        <div className="border-t pt-4">
                                            <div className="space-y-2 field-row">
                                                <Label className="text-base font-semibold">Extra Work Required</Label>
                                                {isEditMode && canEdit ? (
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            id="hasExtraWork"
                                                            checked={projectData.hasExtraWork || false}
                                                            onChange={(e) => setProjectData({
                                                                ...projectData,
                                                                hasExtraWork: e.target.checked,
                                                                extraWorkNotes: e.target.checked ? projectData.extraWorkNotes : ''
                                                            })}
                                                            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                                        />
                                                        <Label htmlFor="hasExtraWork" className="cursor-pointer font-normal">
                                                            This project requires extra work
                                                        </Label>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        {projectData.hasExtraWork ? (
                                                            <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                                                                Yes
                                                            </Badge>
                                                        ) : (
                                                            <Badge className="bg-gray-100 text-gray-800 border-gray-300">
                                                                No
                                                            </Badge>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {projectData.hasExtraWork && (
                                                <div className="space-y-2 mt-3">
                                                    <Label>Extra Work Notes</Label>
                                                    {isEditMode && canEdit ? (
                                                        <Textarea
                                                            value={projectData.extraWorkNotes || ''}
                                                            onChange={(e) => setProjectData({ ...projectData, extraWorkNotes: e.target.value })}
                                                            placeholder="Describe the extra work requirements..."
                                                            className="min-h-[100px]"
                                                        />
                                                    ) : (
                                                        <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                                {projectData.extraWorkNotes}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Entities */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Project Entities</CardTitle>
                                        <CardDescription>
                                            {isEditMode && canEdit
                                                ? 'Select entities and specify quantities'
                                                : 'Entities included in this project'}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {isEditMode && canEdit ? (
                                            /* Edit Mode - Show all entities with checkboxes */
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {allEntities.map(entity => {
                                                    const selected = projectData.entities.find(e => e.id === entity.id);
                                                    return (
                                                        <div
                                                            key={entity.id}
                                                            className={`border rounded-lg p-4 transition-all ${selected ? 'border-teal-500 bg-teal-50' : 'border-gray-200'
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <input
                                                                    type="checkbox"
                                                                    id={`entity-${entity.id}`}
                                                                    checked={!!selected}
                                                                    onChange={() => toggleEntity(entity.id)}
                                                                    className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                                                                />
                                                                <div className="flex-1">
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="flex-1">
                                                                            <label
                                                                                htmlFor={`entity-${entity.id}`}
                                                                                className="font-medium text-gray-900 cursor-pointer"
                                                                            >
                                                                                {entity.entityName}
                                                                            </label>
                                                                            {entity.description && (
                                                                                <p className="text-xs text-gray-500 mt-1">{entity.description}</p>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex items-center gap-2 ml-4">
                                                                            <Label className="text-xs text-gray-600">Qty:</Label>
                                                                            <div className="flex items-center border border-input rounded-md overflow-hidden">
                                                                                <button
                                                                                    type="button"
                                                                                    disabled={!selected || (selected && selected.quantity <= 1)}
                                                                                    onClick={() => updateEntityQuantity(entity.id, Math.max(1, (selected?.quantity || 1) - 1))}
                                                                                    className="w-9 h-9 flex items-center justify-center text-lg font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                                                                    aria-label="Decrease quantity"
                                                                                >
                                                                                    −
                                                                                </button>
                                                                                <Input
                                                                                    type="number"
                                                                                    inputMode="numeric"
                                                                                    min="1"
                                                                                    value={selected ? selected.quantity : 1}
                                                                                    onChange={(e) => updateEntityQuantity(entity.id, e.target.value)}
                                                                                    disabled={!selected}
                                                                                    className="w-12 h-9 text-sm text-center border-0 rounded-none shadow-none focus-visible:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    disabled={!selected}
                                                                                    onClick={() => updateEntityQuantity(entity.id, (selected?.quantity || 1) + 1)}
                                                                                    className="w-9 h-9 flex items-center justify-center text-lg font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                                                                    aria-label="Increase quantity"
                                                                                >
                                                                                    +
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            /* View Mode - Show only selected entities */
                                            <div className="space-y-3">
                                                {projectData.entities && projectData.entities.length > 0 ? (
                                                    projectData.entities.map((entity) => (
                                                        <div key={entity.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                            <div className="flex items-center justify-between">
                                                                <div>
                                                                    <p className="font-medium text-gray-900">{entity.name}</p>
                                                                </div>
                                                                <div className="text-sm text-gray-600">
                                                                    Quantity: <span className="font-medium text-gray-900">{entity.quantity}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="text-sm text-gray-500 text-center py-4">No entities added yet</p>
                                                )}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Delete Project Confirmation Dialog */}
                                <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { if (!deletingProject) setDeleteDialogOpen(open); }}>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete Project</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Are you sure you want to delete the project <strong>"{projectData.clientName}"</strong>? This action cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel disabled={deletingProject}>Cancel</AlertDialogCancel>
                                            <Button
                                                className="bg-red-600 hover:bg-red-700 text-white"
                                                disabled={deletingProject}
                                                onClick={async () => {
                                                    setDeletingProject(true);
                                                    try {
                                                        await api.deleteProject(id);
                                                        setDeleteDialogOpen(false);
                                                        toast.success('Project deleted successfully');
                                                        navigate('/admin/dashboard');
                                                    } catch (err) {
                                                        setDeletingProject(false);
                                                        toast.error(err?.message || 'Could not delete this project.');
                                                    }
                                                }}
                                            >
                                                {deletingProject ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                                                Delete
                                            </Button>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>

                                {/* Put On Hold Dialog */}
                                <Dialog open={holdDialogOpen} onOpenChange={setHoldDialogOpen}>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Put Project On Hold</DialogTitle>
                                            <DialogDescription>
                                                This will pause the project and unassign all teams. The project can be resumed later by an admin.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-4 py-2">
                                            <div className="space-y-2 field-row">
                                                <Label htmlFor="holdReason">Reason for hold *</Label>
                                                <Textarea
                                                    id="holdReason"
                                                    placeholder="Enter reason for putting the project on hold..."
                                                    value={holdReason}
                                                    onChange={(e) => setHoldReason(e.target.value)}
                                                    rows={3}
                                                />
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setHoldDialogOpen(false)} disabled={holdingProject}>
                                                Cancel
                                            </Button>
                                            <Button
                                                className="bg-orange-600 hover:bg-orange-700"
                                                disabled={holdingProject || !holdReason.trim()}
                                                onClick={async () => {
                                                    setHoldingProject(true);
                                                    try {
                                                        await api.putProjectOnHold(id, holdReason.trim());
                                                        toast.success('Project put on hold');
                                                        setHoldDialogOpen(false);
                                                        fetchRealProjectData();
                                                    } catch (err) {
                                                        toast.error(err?.message || 'Failed to put project on hold');
                                                    } finally {
                                                        setHoldingProject(false);
                                                    }
                                                }}
                                            >
                                                {holdingProject ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PauseCircle className="w-4 h-4 mr-1" />}
                                                Put On Hold
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>

                                {/* Resume Project Dialog */}
                                <AlertDialog open={resumeDialogOpen} onOpenChange={setResumeDialogOpen}>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Resume Project</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This will resume the project back to its previous stage (<strong>Installation In Progress</strong>).
                                                Teams will need to be reassigned if necessary.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel disabled={resumingProject}>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                className="bg-green-600 hover:bg-green-700"
                                                disabled={resumingProject}
                                                onClick={async (e) => {
                                                    e.preventDefault();
                                                    setResumingProject(true);
                                                    try {
                                                        await api.resumeProject(id, {});
                                                        toast.success('Project resumed successfully');
                                                        setResumeDialogOpen(false);
                                                        fetchRealProjectData();
                                                    } catch (err) {
                                                        toast.error(err?.message || 'Failed to resume project');
                                                    } finally {
                                                        setResumingProject(false);
                                                    }
                                                }}
                                            >
                                                {resumingProject ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                                                Resume
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </TabsContent>

                            {/* Quotation Tab */}
                            <TabsContent value="quotation" className="space-y-6 mt-0">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Quotation Details</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {/* Quotations Summary */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                            <div className="bg-blue-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-600 mb-1">Base Work Amount</p>
                                                <p className="text-xl font-bold text-blue-700">
                                                    ₹{quotations
                                                        .filter(q => q.quotationType === QuotationType.BaseWork && q.status?.toLowerCase() !== 'rejected')
                                                        .reduce((sum, q) => sum + q.totalAmount, 0)
                                                        .toLocaleString()}
                                                </p>
                                            </div>
                                            <div className="bg-purple-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-600 mb-1">Extra Work Amount</p>
                                                <p className="text-xl font-bold text-purple-700">
                                                    ₹{quotations
                                                        .filter(q => q.quotationType === QuotationType.AdditionalWork && q.status?.toLowerCase() !== 'rejected')
                                                        .reduce((sum, q) => sum + q.totalAmount, 0)
                                                        .toLocaleString()}
                                                </p>
                                            </div>
                                            <div className="bg-orange-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-600 mb-1">GST</p>
                                                <p className="text-xl font-bold text-orange-700">
                                                    ₹{quotations
                                                        .filter(q => q.status?.toLowerCase() !== 'rejected')
                                                        .reduce((sum, q) => sum + q.taxAmount, 0)
                                                        .toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                            <div className="bg-teal-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                                                <p className="text-xl font-bold text-teal-700">
                                                    ₹{quotations
                                                        .filter(q => q.status?.toLowerCase() !== 'rejected')
                                                        .reduce((sum, q) => sum + q.grandTotal, 0)
                                                        .toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Quotations Table */}
                                        <div>
                                            <div className="flex items-center justify-between mb-3">
                                                <Label className="text-lg font-semibold">Quotations</Label>
                                                {canCreateQuotation && (() => {
                                                    const hasPendingOrApprovedBaseWork = quotations?.some(q => q.quotationType === QuotationType.BaseWork && [QuotationStatus.Draft, QuotationStatus.Sent, QuotationStatus.Approved].includes(q.status?.toLowerCase()));
                                                    const hasApprovedBaseWork = quotations?.some(q => q.quotationType === QuotationType.BaseWork && q.status === QuotationStatus.Approved);
                                                    return (
                                                        <div className="flex gap-2">
                                                            {!hasPendingOrApprovedBaseWork && (
                                                                <Button
                                                                    className="bg-teal-600 hover:bg-teal-700"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setQuotationType(QuotationType.BaseWork);
                                                                        setQuotationModalOpen(true);
                                                                    }}
                                                                >
                                                                    <Plus className="w-4 h-4 sm:mr-2" />
                                                                    <span className="inline">New Quotation</span>
                                                                </Button>
                                                            )}
                                                            {hasApprovedBaseWork && (
                                                                <Button
                                                                    className="bg-orange-600 hover:bg-orange-700 text-white"
                                                                    size="sm"
                                                                    onClick={() => setAdditionalWorkModalOpen(true)}
                                                                >
                                                                    <Plus className="w-4 h-4 sm:mr-2" />
                                                                    <span className="inline">Additional Work</span>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            {loadingQuotations ? (
                                                <div className="border rounded-lg p-8 text-center">
                                                    <p className="text-gray-500">Loading quotations...</p>
                                                </div>
                                            ) : quotations.length === 0 ? (
                                                <div className="border rounded-lg p-8 text-center">
                                                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                                    <p className="text-gray-500">No quotations available</p>
                                                </div>
                                            ) : (
                                                <div className="border rounded-lg overflow-x-auto">
                                                    <table className="w-full min-w-[640px]">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Quotation #</th>
                                                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Type</th>
                                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
                                                                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Amount</th>
                                                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Status</th>
                                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Remarks</th>
                                                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y">
                                                            {quotations.map((quotation) => (
                                                                <tr key={quotation.id} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{quotation.quotationNumber}</td>
                                                                    <td className="px-4 py-3 text-center">
                                                                        <Badge className={quotation.quotationType === QuotationType.BaseWork ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}>
                                                                            {quotation.quotationType === QuotationType.BaseWork ? 'Base Work' : 'Additional Work'}
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm text-gray-900">{new Date(quotation.createdAt).toLocaleDateString()}</td>
                                                                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                                                                        ₹{quotation.grandTotal.toLocaleString()}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center">
                                                                        <Badge className={getStatusColor(quotation.status)}>
                                                                            {formatQuotationStatus(quotation.status)}
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600">{quotation.notes || quotation.description || '-'}</td>
                                                                    <td className="px-4 py-3 text-center">
                                                                        <div className="flex justify-center gap-2">
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                title="View Details"
                                                                                onClick={() => {
                                                                                    setSelectedQuotation(quotation);
                                                                                    setQuotationDetailsOpen(true);
                                                                                }}
                                                                            >
                                                                                <Eye className="w-4 h-4" />
                                                                            </Button>
                                                                            {/* OEM Approve Button for sent quotations */}
                                                                            {isOEM && quotation.status?.toLowerCase() === QuotationStatus.Sent && (
                                                                                <Button
                                                                                    size="sm"
                                                                                    className="bg-green-600 hover:bg-green-700 text-white"
                                                                                    onClick={() => {
                                                                                        setQuotationForApproval(quotation);
                                                                                        setQuotationApprovalOpen(true);
                                                                                    }}
                                                                                >
                                                                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                                                                    Approve
                                                                                </Button>
                                                                            )}
                                                                            {((quotation.quotationType === QuotationType.AdditionalWork ? canManageAdditionalWork : canEdit)
                                                                                && ![QuotationStatus.Approved, QuotationStatus.Rejected].includes(quotation.status?.toLowerCase())) && (
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        title="Edit Quotation"
                                                                                        onClick={() => {
                                                                                            if (quotation.quotationType === QuotationType.AdditionalWork) {
                                                                                                setEditingQuotation(quotation);
                                                                                                setAdditionalWorkModalOpen(true);
                                                                                            } else {
                                                                                                setEditingQuotation(quotation);
                                                                                                setQuotationType(quotation.quotationType || QuotationType.BaseWork);
                                                                                                setQuotationModalOpen(true);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <Edit className="w-4 h-4" />
                                                                                    </Button>
                                                                                )}
                                                                            {canEdit && (
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    title="Upload Document"
                                                                                    onClick={() => {
                                                                                        setQuotationForUpload(quotation);
                                                                                        setQuotationDocumentUploadOpen(true);
                                                                                    }}
                                                                                >
                                                                                    <Upload className="w-4 h-4" />
                                                                                </Button>
                                                                            )}
                                                                            {quotation.documentUrl && (
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    title="Download Document"
                                                                                    onClick={() => window.open(quotation.documentUrl, '_blank')}
                                                                                >
                                                                                    <Download className="w-4 h-4" />
                                                                                </Button>
                                                                            )}
                                                                            {canManageAdditionalWork
                                                                                && quotation.quotationType === QuotationType.AdditionalWork
                                                                                && quotation.status?.toLowerCase() !== QuotationStatus.Approved && (
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        title="Delete Quotation"
                                                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                                        onClick={() => {
                                                                                            setQuotationToDelete(quotation);
                                                                                            setQuotationDeleteConfirmOpen(true);
                                                                                        }}
                                                                                    >
                                                                                        <Trash2 className="w-4 h-4" />
                                                                                    </Button>
                                                                                )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Documents Tab */}
                            <TabsContent value="documents" className="space-y-6 mt-0">
                                {/* Hardware Section */}
                                <Card className="border-orange-200 bg-orange-50/30">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <Wrench className="w-5 h-5 text-orange-600" />
                                            Hardware List
                                        </CardTitle>
                                        <CardDescription>Download sample hardware lists and upload your completed list</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        {/* Sample Downloads */}
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Sample Templates</p>
                                            <div className="flex flex-wrap gap-3">
                                                <a
                                                    href="/samples/KITCHEN_HARDWARE_LIST.pdf"
                                                    download="KITCHEN_HARDWARE_LIST.pdf"
                                                    className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-orange-300 rounded-lg bg-white hover:bg-orange-50 text-orange-700 transition-colors"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    Kitchen Hardware List
                                                </a>
                                                <a
                                                    href="/samples/WARDROBES_HARDWARE_LIST.pdf"
                                                    download="WARDROBES_HARDWARE_LIST.pdf"
                                                    className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-orange-300 rounded-lg bg-white hover:bg-orange-50 text-orange-700 transition-colors"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    Wardrobes Hardware List
                                                </a>
                                            </div>
                                        </div>

                                        <Separator />

                                        {/* Upload Area */}
                                        <div>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    ref={hardwareFileInputRef}
                                                    type="file"
                                                    accept=".pdf,.xlsx,.xls,.doc,.docx,.csv"
                                                    className="hidden"
                                                    onChange={handleHardwareUpload}
                                                />
                                                <Button
                                                    variant="outline"
                                                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                                    onClick={() => hardwareFileInputRef.current?.click()}
                                                    disabled={hardwareUploading}
                                                >
                                                    {hardwareUploading ? (
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    ) : (
                                                        <Upload className="w-4 h-4 mr-2" />
                                                    )}
                                                    {hardwareUploading ? 'Uploading...' : 'Upload Hardware List'}
                                                </Button>
                                                <span className="text-xs text-gray-500">PDF, Excel, Word, or CSV</span>
                                            </div>
                                        </div>

                                        {/* Show uploaded hardware documents */}
                                        {(() => {
                                            const hwDocs = documents.filter(doc => doc.subfolder === 'hardware' || doc.documentType === 'Hardware List');
                                            if (hwDocs.length === 0) return null;
                                            return (
                                                <div>
                                                    <Separator />
                                                    <p className="text-sm font-medium text-gray-700 mb-2 mt-4">Uploaded Hardware Documents</p>
                                                    <div className="space-y-2 field-row">
                                                        {hwDocs.map((doc) => (
                                                            <div key={doc.id} className="flex items-center justify-between p-2 border rounded-lg bg-white">
                                                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                    <FileText className="w-4 h-4 text-orange-500 shrink-0" />
                                                                    <span className="text-sm text-gray-900 truncate">{doc.fileName}</span>
                                                                    <span className="text-xs text-gray-500">{formatFileSize(doc.fileSizeMB)}</span>
                                                                </div>
                                                                <div className="flex gap-1 shrink-0">
                                                                    <Button variant="ghost" size="sm" onClick={() => handleViewDocument(doc.id)}>
                                                                        <Eye className="w-4 h-4" />
                                                                    </Button>
                                                                    <Button variant="ghost" size="sm" onClick={() => handleDownloadDocument(doc.id, doc.fileName)} disabled={downloadingDocId === doc.id}>
                                                                        {downloadingDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                                                    </Button>
                                                                    {isAdmin && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => confirmDeleteDocument(doc.id, doc.fileName)}
                                                                            disabled={deletingDocId === doc.id}
                                                                        >
                                                                            {deletingDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-red-500" />}
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </CardContent>
                                </Card>

                                {/* Upload Button */}
                                <div className="flex justify-end">
                                    {!isReadOnlyViewer && (isEditMode || canEdit) && (
                                        <Button
                                            className="bg-teal-600 hover:bg-teal-700"
                                            onClick={() => setUploadDialogOpen(true)}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Upload Document
                                        </Button>
                                    )}
                                </div>

                                {loadingDocuments ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500">Loading documents...</p>
                                    </div>
                                ) : documents.length === 0 ? (
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="text-center py-8">
                                                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                                <p className="text-gray-500">No documents uploaded yet</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <>
                                        {/* Images Section */}
                                        {(() => {
                                            const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
                                            const images = documents.filter(doc => imageTypes.includes(doc.mimeType?.toLowerCase()) && doc.subfolder !== 'hardware');

                                            if (images.length === 0) return null;

                                            return (
                                                <Card>
                                                    <CardHeader>
                                                        <CardTitle className="text-lg">Images ({images.length})</CardTitle>
                                                    </CardHeader>
                                                    <CardContent>
                                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                                            {images.map((doc) => {
                                                                const thumbnailUrl = imageUrls[doc.id];

                                                                return (
                                                                    <div key={doc.id} className="group relative border rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                                                                        onClick={() => handleViewDocument(doc.id)}>
                                                                        {/* Image Thumbnail */}
                                                                        <div className="aspect-square bg-gray-100 relative">
                                                                            {thumbnailUrl ? (
                                                                                <img
                                                                                    src={thumbnailUrl}
                                                                                    alt={doc.fileName}
                                                                                    className="w-full h-full object-cover"
                                                                                    onError={(e) => {
                                                                                        e.target.style.display = 'none';
                                                                                        const placeholder = e.target.nextElementSibling;
                                                                                        if (placeholder) placeholder.style.display = 'flex';
                                                                                    }}
                                                                                />
                                                                            ) : null}
                                                                            {/* Placeholder */}
                                                                            <div
                                                                                className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center"
                                                                                style={{ display: thumbnailUrl ? 'none' : 'flex' }}
                                                                            >
                                                                                {loadingDocuments ? (
                                                                                    <Loader2 className="w-12 h-12 text-blue-300 animate-spin" />
                                                                                ) : (
                                                                                    <ImageIcon className="w-16 h-16 text-blue-300" />
                                                                                )}
                                                                            </div>
                                                                            {/* Overlay on Hover */}
                                                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2">
                                                                                <div className="opacity-0 group-hover:opacity-100 flex gap-2 transition-opacity">
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        className="bg-white hover:bg-gray-100"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleViewDocument(doc.id);
                                                                                        }}
                                                                                        title="View full size"
                                                                                    >
                                                                                        <Eye className="w-4 h-4" />
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        className="bg-white hover:bg-gray-100"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleDownloadDocument(doc.id, doc.fileName);
                                                                                        }}
                                                                                        title="Download"
                                                                                        disabled={downloadingDocId === doc.id}
                                                                                    >
                                                                                        {downloadingDocId === doc.id ? (
                                                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                                                        ) : (
                                                                                            <Download className="w-4 h-4" />
                                                                                        )}
                                                                                    </Button>
                                                                                    {canDeleteDocuments && (
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="bg-white hover:bg-red-50"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                confirmDeleteDocument(doc.id, doc.fileName);
                                                                                            }}
                                                                                            title="Delete"
                                                                                            disabled={deletingDocId === doc.id}
                                                                                        >
                                                                                            {deletingDocId === doc.id ? (
                                                                                                <Loader2 className="w-4 h-4 text-red-500 animate-spin" />
                                                                                            ) : (
                                                                                                <Trash2 className="w-4 h-4 text-red-500" />
                                                                                            )}
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        {/* Image Info */}
                                                                        <div className="p-2 bg-white">
                                                                            <p className="text-sm font-medium text-gray-900 truncate" title={doc.fileName}>
                                                                                {doc.fileName}
                                                                            </p>
                                                                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                                                                {doc.subfolder && (
                                                                                    <span className="text-blue-600">{formatSubfolder(doc.subfolder)}</span>
                                                                                )}
                                                                                {doc.subfolder && <span>•</span>}
                                                                                <span>{formatFileSize(doc.fileSizeMB)}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })()}

                                        {/* Documents Section (PDFs, Excel, Word, etc.) */}
                                        {(() => {
                                            const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
                                            const docs = documents.filter(doc => !imageTypes.includes(doc.mimeType?.toLowerCase()) && doc.subfolder !== 'hardware');

                                            if (docs.length === 0) return null;

                                            return (
                                                <Card>
                                                    <CardHeader>
                                                        <CardTitle className="text-lg">Documents ({docs.length})</CardTitle>
                                                    </CardHeader>
                                                    <CardContent>
                                                        <div className="space-y-2 field-row">
                                                            {docs.map((doc) => (
                                                                <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                        <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="font-medium text-gray-900 truncate">{doc.fileName}</p>
                                                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                                                <span>{doc.documentType}</span>
                                                                                {doc.subfolder && (
                                                                                    <>
                                                                                        <span>•</span>
                                                                                        <span className="text-blue-600">{formatSubfolder(doc.subfolder)}</span>
                                                                                    </>
                                                                                )}
                                                                                <span>•</span>
                                                                                <span>{formatFileSize(doc.fileSizeMB)}</span>
                                                                            </div>
                                                                            <p className="text-xs text-gray-500">
                                                                                Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex gap-2 shrink-0">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleViewDocument(doc.id)}
                                                                            title="View document"
                                                                        >
                                                                            <Eye className="w-4 h-4" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleDownloadDocument(doc.id, doc.fileName)}
                                                                            title="Download document"
                                                                            disabled={downloadingDocId === doc.id}
                                                                        >
                                                                            {downloadingDocId === doc.id ? (
                                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                                            ) : (
                                                                                <Download className="w-4 h-4" />
                                                                            )}
                                                                        </Button>
                                                                        {canDeleteDocuments && (
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                onClick={() => confirmDeleteDocument(doc.id, doc.fileName)}
                                                                                title="Delete document"
                                                                                disabled={deletingDocId === doc.id}
                                                                            >
                                                                                {deletingDocId === doc.id ? (
                                                                                    <Loader2 className="w-4 h-4 text-red-500 animate-spin" />
                                                                                ) : (
                                                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                                                )}
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })()}
                                    </>
                                )}
                            </TabsContent>

                            {/* Bills Tab (Admin + OEM) */}
                            {(isAdmin || isOEM) && (
                                <TabsContent value="bills" className="space-y-6 mt-0">
                                    <Card>
                                        <CardHeader>
                                            <div className="flex items-center justify-between">
                                                <CardTitle>Bills</CardTitle>
                                                {isAdmin && (
                                                    <Button
                                                        className="bg-teal-600 hover:bg-teal-700"
                                                        onClick={() => {
                                                            setBillForm({ name: '', notes: '', file: null });
                                                            if (billFileRef.current) billFileRef.current.value = '';
                                                            setBillDialogOpen(true);
                                                        }}
                                                    >
                                                        <Plus className="w-4 h-4 mr-2" />
                                                        Upload Bill
                                                    </Button>
                                                )}
                                            </div>
                                            <CardDescription>
                                                {isAdmin ? 'Upload and manage project bills (PDF or images)' : 'View project bills uploaded by admin'}
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            {loadingBills ? (
                                                <div className="flex items-center justify-center py-12">
                                                    <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                                                </div>
                                            ) : bills.length === 0 ? (
                                                <div className="text-center py-12 text-gray-500">
                                                    <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                                    <p className="text-lg font-medium">No bills uploaded yet</p>
                                                    {isAdmin && <p className="text-sm mt-1">Click "Upload Bill" to add a PDF or image file</p>}
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    {bills.map((bill) => (
                                                        <div key={bill.id} className="border rounded-lg p-4 flex items-start gap-4 hover:bg-gray-50">
                                                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                                                                {bill.mimeType === 'application/pdf' ? (
                                                                    <FileText className="w-5 h-5 text-teal-600" />
                                                                ) : (
                                                                    <ImageIcon className="w-5 h-5 text-teal-600" />
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="font-medium text-gray-900 truncate">{bill.billName || bill.fileName}</p>
                                                                    <Badge variant="outline" className="text-xs shrink-0">
                                                                        {bill.mimeType === 'application/pdf' ? 'PDF' : 'Image'}
                                                                    </Badge>
                                                                </div>
                                                                <p className="text-xs text-gray-400 truncate">{bill.fileName}</p>
                                                                {editingBillId === bill.id ? (
                                                                    <div className="flex items-center gap-2 mt-2">
                                                                        <Input
                                                                            value={editBillNotes}
                                                                            onChange={(e) => setEditBillNotes(e.target.value)}
                                                                            placeholder="Edit notes..."
                                                                            className="flex-1 h-8 text-sm"
                                                                        />
                                                                        <Button size="sm" variant="ghost" onClick={() => handleUpdateBillNotes(bill.id)}>
                                                                            <Save className="w-3 h-3" />
                                                                        </Button>
                                                                        <Button size="sm" variant="ghost" onClick={() => setEditingBillId(null)}>
                                                                            <X className="w-3 h-3" />
                                                                        </Button>
                                                                    </div>
                                                                ) : (
                                                                    bill.notes && <p className="text-sm text-gray-600 mt-1">{bill.notes}</p>
                                                                )}
                                                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                                                    <span>{new Date(bill.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                                                    <span>{bill.fileSizeMB > 0.01 ? `${bill.fileSizeMB.toFixed(2)} MB` : '<0.01 MB'}</span>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1 shrink-0">
                                                                <Button variant="ghost" size="sm" onClick={() => handleViewBill(bill)}>
                                                                    <Eye className="w-4 h-4" />
                                                                </Button>
                                                                <Button variant="ghost" size="sm" onClick={() => handleDownloadBill(bill)}>
                                                                    <Download className="w-4 h-4" />
                                                                </Button>
                                                                {isAdmin && (
                                                                    <>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                setEditingBillId(bill.id);
                                                                                setEditBillNotes(bill.notes || '');
                                                                            }}
                                                                        >
                                                                            <Edit className="w-4 h-4" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="text-red-600 hover:text-red-700"
                                                                            onClick={() => handleDeleteBill(bill.id)}
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Upload Bill Dialog */}
                                    <Dialog open={billDialogOpen} onOpenChange={setBillDialogOpen}>
                                        <DialogContent className="sm:max-w-md">
                                            <DialogHeader>
                                                <DialogTitle>Upload Bill</DialogTitle>
                                                <DialogDescription>Add a bill with name, notes, and file attachment</DialogDescription>
                                            </DialogHeader>
                                            <div className="space-y-4 py-2">
                                                <div className="space-y-2 field-row">
                                                    <Label htmlFor="bill-name">Bill Name *</Label>
                                                    <Input
                                                        id="bill-name"
                                                        placeholder="e.g. Material Purchase Invoice"
                                                        value={billForm.name}
                                                        onChange={(e) => setBillForm({ ...billForm, name: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-2 field-row">
                                                    <Label htmlFor="bill-notes">Notes</Label>
                                                    <Textarea
                                                        id="bill-notes"
                                                        placeholder="Optional notes about this bill..."
                                                        value={billForm.notes}
                                                        onChange={(e) => setBillForm({ ...billForm, notes: e.target.value })}
                                                        rows={3}
                                                    />
                                                </div>
                                                <div className="space-y-2 field-row">
                                                    <Label htmlFor="bill-file">Document *</Label>
                                                    <Input
                                                        id="bill-file"
                                                        ref={billFileRef}
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp"
                                                        onChange={(e) => setBillForm({ ...billForm, file: e.target.files?.[0] || null })}
                                                    />
                                                    <p className="text-xs text-gray-500">Accepted: PDF, JPG, PNG, GIF, WebP, BMP (max 10 MB)</p>
                                                </div>
                                            </div>
                                            <DialogFooter>
                                                <Button variant="outline" onClick={() => setBillDialogOpen(false)}>Cancel</Button>
                                                <Button
                                                    className="bg-teal-600 hover:bg-teal-700"
                                                    onClick={handleUploadBill}
                                                    disabled={uploadingBill || !billForm.name.trim() || !billForm.file}
                                                >
                                                    {uploadingBill ? (
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    ) : (
                                                        <Upload className="w-4 h-4 mr-2" />
                                                    )}
                                                    Upload
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                </TabsContent>
                            )}

                            {/* Site Visits Tab */}
                            {(isAdmin || ((isOEM || isSupervisor) && showSiteVisitsTab) || ((isAdmin || isOEM) && (projectData.currentStage === StageName.QuotationApprovalPending || projectData.currentStage === StageName.QuotationRejected))) && (
                                <TabsContent value="sitevisits" className="space-y-6 mt-0">
                                    {loadingSiteVisits ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                                        </div>
                                    ) : (
                                        <>
                                            {/* SV1 Card */}
                                            <Card className="border-green-200">
                                                <CardHeader className="bg-green-50">
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                        <CardTitle>Site Visit 1 (SV1)</CardTitle>
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <Badge className={
                                                                siteVisitsData?.sv1?.status === BookingStatus.Confirmed ? 'bg-green-100 text-green-800' :
                                                                    siteVisitsData?.sv1?.status === BookingStatus.Completed ? 'bg-blue-100 text-blue-800' :
                                                                        siteVisitsData?.sv1?.status === BookingStatus.InProgress ? 'bg-amber-100 text-amber-800' :
                                                                            siteVisitsData?.sv1?.status === BookingStatus.Proposed ? 'bg-yellow-100 text-yellow-800' :
                                                                                siteVisitsData?.sv1?.status === BookingStatus.Cancelled ? 'bg-red-100 text-red-800' :
                                                                                    'bg-gray-100 text-gray-800'
                                                            }>
                                                                {siteVisitsData?.sv1?.status === BookingStatus.InProgress ? 'In Progress' : siteVisitsData?.sv1?.status ? siteVisitsData.sv1.status.charAt(0).toUpperCase() + siteVisitsData.sv1.status.slice(1) : 'Pending'}
                                                            </Badge>
                                                            {isAdmin && siteVisitsData?.sv1?.status === BookingStatus.Confirmed && projectData.currentStage === StageName.QuotationApproved && (
                                                                <AlertDialog>
                                                                    <AlertDialogTrigger asChild>
                                                                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                                                                            <CalendarIcon className="w-4 h-4 mr-1" />
                                                                            Schedule SV1
                                                                        </Button>
                                                                    </AlertDialogTrigger>
                                                                    <AlertDialogContent>
                                                                        <AlertDialogHeader>
                                                                            <AlertDialogTitle>Confirm Schedule SV1</AlertDialogTitle>
                                                                            <AlertDialogDescription>
                                                                                This will move the project from "Quotation Approved" to "SV1 Scheduled".
                                                                                <br /><br />
                                                                                <strong>Date:</strong> {siteVisitsData?.sv1?.scheduledDate ? new Date(siteVisitsData.sv1.scheduledDate).toLocaleDateString() : 'N/A'}<br />
                                                                                <strong>Slot:</strong> {siteVisitsData?.sv1?.slotName || 'N/A'}<br />
                                                                                <strong>SuperAligner:</strong> {siteVisitsData?.sv1?.supervisorName || 'N/A'}
                                                                            </AlertDialogDescription>
                                                                        </AlertDialogHeader>
                                                                        <AlertDialogFooter>
                                                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                            <AlertDialogAction
                                                                                className="bg-amber-600 hover:bg-amber-700"
                                                                                onClick={async () => {
                                                                                    try {
                                                                                        await api.assignSupervisor(
                                                                                            id,
                                                                                            siteVisitsData.sv1.supervisorId,
                                                                                            siteVisitsData.sv1.scheduledDate
                                                                                        );
                                                                                        toast.success('Project moved to SV1 Scheduled');
                                                                                        fetchRealProjectData();
                                                                                        fetchSiteVisits();
                                                                                    } catch (error) {
                                                                                        console.error('Error scheduling SV1:', error);
                                                                                        toast.error('Failed to schedule SV1');
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Confirm
                                                                            </AlertDialogAction>
                                                                        </AlertDialogFooter>
                                                                    </AlertDialogContent>
                                                                </AlertDialog>
                                                            )}
                                                            {isAdmin && siteVisitsData?.sv1?.bookingId > 0 && siteVisitsData?.sv1?.status !== BookingStatus.Completed && siteVisitsData?.sv1?.status !== BookingStatus.Cancelled && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={openEditSv1}
                                                                >
                                                                    <Edit className="w-4 h-4 mr-1" />
                                                                    Edit SV1
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="pt-6 space-y-4">
                                                    {siteVisitsData?.sv1 ? (
                                                        <>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div>
                                                                    <Label className="text-gray-600">Scheduled Date</Label>
                                                                    <p className="text-gray-900 font-medium">
                                                                        {new Date(siteVisitsData.sv1.scheduledDate).toLocaleDateString()}
                                                                    </p>
                                                                </div>
                                                                {siteVisitsData.sv1.slotName && (
                                                                    <div>
                                                                        <Label className="text-gray-600">Time Slot</Label>
                                                                        <p className="text-gray-900 font-medium">{siteVisitsData.sv1.slotName}</p>
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <Label className="text-gray-600">SuperAligner</Label>
                                                                    <p className="text-gray-900 font-medium">{siteVisitsData.sv1.supervisorName || 'Not assigned'}</p>
                                                                    {siteVisitsData.sv1.supervisorPhone && (
                                                                        <p className="text-sm text-gray-600">{siteVisitsData.sv1.supervisorPhone}</p>
                                                                    )}
                                                                </div>
                                                                {siteVisitsData.sv1.completedAt && (
                                                                    <div>
                                                                        <Label className="text-gray-600">Completed Date</Label>
                                                                        <p className="text-gray-900 font-medium">
                                                                            {new Date(siteVisitsData.sv1.completedAt).toLocaleDateString()}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {siteVisitsData.sv1.supervisorNotes && (
                                                                <div>
                                                                    <Label className="text-gray-600">Remarks</Label>
                                                                    <p className="text-gray-900 mt-1">{siteVisitsData.sv1.supervisorNotes}</p>
                                                                </div>
                                                            )}
                                                            {/* SV1 Checklist */}
                                                            {siteVisitsData.sv1.checklistResponses?.length > 0 && (
                                                                <div className="border-t pt-4">
                                                                    <button
                                                                        className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 w-full"
                                                                        onClick={() => setSv1ChecklistOpen(!sv1ChecklistOpen)}
                                                                    >
                                                                        {sv1ChecklistOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                                        Checklist ({siteVisitsData.sv1.checklistResponses.filter(r => r.isCompleted).length}/{siteVisitsData.sv1.checklistResponses.length} completed)
                                                                    </button>
                                                                    {sv1ChecklistOpen && (
                                                                        <div className="mt-3 space-y-2">
                                                                            {(() => {
                                                                                const groups = {};
                                                                                siteVisitsData.sv1.checklistResponses.forEach(r => {
                                                                                    const g = r.groupName || 'General';
                                                                                    if (!groups[g]) groups[g] = { order: r.groupDisplayOrder || 0, items: [] };
                                                                                    groups[g].items.push(r);
                                                                                });
                                                                                return Object.entries(groups).sort((a, b) => a[1].order - b[1].order).map(([gName, gData]) => (
                                                                                    <div key={gName}>
                                                                                        <p className="text-xs font-semibold text-gray-500 bg-gray-50 px-3 py-1 rounded mb-1">
                                                                                            {gName} ({gData.items.filter(i => i.isCompleted).length}/{gData.items.length})
                                                                                        </p>
                                                                                        {gData.items.map(item => (
                                                                                            <div key={item.id} className="py-1.5 px-3">
                                                                                                <div className="flex items-start gap-2">
                                                                                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.isCompleted ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'}`}>
                                                                                                        {item.isCompleted ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                                                                                                    </div>
                                                                                                    <div className="flex-1 min-w-0">
                                                                                                        <p className="text-sm text-gray-800">
                                                                                                            {item.checklistItemName}
                                                                                                            {item.isRequired && <span className="text-red-500 ml-1">*</span>}
                                                                                                            {item.isPhotoRequired && <span className="text-blue-500 ml-1 text-xs">📷</span>}
                                                                                                            {item.photoCount > 0 && (
                                                                                                                <button
                                                                                                                    className="text-xs text-blue-500 hover:text-blue-700 hover:underline ml-2 inline-flex items-center gap-0.5"
                                                                                                                    onClick={() => toggleSvItemPhotos(item.id)}
                                                                                                                >
                                                                                                                    {svLoadingPhotos === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                                                                                                                    {svPhotosExpanded[item.id] ? 'Hide' : 'View'} {item.photoCount} photo{item.photoCount > 1 ? 's' : ''}
                                                                                                                    {svPhotosExpanded[item.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                                                                                                </button>
                                                                                                            )}
                                                                                                        </p>
                                                                                                        {(item.notes || item.remarks) && (
                                                                                                            <div className="flex gap-3 mt-0.5">
                                                                                                                {item.notes && <p className="text-xs text-gray-500"><span className="font-medium">Notes:</span> {item.notes}</p>}
                                                                                                                {item.remarks && <p className="text-xs text-gray-500"><span className="font-medium">Remarks:</span> {item.remarks}</p>}
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>
                                                                                                {svPhotosExpanded[item.id] && svItemPhotos[item.id]?.length > 0 && (
                                                                                                    <div className="ml-6 mt-2 flex gap-2 flex-wrap">
                                                                                                        {svItemPhotos[item.id].map((photo, idx) => (
                                                                                                            <img
                                                                                                                key={photo.id || idx}
                                                                                                                src={photo.url}
                                                                                                                alt={photo.fileName || 'Photo'}
                                                                                                                className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                                                                                                onClick={() => setSvEnlargedPhoto({ url: photo.url, fileName: photo.fileName })}
                                                                                                            />
                                                                                                        ))}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ));
                                                                            })()}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {/* OEM Approve Quotation button in SV1 section */}
                                                            {isOEM && projectData.currentStage === StageName.QuotationApprovalPending && (
                                                                <div className="pt-4 border-t">
                                                                    <Button
                                                                        size="sm"
                                                                        className="bg-green-600 hover:bg-green-700 text-white"
                                                                        onClick={async () => {
                                                                            try {
                                                                                const response = await api.get(`/api/projects/${id}/quotations`);
                                                                                const pendingQuotation = response.quotations?.find(q => q.status?.toLowerCase() === QuotationStatus.Sent);
                                                                                if (pendingQuotation) {
                                                                                    setQuotationForApproval(pendingQuotation);
                                                                                    setQuotationApprovalOpen(true);
                                                                                } else {
                                                                                    toast.error('No pending quotation found');
                                                                                }
                                                                            } catch (error) {
                                                                                console.error('Error fetching quotation:', error);
                                                                                toast.error('Failed to load quotation');
                                                                            }
                                                                        }}
                                                                    >
                                                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                                                        Approve Quotation
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="text-center py-4">
                                                            <p className="text-gray-500">SV1 not yet scheduled.</p>
                                                            {isOEM && projectData.currentStage === StageName.QuotationApprovalPending && (
                                                                <Button
                                                                    size="sm"
                                                                    className="bg-green-600 hover:bg-green-700 text-white mt-3"
                                                                    onClick={async () => {
                                                                        try {
                                                                            const response = await api.get(`/api/projects/${id}/quotations`);
                                                                            const pendingQuotation = response.quotations?.find(q => q.status?.toLowerCase() === QuotationStatus.Sent);
                                                                            if (pendingQuotation) {
                                                                                setQuotationForApproval(pendingQuotation);
                                                                                setQuotationApprovalOpen(true);
                                                                            } else {
                                                                                toast.error('No pending quotation found');
                                                                            }
                                                                        } catch (error) {
                                                                            console.error('Error fetching quotation:', error);
                                                                            toast.error('Failed to load quotation');
                                                                        }
                                                                    }}
                                                                >
                                                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                                                    Approve Quotation
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            {/* SV2 Card — only show if SV2 exists */}
                                            {siteVisitsData?.sv2 ? (
                                                <Card className="border-blue-200">
                                                    <CardHeader className="bg-blue-50">
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                            <CardTitle>Site Visit 2 (SV2)</CardTitle>
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <Badge className={
                                                                    siteVisitsData.sv2.status === BookingStatus.Confirmed ? 'bg-green-100 text-green-800' :
                                                                        siteVisitsData.sv2.status === BookingStatus.Completed ? 'bg-blue-100 text-blue-800' :
                                                                            siteVisitsData.sv2.status === BookingStatus.Proposed ? 'bg-amber-100 text-amber-800' :
                                                                                siteVisitsData.sv2.status === BookingStatus.InProgress ? 'bg-amber-100 text-amber-800' :
                                                                                    siteVisitsData.sv2.status === BookingStatus.Cancelled ? 'bg-red-100 text-red-800' :
                                                                                        'bg-gray-100 text-gray-800'
                                                                }>
                                                                    {siteVisitsData.sv2.status === BookingStatus.InProgress ? 'In Progress' :
                                                                        siteVisitsData.sv2.status === BookingStatus.Proposed ? 'Proposed (Awaiting OEM)' :
                                                                            siteVisitsData.sv2.status.charAt(0).toUpperCase() + siteVisitsData.sv2.status.slice(1)}
                                                                </Badge>
                                                                {isAdmin && siteVisitsData?.sv2?.status === BookingStatus.Proposed && projectData.currentStage === StageName.Sv2Proposed && (
                                                                    <AlertDialog>
                                                                        <AlertDialogTrigger asChild>
                                                                            <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                                                                                <CalendarIcon className="w-4 h-4 mr-1" />
                                                                                Schedule SV2
                                                                            </Button>
                                                                        </AlertDialogTrigger>
                                                                        <AlertDialogContent>
                                                                            <AlertDialogHeader>
                                                                                <AlertDialogTitle>Confirm Schedule SV2</AlertDialogTitle>
                                                                                <AlertDialogDescription>
                                                                                    This will move the project from "SV2 Proposed" to "SV2 Scheduled".
                                                                                    <br /><br />
                                                                                    <strong>Date:</strong> {siteVisitsData?.sv2?.scheduledDate ? new Date(siteVisitsData.sv2.scheduledDate).toLocaleDateString() : 'N/A'}<br />
                                                                                    <strong>Slot:</strong> {siteVisitsData?.sv2?.slotName || 'N/A'}<br />
                                                                                    <strong>SuperAligner:</strong> {siteVisitsData?.sv2?.supervisorName || 'N/A'}
                                                                                </AlertDialogDescription>
                                                                            </AlertDialogHeader>
                                                                            <AlertDialogFooter>
                                                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                                <AlertDialogAction
                                                                                    className="bg-amber-600 hover:bg-amber-700"
                                                                                    onClick={async () => {
                                                                                        try {
                                                                                            await api.approveSv2(id, { reschedule: false });
                                                                                            toast.success('Project moved to SV2 Scheduled');
                                                                                            fetchRealProjectData();
                                                                                            fetchSiteVisits();
                                                                                        } catch (error) {
                                                                                            console.error('Error scheduling SV2:', error);
                                                                                            toast.error('Failed to schedule SV2');
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    Confirm
                                                                                </AlertDialogAction>
                                                                            </AlertDialogFooter>
                                                                        </AlertDialogContent>
                                                                    </AlertDialog>
                                                                )}
                                                                {isAdmin && siteVisitsData?.sv2?.bookingId > 0 && siteVisitsData?.sv2?.status !== BookingStatus.Completed && (
                                                                    <Button size="sm" variant="outline" onClick={openEditSv2}>
                                                                        <Edit className="h-3.5 w-3.5 mr-1" /> Edit SV2
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CardHeader>
                                                    <CardContent className="pt-6 space-y-4">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <div>
                                                                <Label className="text-gray-600">Scheduled Date</Label>
                                                                <p className="text-gray-900 font-medium">
                                                                    {new Date(siteVisitsData.sv2.scheduledDate).toLocaleDateString()}
                                                                </p>
                                                            </div>
                                                            {siteVisitsData.sv2.slotName && (
                                                                <div>
                                                                    <Label className="text-gray-600">Time Slot</Label>
                                                                    <p className="text-gray-900 font-medium">{siteVisitsData.sv2.slotName}</p>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <Label className="text-gray-600">SuperAligner</Label>
                                                                <p className="text-gray-900 font-medium">{siteVisitsData.sv2.supervisorName || 'Not assigned'}</p>
                                                                {siteVisitsData.sv2.supervisorPhone && (
                                                                    <p className="text-sm text-gray-600">{siteVisitsData.sv2.supervisorPhone}</p>
                                                                )}
                                                            </div>
                                                            {siteVisitsData.sv2.completedAt && (
                                                                <div>
                                                                    <Label className="text-gray-600">Completed Date</Label>
                                                                    <p className="text-gray-900 font-medium">
                                                                        {new Date(siteVisitsData.sv2.completedAt).toLocaleDateString()}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {siteVisitsData.sv2.supervisorNotes && (
                                                            <div>
                                                                <Label className="text-gray-600">Remarks</Label>
                                                                <p className="text-gray-900 mt-1">{siteVisitsData.sv2.supervisorNotes}</p>
                                                            </div>
                                                        )}
                                                        {/* SV2 Checklist */}
                                                        {siteVisitsData.sv2.checklistResponses?.length > 0 && (
                                                            <div className="border-t pt-4">
                                                                <button
                                                                    className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 w-full"
                                                                    onClick={() => setSv2ChecklistOpen(!sv2ChecklistOpen)}
                                                                >
                                                                    {sv2ChecklistOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                                    Checklist ({siteVisitsData.sv2.checklistResponses.filter(r => r.isCompleted).length}/{siteVisitsData.sv2.checklistResponses.length} completed)
                                                                </button>
                                                                {sv2ChecklistOpen && (
                                                                    <div className="mt-3 space-y-2">
                                                                        {(() => {
                                                                            const groups = {};
                                                                            siteVisitsData.sv2.checklistResponses.forEach(r => {
                                                                                const g = r.groupName || 'General';
                                                                                if (!groups[g]) groups[g] = { order: r.groupDisplayOrder || 0, items: [] };
                                                                                groups[g].items.push(r);
                                                                            });
                                                                            return Object.entries(groups).sort((a, b) => a[1].order - b[1].order).map(([gName, gData]) => (
                                                                                <div key={gName}>
                                                                                    <p className="text-xs font-semibold text-gray-500 bg-gray-50 px-3 py-1 rounded mb-1">
                                                                                        {gName} ({gData.items.filter(i => i.isCompleted).length}/{gData.items.length})
                                                                                    </p>
                                                                                    {gData.items.map(item => (
                                                                                        <div key={item.id} className="py-1.5 px-3">
                                                                                            <div className="flex items-start gap-2">
                                                                                                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.isCompleted ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'}`}>
                                                                                                    {item.isCompleted ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                                                                                                </div>
                                                                                                <div className="flex-1 min-w-0">
                                                                                                    <p className="text-sm text-gray-800">
                                                                                                        {item.checklistItemName}
                                                                                                        {item.isRequired && <span className="text-red-500 ml-1">*</span>}
                                                                                                        {item.isPhotoRequired && <span className="text-blue-500 ml-1 text-xs">📷</span>}
                                                                                                        {item.photoCount > 0 && (
                                                                                                            <button
                                                                                                                className="text-xs text-blue-500 hover:text-blue-700 hover:underline ml-2 inline-flex items-center gap-0.5"
                                                                                                                onClick={() => toggleSvItemPhotos(item.id)}
                                                                                                            >
                                                                                                                {svLoadingPhotos === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                                                                                                                {svPhotosExpanded[item.id] ? 'Hide' : 'View'} {item.photoCount} photo{item.photoCount > 1 ? 's' : ''}
                                                                                                                {svPhotosExpanded[item.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                                                                                            </button>
                                                                                                        )}
                                                                                                    </p>
                                                                                                    {(item.notes || item.remarks) && (
                                                                                                        <div className="flex gap-3 mt-0.5">
                                                                                                            {item.notes && <p className="text-xs text-gray-500"><span className="font-medium">Notes:</span> {item.notes}</p>}
                                                                                                            {item.remarks && <p className="text-xs text-gray-500"><span className="font-medium">Remarks:</span> {item.remarks}</p>}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                            {svPhotosExpanded[item.id] && svItemPhotos[item.id]?.length > 0 && (
                                                                                                <div className="ml-6 mt-2 flex gap-2 flex-wrap">
                                                                                                    {svItemPhotos[item.id].map((photo, idx) => (
                                                                                                        <img
                                                                                                            key={photo.id || idx}
                                                                                                            src={photo.url}
                                                                                                            alt={photo.fileName || 'Photo'}
                                                                                                            className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                                                                                            onClick={() => setSvEnlargedPhoto({ url: photo.url, fileName: photo.fileName })}
                                                                                                        />
                                                                                                    ))}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            ));
                                                                        })()}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            ) : (
                                                <Card className="border-blue-200">
                                                    <CardHeader className="bg-blue-50">
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                            <CardTitle>Site Visit 2 (SV2)</CardTitle>
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <Badge className="bg-gray-100 text-gray-800">Pending</Badge>
                                                                {isAdmin && projectData.currentStage === 'SV1 Completed' && (
                                                                    <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={openProposeSv2}>
                                                                        <CalendarIcon className="w-4 h-4 mr-1" />
                                                                        Propose SV2
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CardHeader>
                                                    <CardContent className="pt-6">
                                                        <p className="text-gray-500 text-center py-4">SV2 not yet scheduled.</p>
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </>
                                    )}
                                </TabsContent>
                            )}

                            {/* Installation Tab (Admin + OEM + Supervisor) */}
                            {(isAdmin || isOEM || isSupervisor) && showInstallationTab && (
                                <TabsContent value="installation" className="space-y-6 mt-0">
                                    {loadingInstallation ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                                            <span className="ml-2 text-gray-500">Loading installation details...</span>
                                        </div>
                                    ) : installationData ? (
                                        <>
                                            {/* Admin Actions Section */}
                                            {isAdmin && (
                                                <Card>
                                                    <CardHeader>
                                                        <CardTitle className="text-base">Admin Actions</CardTitle>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        {/* Supervisor */}
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                                                                    <User className="w-5 h-5 text-teal-600" />
                                                                </div>
                                                                <div>
                                                                    <p className="font-medium text-gray-900">{installationData.supervisorName || 'Not Assigned'}</p>
                                                                    <p className="text-xs text-gray-500">Project SuperAligner</p>
                                                                </div>
                                                            </div>
                                                            {isAdmin && (
                                                                <Button size="sm" variant="outline" onClick={handleOpenChangeSupervisor}>
                                                                    Change
                                                                </Button>
                                                            )}
                                                        </div>
                                                        {/* Assigned Teams */}
                                                        <Separator />
                                                        <div>
                                                            <div className="flex items-center justify-between mb-3">
                                                                <div className="flex items-center gap-2">
                                                                    <Users className="w-4 h-4 text-teal-600" />
                                                                    <p className="text-sm font-medium text-gray-900">Assigned Teams</p>
                                                                </div>
                                                                <Button size="sm" variant="outline" onClick={handleOpenAddTeam}>
                                                                    <Plus className="w-3 h-3 mr-1" /> Add Team
                                                                </Button>
                                                            </div>
                                                            {loadingTeams ? (
                                                                <div className="flex items-center justify-center py-4">
                                                                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                                                </div>
                                                            ) : projectTeams.length === 0 ? (
                                                                <p className="text-sm text-gray-400 text-center py-3">No teams assigned</p>
                                                            ) : (
                                                                <div className="space-y-2 field-row">
                                                                    {projectTeams.map(team => {
                                                                        const isExpanded = expandedTeamId === team.teamId;
                                                                        const members = teamMembersMap[team.teamId] || [];
                                                                        const isLoadingMembers = loadingMembersTeamId === team.teamId;
                                                                        return (
                                                                            <div key={team.teamId} className="rounded-lg border bg-gray-50 overflow-hidden">
                                                                                <div className="flex items-center justify-between p-2.5">
                                                                                    <div className="flex items-center gap-3">
                                                                                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                                                                            <Users className="w-4 h-4 text-purple-600" />
                                                                                        </div>
                                                                                        <div>
                                                                                            <p className="text-sm font-medium text-gray-900">{team.teamName}</p>
                                                                                            <div className="flex items-center gap-2">
                                                                                                <Badge variant="outline" className="text-xs">{team.skillLevel}</Badge>
                                                                                                <span className="text-xs text-gray-500">{team.memberCount} member{team.memberCount !== 1 ? 's' : ''}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex items-center gap-1">
                                                                                        <Button
                                                                                            size="sm"
                                                                                            variant="ghost"
                                                                                            className="h-8 px-2 text-xs"
                                                                                            onClick={() => handleToggleTeamMembers(team.teamId)}
                                                                                        >
                                                                                            {isExpanded ? (
                                                                                                <><ChevronUp className="w-3.5 h-3.5 mr-1" /> Hide</>
                                                                                            ) : (
                                                                                                <><Eye className="w-3.5 h-3.5 mr-1" /> View Members</>
                                                                                            )}
                                                                                        </Button>
                                                                                        <Button
                                                                                            size="sm"
                                                                                            variant="ghost"
                                                                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                                                                            onClick={() => handleRemoveTeam(team.teamId, team.teamName)}
                                                                                            disabled={removingTeamId === team.teamId}
                                                                                        >
                                                                                            {removingTeamId === team.teamId ? (
                                                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                                            ) : (
                                                                                                <X className="w-3.5 h-3.5" />
                                                                                            )}
                                                                                        </Button>
                                                                                    </div>
                                                                                </div>
                                                                                {isExpanded && (
                                                                                    <div className="bg-white border-t px-3 py-2">
                                                                                        {isLoadingMembers ? (
                                                                                            <div className="flex items-center justify-center py-3">
                                                                                                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                                                                                <span className="ml-2 text-xs text-gray-500">Loading members...</span>
                                                                                            </div>
                                                                                        ) : members.length === 0 ? (
                                                                                            <p className="text-xs text-gray-400 text-center py-2">No members in this team</p>
                                                                                        ) : (
                                                                                            <div className="space-y-1.5">
                                                                                                {members.filter(m => m.isActive !== false).map(m => (
                                                                                                    <div key={m.userId} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-gray-50">
                                                                                                        <div className="flex items-center gap-2 min-w-0">
                                                                                                            <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                                                                                                <User className="w-3.5 h-3.5 text-teal-700" />
                                                                                                            </div>
                                                                                                            <div className="min-w-0">
                                                                                                                <p className="text-xs font-medium text-gray-900 truncate">{m.userName}</p>
                                                                                                                <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                                                                                                    {m.role && <span className="px-1.5 py-0.5 rounded bg-gray-100">{m.role}</span>}
                                                                                                                    {m.email && <span className="truncate">{m.email}</span>}
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        {m.phoneNumber && (
                                                                                                            <a href={`tel:${m.phoneNumber}`} className="text-xs text-teal-600 hover:underline whitespace-nowrap">
                                                                                                                {m.phoneNumber}
                                                                                                            </a>
                                                                                                        )}
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* Progress Report Override */}
                                                        {(isAdmin || isSupervisor) && projectData.currentStage !== StageName.ProjectCompleted && (
                                                            <>
                                                                <Separator />
                                                                <div className="flex items-center justify-between">
                                                                    <div>
                                                                        <p className="text-sm font-medium text-gray-900">Allow Progress Report Anytime</p>
                                                                        <p className="text-xs text-gray-500">Override time window restrictions for installers</p>
                                                                    </div>
                                                                    <Switch
                                                                        checked={installationData.allowProgressReportAnytime}
                                                                        onCheckedChange={handleToggleOverride}
                                                                        disabled={togglingOverride}
                                                                        className="data-[state=checked]:bg-orange-500 data-[state=unchecked]:bg-gray-300 [&>span]:bg-white [&>span]:shadow"
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                        {isAdmin && (
                                                            <>
                                                                <Separator />
                                                                <div>
                                                                    <div className="flex items-center justify-between gap-4">
                                                                        <div className="flex-shrink-0">
                                                                            <p className="text-sm font-medium text-gray-900">Location Validation Radius</p>
                                                                            <p className="text-xs text-gray-500">
                                                                                Max distance (meters) from site for check-in & reports.
                                                                                {' '}Default: <span className="font-medium">50m</span>
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                                            <Input
                                                                                type="number"
                                                                                min="10"
                                                                                max="5000"
                                                                                placeholder="50 (default)"
                                                                                value={radiusInput}
                                                                                onChange={(e) => setRadiusInput(e.target.value)}
                                                                                className="w-32 h-8 text-sm"
                                                                            />
                                                                            <span className="text-xs text-gray-500">meters</span>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline"
                                                                                onClick={handleUpdateRadius}
                                                                                disabled={savingRadius}
                                                                                className="h-8"
                                                                            >
                                                                                {savingRadius ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                                                            </Button>
                                                                            {radiusInput && (
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="ghost"
                                                                                    onClick={async () => {
                                                                                        setSavingRadius(true);
                                                                                        try {
                                                                                            const result = await api.updateLocationRadius(id, null);
                                                                                            setInstallationData(prev => ({ ...prev, locationValidationRadius: null }));
                                                                                            setRadiusInput('');
                                                                                            toast.success(result.message);
                                                                                        } catch { toast.error('Failed to reset radius'); }
                                                                                        finally { setSavingRadius(false); }
                                                                                    }}
                                                                                    className="h-8 text-xs text-gray-500"
                                                                                >
                                                                                    Reset to default
                                                                                </Button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            )}

                                            {/* Supervisor: Progress Report Override (toggle only) */}
                                            {!isAdmin && isSupervisor && projectData.currentStage !== StageName.ProjectCompleted && (
                                                <Card>
                                                    <CardHeader>
                                                        <CardTitle className="text-base">Supervisor Actions</CardTitle>
                                                    </CardHeader>
                                                    <CardContent>
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">Allow Progress Report Anytime</p>
                                                                <p className="text-xs text-gray-500">Override time window restrictions for installers</p>
                                                            </div>
                                                            <Switch
                                                                checked={installationData.allowProgressReportAnytime}
                                                                onCheckedChange={handleToggleOverride}
                                                                disabled={togglingOverride}
                                                                className="data-[state=checked]:bg-orange-500 data-[state=unchecked]:bg-gray-300 [&>span]:bg-white [&>span]:shadow"
                                                            />
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            )}

                                            {/* Installation Details */}
                                            <Card>
                                                <CardHeader>
                                                    <div className="flex items-center justify-between">
                                                        <CardTitle>Installation Details</CardTitle>
                                                        <Badge className={getStatusColor(installationData.status)}>
                                                            {installationData.status}
                                                        </Badge>
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="space-y-6">
                                                    {/* Progress */}
                                                    <div>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <Label className="text-gray-600">Overall Progress</Label>
                                                            <span className="text-2xl font-bold text-teal-600">{installationData.progress}%</span>
                                                        </div>
                                                        <Progress value={installationData.progress} className="h-3" />
                                                    </div>

                                                    {/* Team & Schedule Details */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <Label className="text-gray-600">Team</Label>
                                                            <p className="text-gray-900 font-medium flex items-center gap-2">
                                                                <Users className="w-4 h-4 text-gray-500" />
                                                                {installationData.team || 'N/A'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <Label className="text-gray-600">Scheduled Date</Label>
                                                            <p className="text-gray-900 font-medium">
                                                                {installationData.scheduledDate ? new Date(installationData.scheduledDate).toLocaleDateString() : 'N/A'}
                                                            </p>
                                                        </div>
                                                        {installationData.startDate && (
                                                            <div>
                                                                <Label className="text-gray-600">Start Date</Label>
                                                                <p className="text-gray-900 font-medium">{new Date(installationData.startDate).toLocaleDateString()}</p>
                                                            </div>
                                                        )}
                                                        {installationData.completedDate && (
                                                            <div>
                                                                <Label className="text-gray-600">Completed Date</Label>
                                                                <p className="text-gray-900 font-medium">{new Date(installationData.completedDate).toLocaleDateString()}</p>
                                                            </div>
                                                        )}
                                                        {installationData.teamInfo?.members?.length > 0 && (
                                                            <div className="md:col-span-2">
                                                                <Label className="text-gray-600">Team Members</Label>
                                                                <div className="flex flex-wrap gap-2 mt-1">
                                                                    {installationData.teamInfo.members.map((m, i) => (
                                                                        <span key={i} className="text-sm bg-gray-100 px-2 py-1 rounded">
                                                                            {m.name}{m.phone ? ` (${m.phone})` : ''}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Checklist Items - Entity → Unit → Group → Items */}
                                                    {installationData.entities?.length > 0 && (
                                                        <div>
                                                            <Label className="text-gray-600 mb-2 block">
                                                                Installation Checklist ({installationData.checklistCompleted}/{installationData.checklistTotal} approved)
                                                            </Label>
                                                            <div className="space-y-4">
                                                                {installationData.entities.map((entity) => {
                                                                    const isEntityOpen = instExpandedEntities[entity.entityId] === true;
                                                                    return (
                                                                        <div key={entity.entityId} className="border rounded-lg overflow-hidden">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setInstExpandedEntities(prev => ({ ...prev, [entity.entityId]: !isEntityOpen }))}
                                                                                className="w-full bg-gray-50 px-4 py-2 border-b flex items-center justify-between hover:bg-gray-100 transition-colors"
                                                                            >
                                                                                <p className="font-medium text-gray-800">{entity.entityName} <span className="text-xs text-gray-500">× {entity.quantity}</span></p>
                                                                                {isEntityOpen ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
                                                                            </button>
                                                                            {isEntityOpen && (
                                                                                <div className="divide-y">
                                                                                    {entity.units?.map((unit) => {
                                                                                        const isUnitOpen = instExpandedUnits[unit.unitId] === true;
                                                                                        return (
                                                                                            <div key={unit.unitId} className="px-4 py-3">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => setInstExpandedUnits(prev => ({ ...prev, [unit.unitId]: !isUnitOpen }))}
                                                                                                    className="w-full flex items-center justify-between mb-2 hover:bg-gray-50 rounded px-1 py-1 transition-colors"
                                                                                                >
                                                                                                    <p className="text-sm font-medium text-teal-700 flex items-center gap-1">
                                                                                                        {isUnitOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                                                                                        {unit.unitName || `Unit ${unit.unitNumber}`}
                                                                                                    </p>
                                                                                                    <span className="text-xs text-gray-500">
                                                                                                        {unit.approvedItems}/{unit.totalItems} approved
                                                                                                    </span>
                                                                                                </button>
                                                                                                {isUnitOpen && (
                                                                                                    <div className="space-y-2 field-row">
                                                                                                        {unit.groups?.map((group, gIdx) => (
                                                                                                            <div key={gIdx}>
                                                                                                                <p className="text-xs font-semibold text-gray-500 bg-gray-50 px-2 py-1 rounded mb-1">
                                                                                                                    {group.groupName} ({group.items?.filter(i => i.isApproved === true).length}/{group.items?.length})
                                                                                                                </p>
                                                                                                                <div className="space-y-1">
                                                                                                                    {group.items?.map((item, idx) => (
                                                                                                                        <div key={idx} className="py-1 px-2">
                                                                                                                            <div className="flex items-start gap-2">
                                                                                                                                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.isApproved === true ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                                                                                                                    {item.isApproved === true ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                                                                                                                                </div>
                                                                                                                                <div className="flex-1 min-w-0">
                                                                                                                                    <p className="text-sm text-gray-800">
                                                                                                                                        {item.itemName}
                                                                                                                                        {item.photoCount > 0 && (
                                                                                                                                            <button
                                                                                                                                                className="text-xs text-blue-500 hover:text-blue-700 hover:underline ml-2 inline-flex items-center gap-0.5"
                                                                                                                                                onClick={() => toggleSvItemPhotos(item.responseId)}
                                                                                                                                            >
                                                                                                                                                {svLoadingPhotos === item.responseId ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                                                                                                                                                {svPhotosExpanded[item.responseId] ? 'Hide' : 'View'} {item.photoCount} photo{item.photoCount > 1 ? 's' : ''}
                                                                                                                                                {svPhotosExpanded[item.responseId] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                                                                                                                            </button>
                                                                                                                                        )}
                                                                                                                                    </p>
                                                                                                                                    {item.notes && (
                                                                                                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                                                                                                            <span className="font-medium">Notes:</span> {item.notes}
                                                                                                                                        </p>
                                                                                                                                    )}
                                                                                                                                </div>
                                                                                                                            </div>
                                                                                                                            {svPhotosExpanded[item.responseId] && svItemPhotos[item.responseId]?.length > 0 && (
                                                                                                                                <div className="ml-6 mt-2 flex gap-2 flex-wrap">
                                                                                                                                    {svItemPhotos[item.responseId].map((photo, pidx) => (
                                                                                                                                        <img
                                                                                                                                            key={photo.id || pidx}
                                                                                                                                            src={photo.url}
                                                                                                                                            alt={photo.fileName || 'Photo'}
                                                                                                                                            className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                                                                                                                            onClick={() => setSvEnlargedPhoto({ url: photo.url, fileName: photo.fileName })}
                                                                                                                                        />
                                                                                                                                    ))}
                                                                                                                                </div>
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    ))}
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        ))}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            {/* Report Progress Section */}
                                            {(isAdmin || isSupervisor) && (
                                                <>
                                                    <Card>
                                                        <CardHeader>
                                                            <div className="flex items-center justify-between">
                                                                <CardTitle className="text-base">Report Progress</CardTitle>
                                                                <Button size="sm" variant="outline" onClick={fetchProgressReports} disabled={loadingProgressReports}>
                                                                    {loadingProgressReports ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load Reports'}
                                                                </Button>
                                                            </div>
                                                        </CardHeader>
                                                        <CardContent>
                                                            {loadingProgressReports ? (
                                                                <div className="flex items-center justify-center py-6">
                                                                    <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                                                                </div>
                                                            ) : progressReports.length === 0 ? (
                                                                <p className="text-sm text-gray-500 text-center py-4">No progress reports submitted yet. Click "Load Reports" to check.</p>
                                                            ) : (
                                                                <div className="space-y-3">
                                                                    {progressReports.map((report) => (
                                                                        <div key={report.id} className="border rounded-lg p-3 space-y-2">
                                                                            <div className="flex items-center justify-between">
                                                                                <div>
                                                                                    <div className="flex items-center gap-2">
                                                                                        <p className="text-sm font-medium text-gray-900">{report.updatedByName}</p>
                                                                                        {report.teamName && report.teamName !== 'N/A' && (
                                                                                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{report.teamName}</span>
                                                                                        )}
                                                                                    </div>
                                                                                    <p className="text-xs text-gray-500">
                                                                                        {new Date(report.updateDate).toLocaleString()} — {report.timeWindow || 'N/A'}
                                                                                        {report.isOverrideSubmission && (
                                                                                            <Badge variant="outline" className="ml-2 text-xs">Override</Badge>
                                                                                        )}
                                                                                    </p>
                                                                                </div>
                                                                                <div className="text-right">
                                                                                    <p className="text-xs text-gray-500">
                                                                                        📍 {report.latitude?.toFixed(5)}, {report.longitude?.toFixed(5)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                            {report.workDescription && (
                                                                                <p className="text-sm text-gray-700">{report.workDescription}</p>
                                                                            )}
                                                                            {report.issues && (
                                                                                <p className="text-sm text-red-600">Issues: {report.issues}</p>
                                                                            )}
                                                                            {/* Photo thumbnails */}
                                                                            {report.resolvedPhotos?.length > 0 && (
                                                                                <div>
                                                                                    <div className="flex items-center gap-2 flex-wrap mt-1">
                                                                                        {report.resolvedPhotos.map((url, idx) => (
                                                                                            <img
                                                                                                key={idx}
                                                                                                src={url}
                                                                                                alt={`Report photo ${idx + 1}`}
                                                                                                className="w-14 h-14 object-cover rounded border cursor-pointer hover:opacity-80"
                                                                                                onClick={() => setReportPhotosOpen({ photos: report.resolvedPhotos, index: idx })}
                                                                                            />
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </CardContent>
                                                    </Card>

                                                    {/* Report Photo Viewer */}
                                                    {reportPhotosOpen && (
                                                        <Dialog open={!!reportPhotosOpen} onOpenChange={() => setReportPhotosOpen(null)}>
                                                            <DialogContent className="max-w-[90vw] sm:max-w-2xl">
                                                                <DialogHeader>
                                                                    <DialogTitle>Progress Report Photo</DialogTitle>
                                                                </DialogHeader>
                                                                <img
                                                                    src={reportPhotosOpen.photos[reportPhotosOpen.index]}
                                                                    alt="Progress report"
                                                                    className="w-full max-h-[70vh] object-contain rounded"
                                                                />
                                                                {reportPhotosOpen.photos.length > 1 && (
                                                                    <div className="flex justify-center gap-2 mt-2">
                                                                        {reportPhotosOpen.photos.map((url, idx) => (
                                                                            <img
                                                                                key={idx}
                                                                                src={url}
                                                                                alt=""
                                                                                className={cn("w-12 h-12 object-cover rounded cursor-pointer border-2", idx === reportPhotosOpen.index ? "border-teal-500" : "border-gray-200")}
                                                                                onClick={() => setReportPhotosOpen({ ...reportPhotosOpen, index: idx })}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </DialogContent>
                                                        </Dialog>
                                                    )}
                                                </>
                                            )}

                                            {/* Attendance Records Section */}
                                            {isAdmin && (
                                                <Card>
                                                    <CardHeader>
                                                        <div className="flex items-center justify-between">
                                                            <CardTitle className="text-base">Attendance Records</CardTitle>
                                                            <Button size="sm" variant="outline" onClick={fetchAttendance} disabled={loadingAttendance}>
                                                                {loadingAttendance ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load Attendance'}
                                                            </Button>
                                                        </div>
                                                    </CardHeader>
                                                    <CardContent>
                                                        {loadingAttendance ? (
                                                            <div className="flex items-center justify-center py-6">
                                                                <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
                                                            </div>
                                                        ) : attendanceRecords.length === 0 ? (
                                                            <p className="text-sm text-gray-500 text-center py-4">No attendance records yet. Click "Load Attendance" to check.</p>
                                                        ) : (
                                                            <div className="overflow-x-auto">
                                                                <table className="w-full text-sm">
                                                                    <thead>
                                                                        <tr className="border-b bg-gray-50">
                                                                            <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                                                                            <th className="text-left py-2 px-3 font-medium text-gray-600">Installer</th>
                                                                            <th className="text-left py-2 px-3 font-medium text-gray-600">Team</th>
                                                                            <th className="text-left py-2 px-3 font-medium text-gray-600">Clock In</th>
                                                                            <th className="text-center py-2 px-3 font-medium text-gray-600">In Selfie</th>
                                                                            <th className="text-center py-2 px-3 font-medium text-gray-600">In Distance</th>
                                                                            <th className="text-left py-2 px-3 font-medium text-gray-600">Clock Out</th>
                                                                            <th className="text-center py-2 px-3 font-medium text-gray-600">Out Selfie</th>
                                                                            <th className="text-center py-2 px-3 font-medium text-gray-600">Out Distance</th>
                                                                            <th className="text-left py-2 px-3 font-medium text-gray-600">Hours Worked</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {attendanceRecords.map((record) => {
                                                                            const checkIn = record.checkInTime ? new Date(record.checkInTime) : null;
                                                                            const checkOut = record.checkOutTime ? new Date(record.checkOutTime) : null;
                                                                            const hoursWorked = record.hoursWorked != null ? record.hoursWorked : null;
                                                                            const hrs = hoursWorked != null ? Math.floor(hoursWorked) : null;
                                                                            const mins = hoursWorked != null ? Math.round((hoursWorked - hrs) * 60) : null;

                                                                            return (
                                                                                <tr key={record.id} className="border-b hover:bg-gray-50 transition-colors">
                                                                                    <td className="py-2 px-3 whitespace-nowrap">
                                                                                        {checkIn ? format(checkIn, 'dd MMM yyyy') : '—'}
                                                                                    </td>
                                                                                    <td className="py-2 px-3">
                                                                                        <span className="font-medium text-gray-900">{record.userName}</span>
                                                                                    </td>
                                                                                    <td className="py-2 px-3">
                                                                                        {record.teamName && record.teamName !== 'N/A' ? (
                                                                                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{record.teamName}</span>
                                                                                        ) : (
                                                                                            <span className="text-gray-300">—</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="py-2 px-3 whitespace-nowrap">
                                                                                        {checkIn ? format(checkIn, 'hh:mm a') : '—'}
                                                                                    </td>
                                                                                    <td className="py-2 px-3 text-center">
                                                                                        {record.selfieObjectUrl ? (
                                                                                            <Button
                                                                                                size="sm"
                                                                                                variant="ghost"
                                                                                                className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800"
                                                                                                onClick={() => setReportPhotosOpen({ photos: [record.selfieObjectUrl], index: 0 })}
                                                                                            >
                                                                                                <Eye className="w-3.5 h-3.5 mr-1" /> View
                                                                                            </Button>
                                                                                        ) : (
                                                                                            <span className="text-gray-300">—</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="py-2 px-3 text-center whitespace-nowrap">
                                                                                        {record.distanceFromSite != null ? (
                                                                                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${record.locationStatus === 'verified' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                                                                {Number(record.distanceFromSite).toFixed(0)}m
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span className="text-gray-300">—</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="py-2 px-3 whitespace-nowrap">
                                                                                        {checkOut ? (
                                                                                            <span className="text-green-700">{format(checkOut, 'hh:mm a')}</span>
                                                                                        ) : (
                                                                                            <span className="text-amber-600 text-xs font-medium">Pending</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="py-2 px-3 text-center">
                                                                                        {record.checkOutSelfieObjectUrl ? (
                                                                                            <Button
                                                                                                size="sm"
                                                                                                variant="ghost"
                                                                                                className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800"
                                                                                                onClick={() => setReportPhotosOpen({ photos: [record.checkOutSelfieObjectUrl], index: 0 })}
                                                                                            >
                                                                                                <Eye className="w-3.5 h-3.5 mr-1" /> View
                                                                                            </Button>
                                                                                        ) : (
                                                                                            <span className="text-gray-300">—</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="py-2 px-3 text-center whitespace-nowrap">
                                                                                        {record.checkOutDistanceFromSite != null ? (
                                                                                            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                                                                                                {Number(record.checkOutDistanceFromSite).toFixed(0)}m
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span className="text-gray-300">—</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="py-2 px-3 whitespace-nowrap">
                                                                                        {hoursWorked != null ? (
                                                                                            <span className="font-medium text-gray-900">{hrs}h {mins}m</span>
                                                                                        ) : (
                                                                                            <span className="text-gray-300">—</span>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </>
                                    ) : (
                                        <Card>
                                            <CardContent className="py-12 text-center">
                                                <Wrench className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                                <p className="text-gray-500">No installation details available yet</p>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {/* Change Supervisor Dialog */}
                                    <Dialog open={changeSupervisorOpen} onOpenChange={setChangeSupervisorOpen}>
                                        <DialogContent className="sm:max-w-[450px] max-h-[85dvh] sm:max-h-[70vh] overflow-y-auto">
                                            <DialogHeader>
                                                <DialogTitle>Change SuperAligner</DialogTitle>
                                                <DialogDescription>
                                                    Select a new supervisor for this project's installation.
                                                </DialogDescription>
                                            </DialogHeader>
                                            <div className="py-4">
                                                <Label className="text-gray-600 mb-2 block">Select Supervisor</Label>
                                                {allSupervisors.length === 0 ? (
                                                    <div className="flex items-center justify-center py-8">
                                                        <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
                                                        <span className="ml-2 text-sm text-gray-600">Loading supervisors...</span>
                                                    </div>
                                                ) : (
                                                    <Select value={selectedNewSupervisorId} onValueChange={setSelectedNewSupervisorId}>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Choose a supervisor..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {allSupervisors.map((supervisor) => {
                                                                const supId = supervisor.userId || supervisor.id;
                                                                return (
                                                                    <SelectItem key={supId} value={supId}>
                                                                        {supervisor.name}{supervisor.designation ? ` — ${supervisor.designation}` : ''}
                                                                    </SelectItem>
                                                                );
                                                            })}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            </div>
                                            <DialogFooter>
                                                <Button variant="outline" onClick={() => setChangeSupervisorOpen(false)}>Cancel</Button>
                                                <Button
                                                    className="bg-teal-600 hover:bg-teal-700"
                                                    onClick={handleSaveSupervisorChange}
                                                    disabled={savingSupervisor || !selectedNewSupervisorId}
                                                >
                                                    {savingSupervisor && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                    Update Supervisor
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>

                                    {/* Add Team Dialog */}
                                    <Dialog open={addTeamOpen} onOpenChange={setAddTeamOpen}>
                                        <DialogContent className="sm:max-w-[450px] max-h-[85dvh] sm:max-h-[70vh] overflow-y-auto">
                                            <DialogHeader>
                                                <DialogTitle>Add Team to Project</DialogTitle>
                                                <DialogDescription>
                                                    Select a team to assign to this project. Multiple teams can be assigned.
                                                </DialogDescription>
                                            </DialogHeader>
                                            <div className="py-4">
                                                <Label className="text-gray-600 mb-2 block">Select Team</Label>
                                                {allTeams.length === 0 ? (
                                                    <div className="flex items-center justify-center py-8">
                                                        <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
                                                        <span className="ml-2 text-sm text-gray-600">Loading teams...</span>
                                                    </div>
                                                ) : (
                                                    <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Choose a team..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {allTeams.map((team) => (
                                                                <SelectItem key={team.id} value={team.id}>
                                                                    {team.name} — {team.skillLevel} ({team.members ?? 0} members)
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            </div>
                                            <DialogFooter>
                                                <Button variant="outline" onClick={() => setAddTeamOpen(false)}>Cancel</Button>
                                                <Button
                                                    className="bg-teal-600 hover:bg-teal-700"
                                                    onClick={handleAddTeam}
                                                    disabled={addingTeam || !selectedTeamId}
                                                >
                                                    {addingTeam && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                    Add Team
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                </TabsContent>
                            )}

                            {/* Payments Tab */}
                            {(isAdmin || (isOEM && showPaymentsTab)) && projectData.currentStage !== StageName.NewProject && (
                                <TabsContent value="payments" className="space-y-6 mt-0">
                                    {loadingPayments ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                                        </div>
                                    ) : (
                                        <>
                                            {/* Summary Cards */}
                                            <Card>
                                                <CardHeader>
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                        <CardTitle>Payment Summary</CardTitle>
                                                        {isAdmin && (
                                                            <div className="flex flex-wrap gap-2">
                                                                <Button
                                                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                                                    onClick={() => setGenerateLinkOpen(true)}
                                                                >
                                                                    <Link className="w-4 h-4 mr-2" />
                                                                    Generate Payment Link
                                                                </Button>
                                                                <Button
                                                                    className="bg-teal-600 hover:bg-teal-700 text-white"
                                                                    onClick={() => setRecordPaymentOpen(true)}
                                                                >
                                                                    <Plus className="w-4 h-4 mr-2" />
                                                                    Record Payment
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="space-y-6">
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                        <div className="p-4 bg-gray-50 rounded-lg">
                                                            <Label className="text-gray-600">Total Amount</Label>
                                                            <p className="text-2xl font-bold text-gray-900">
                                                                ₹{(paymentSummary?.totalQuotationAmount ?? 0).toLocaleString()}
                                                            </p>
                                                        </div>
                                                        <div className="p-4 bg-green-50 rounded-lg">
                                                            <Label className="text-green-700">Paid</Label>
                                                            <p className="text-2xl font-bold text-green-700">
                                                                ₹{(paymentSummary?.totalPaid ?? 0).toLocaleString()}
                                                            </p>
                                                        </div>
                                                        <div className="p-4 bg-yellow-50 rounded-lg">
                                                            <Label className="text-yellow-700">Balance</Label>
                                                            <p className="text-2xl font-bold text-yellow-700">
                                                                ₹{(paymentSummary?.balance ?? 0).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Progress Bar */}
                                                    {paymentSummary?.totalQuotationAmount > 0 && (
                                                        <div>
                                                            <div className="flex items-center justify-between mb-2">
                                                                <Label>Payment Progress</Label>
                                                                <span className="text-sm font-medium">
                                                                    {Math.round(((paymentSummary?.totalPaid ?? 0) / paymentSummary.totalQuotationAmount) * 100)}%
                                                                </span>
                                                            </div>
                                                            <Progress
                                                                value={((paymentSummary?.totalPaid ?? 0) / paymentSummary.totalQuotationAmount) * 100}
                                                                className="h-3"
                                                            />
                                                        </div>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            {/* Payment Links */}
                                            {paymentSummary?.paymentLinks?.length > 0 && (
                                                <Card>
                                                    <CardHeader>
                                                        <CardTitle className="text-base">Payment Links</CardTitle>
                                                    </CardHeader>
                                                    <CardContent>
                                                        <div className="border rounded-lg overflow-x-auto">
                                                            <table className="w-full min-w-[700px]">
                                                                <thead className="bg-gray-50">
                                                                    <tr>
                                                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Created</th>
                                                                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Amount</th>
                                                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Status</th>
                                                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Expires</th>
                                                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y">
                                                                    {paymentSummary.paymentLinks.map((link) => (
                                                                        <tr key={link.id}>
                                                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                                                {new Date(link.createdAt).toLocaleDateString()}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                                                                                ₹{link.amount.toLocaleString()}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-center">
                                                                                <Badge className={
                                                                                    link.status === PaymentLinkStatus.Paid ? 'bg-green-100 text-green-800' :
                                                                                        link.status === PaymentLinkStatus.Expired ? 'bg-red-100 text-red-800' :
                                                                                            link.status === PaymentLinkStatus.Cancelled ? 'bg-gray-100 text-gray-800' :
                                                                                                'bg-blue-100 text-blue-800'
                                                                                }>
                                                                                    {link.status}
                                                                                </Badge>
                                                                            </td>
                                                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                                                {link.expiresAt ? new Date(link.expiresAt).toLocaleDateString() : '-'}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-center">
                                                                                <div className="flex justify-center gap-2">
                                                                                    {link.status !== PaymentLinkStatus.Cancelled && link.status !== PaymentLinkStatus.Paid && (
                                                                                        <>
                                                                                            <Button
                                                                                                size="sm"
                                                                                                variant="outline"
                                                                                                onClick={() => {
                                                                                                    navigator.clipboard.writeText(link.paymentLinkUrl);
                                                                                                    toast.success('Link copied to clipboard');
                                                                                                }}
                                                                                            >
                                                                                                <Copy className="w-3 h-3 mr-1" />
                                                                                                Copy
                                                                                            </Button>
                                                                                            <Button
                                                                                                size="sm"
                                                                                                variant="outline"
                                                                                                onClick={() => window.open(link.paymentLinkUrl, '_blank')}
                                                                                            >
                                                                                                <ExternalLink className="w-3 h-3 mr-1" />
                                                                                                Open
                                                                                            </Button>
                                                                                        </>
                                                                                    )}
                                                                                    {isAdmin && link.status !== PaymentLinkStatus.Cancelled && link.status !== PaymentLinkStatus.Paid && (
                                                                                        <>
                                                                                            <Button
                                                                                                size="sm"
                                                                                                variant="outline"
                                                                                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                                                                                onClick={() => handleMarkPaymentLinkPaid(link.id)}
                                                                                            >
                                                                                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                                                                                Mark Paid
                                                                                            </Button>
                                                                                            <Button
                                                                                                size="sm"
                                                                                                variant="outline"
                                                                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                                                onClick={() => handleCancelPaymentLink(link.id)}
                                                                                            >
                                                                                                <X className="w-3 h-3 mr-1" />
                                                                                                Cancel
                                                                                            </Button>
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            )}

                                            {/* Payment History */}
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle className="text-base">Payment History</CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                    {paymentSummary?.payments?.length > 0 ? (
                                                        <div className="border rounded-lg overflow-x-auto">
                                                            <table className="w-full min-w-[700px]">
                                                                <thead className="bg-gray-50">
                                                                    <tr>
                                                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
                                                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                                                                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Amount</th>
                                                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Reference</th>
                                                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Notes</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y">
                                                                    {paymentSummary.payments.map((payment) => (
                                                                        <tr key={payment.id}>
                                                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                                                {new Date(payment.paymentDate).toLocaleDateString()}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-sm text-gray-900 capitalize">
                                                                                {payment.paymentType === 'neft_online' ? 'NEFT/Online' :
                                                                                    payment.paymentType === 'upi' ? 'UPI' :
                                                                                        payment.paymentType.charAt(0).toUpperCase() + payment.paymentType.slice(1)}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                                                                                ₹{payment.amount.toLocaleString()}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                                                {payment.transactionReference || payment.chequeNumber || payment.upiId || '-'}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">
                                                                                {payment.notes || '-'}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <p className="text-gray-500 text-center py-6">No payments recorded yet.</p>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </>
                                    )}
                                </TabsContent>
                            )}

                            {/* Sign Off Tab */}
                            {showSignoffTab && (
                                <TabsContent value="signoff" className="space-y-6 mt-0">
                                    {signoffLoading ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                                        </div>
                                    ) : signoffDetails ? (
                                        <div className="space-y-6">
                                            {/* OEM Approval Info (shown for Admin, or for OEM after they've approved) */}
                                            {(isAdmin || projectData.currentStage !== StageName.PendingOemSignoff) && signoffDetails.oemApproval && (
                                                <Card>
                                                    <CardHeader>
                                                        <CardTitle className="text-base flex items-center gap-2">
                                                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                                                            OEM Approval
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent>
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                            <div>
                                                                <p className="text-sm text-gray-500">Approved By</p>
                                                                <p className="font-medium">{signoffDetails.oemApproval.approvedBy}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Approved On</p>
                                                                <p className="font-medium">
                                                                    {signoffDetails.oemApproval.approvedAt
                                                                        ? format(new Date(signoffDetails.oemApproval.approvedAt), 'dd MMM yyyy, hh:mm a')
                                                                        : '-'}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Rating</p>
                                                                <div className="flex items-center gap-1">
                                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                                        <Star
                                                                            key={star}
                                                                            className={`w-5 h-5 ${(signoffDetails.oemApproval?.rating || signoffDetails.rating) >= star
                                                                                ? 'text-yellow-500 fill-yellow-500'
                                                                                : 'text-gray-300'
                                                                                }`}
                                                                        />
                                                                    ))}
                                                                    <span className="ml-1 text-sm text-gray-600">
                                                                        {signoffDetails.oemApproval?.rating || signoffDetails.rating}/5
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {signoffDetails.oemApproval.notes && (
                                                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                                                <p className="text-sm text-gray-500 mb-1">Notes</p>
                                                                <p className="text-sm">{signoffDetails.oemApproval.notes}</p>
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            )}

                                            {/* Client Review Summary */}
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle className="text-base">Client Review Summary</CardTitle>
                                                    <CardDescription>
                                                        {signoffDetails.completedItems}/{signoffDetails.totalItems} items approved
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="space-y-4">
                                                        {signoffDetails.groups?.map((group, gi) => (
                                                            <div key={gi}>
                                                                <p className="text-sm font-semibold text-gray-700 mb-2">{group.groupName}</p>
                                                                <div className="space-y-2 field-row">
                                                                    {group.items?.map((item) => (
                                                                        <div key={item.id} className="p-3 bg-white border rounded-lg">
                                                                            <div className="flex items-center justify-between">
                                                                                <div className="flex items-center gap-2">
                                                                                    {item.isApproved ? (
                                                                                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                                                    ) : (
                                                                                        <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                                                                    )}
                                                                                    <div>
                                                                                        <span className="text-sm font-medium text-gray-900">{item.itemName}</span>
                                                                                        {item.isRequired && <span className="ml-1 text-xs text-red-500">*</span>}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                                                                    {item.photoCount > 0 && (
                                                                                        <button
                                                                                            className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
                                                                                            onClick={() => loadSignoffItemPhotos(item.id)}
                                                                                        >
                                                                                            📷 {signoffPhotosExpanded[item.id] ? 'Hide' : 'View'} Photos ({item.photoCount})
                                                                                        </button>
                                                                                    )}
                                                                                    {item.approvedBy && (
                                                                                        <span className="text-green-600">✓ {item.approvedBy}</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            {item.notes && (
                                                                                <p className="text-xs text-gray-500 mt-1 ml-6">
                                                                                    <span className="font-medium">Notes:</span> {item.notes}
                                                                                </p>
                                                                            )}
                                                                            {/* Photo thumbnails */}
                                                                            {signoffPhotosExpanded[item.id] && signoffItemPhotos[item.id] && (
                                                                                <div className="mt-2 ml-6 flex flex-wrap gap-2">
                                                                                    {signoffItemPhotos[item.id].map((photo) => (
                                                                                        <img
                                                                                            key={photo.id}
                                                                                            src={photo.url}
                                                                                            alt={photo.fileName}
                                                                                            className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                                                                                            onClick={() => setSignoffEnlargedPhoto(photo)}
                                                                                        />
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </CardContent>
                                            </Card>

                                            {/* Sign-off Photos */}
                                            {signoffDetails.signoffPhotos?.length > 0 && (
                                                <Card>
                                                    <CardHeader>
                                                        <CardTitle className="text-base">Sign-off Photos</CardTitle>
                                                    </CardHeader>
                                                    <CardContent>
                                                        <div className="flex flex-wrap gap-2">
                                                            {signoffDetails.signoffPhotos.map((photo, idx) => (
                                                                <div key={idx} className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                                                                    📷 {photo.fileName}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            )}

                                            {/* OEM: Approve Section (only at pending_oem_signoff) */}
                                            {isOEM && projectData.currentStage === StageName.PendingOemSignoff && (
                                                <Card className="border-pink-200 bg-pink-50/30">
                                                    <CardHeader>
                                                        <CardTitle className="text-base flex items-center gap-2">
                                                            <Star className="w-5 h-5 text-pink-600" />
                                                            Approve Sign-Off
                                                        </CardTitle>
                                                        <CardDescription>Rate the project and approve to move to admin closure</CardDescription>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        {/* Rating */}
                                                        <div>
                                                            <Label className="text-sm font-medium">Rating <span className="text-red-500">*</span></Label>
                                                            <div className="flex items-center gap-1 mt-1">
                                                                {[1, 2, 3, 4, 5].map((star) => (
                                                                    <button
                                                                        key={star}
                                                                        type="button"
                                                                        onClick={() => setSignoffRating(star)}
                                                                        onMouseEnter={() => setSignoffHoverRating(star)}
                                                                        onMouseLeave={() => setSignoffHoverRating(0)}
                                                                        className="p-1 transition-transform hover:scale-110"
                                                                    >
                                                                        <Star
                                                                            className={`w-8 h-8 ${(signoffHoverRating || signoffRating) >= star
                                                                                ? 'text-yellow-500 fill-yellow-500'
                                                                                : 'text-gray-300'
                                                                                }`}
                                                                        />
                                                                    </button>
                                                                ))}
                                                                {signoffRating > 0 && (
                                                                    <span className="ml-2 text-sm font-medium text-gray-600">{signoffRating}/5</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* Notes */}
                                                        <div>
                                                            <Label className="text-sm font-medium">Notes (optional)</Label>
                                                            <Textarea
                                                                value={signoffNotes}
                                                                onChange={(e) => setSignoffNotes(e.target.value)}
                                                                placeholder="Add any notes about the project..."
                                                                className="mt-1"
                                                                rows={3}
                                                            />
                                                        </div>
                                                        {/* Approve Button */}
                                                        <div className="flex justify-end">
                                                            <Button
                                                                className="bg-green-600 hover:bg-green-700"
                                                                onClick={handleApproveSignoff}
                                                                disabled={signoffSubmitting || signoffRating < 1}
                                                            >
                                                                {signoffSubmitting ? (
                                                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Approving...</>
                                                                ) : (
                                                                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Approve Sign-Off</>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            )}

                                            {/* Admin: Close Project Section (only at pending_admin_closure) */}
                                            {isAdmin && projectData.currentStage === StageName.PendingAdminClosure && (
                                                <Card className="border-green-200 bg-green-50/30">
                                                    <CardHeader>
                                                        <CardTitle className="text-base flex items-center gap-2">
                                                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                                                            Close Project
                                                        </CardTitle>
                                                        <CardDescription>Review details above and close this project</CardDescription>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        <div>
                                                            <Label className="text-sm font-medium">Notes (optional)</Label>
                                                            <Textarea
                                                                value={signoffNotes}
                                                                onChange={(e) => setSignoffNotes(e.target.value)}
                                                                placeholder="Add any closure notes..."
                                                                className="mt-1"
                                                                rows={3}
                                                            />
                                                        </div>
                                                        <div className="flex justify-end">
                                                            <Button
                                                                className="bg-green-600 hover:bg-green-700"
                                                                onClick={handleAdminCloseProject}
                                                                disabled={signoffSubmitting}
                                                            >
                                                                {signoffSubmitting ? (
                                                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Closing...</>
                                                                ) : (
                                                                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Close Project</>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            )}

                                            {/* Completed badge for project_completed stage */}
                                            {projectData.currentStage === StageName.ProjectCompleted && (
                                                <Card className="border-green-200 bg-green-50/30">
                                                    <CardContent className="flex items-center gap-3 py-4">
                                                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                                                        <div>
                                                            <p className="font-semibold text-green-800">Project Completed</p>
                                                            <p className="text-sm text-green-600">
                                                                This project has been signed off and closed
                                                                {projectData.completedDate && ` on ${format(new Date(projectData.completedDate), 'dd MMM yyyy')}`}
                                                            </p>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </div>
                                    ) : (
                                        <Card>
                                            <CardContent className="text-center py-8 text-gray-500">
                                                Failed to load sign-off details
                                            </CardContent>
                                        </Card>
                                    )}
                                </TabsContent>
                            )}

                            {/* History Tab */}
                            <TabsContent value="history" className="space-y-6 mt-0">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Project Timeline</CardTitle>
                                        <CardDescription>Chronological history of all project events</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {loadingTimeline ? (
                                            <div className="flex items-center justify-center py-8">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                                                <span className="ml-2 text-gray-500">Loading history...</span>
                                            </div>
                                        ) : timelineEvents.length === 0 ? (
                                            <div className="text-center py-8 text-gray-500">
                                                <p>No history events found</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {timelineEvents.map((event, index) => (
                                                    <div key={event.id || index} className="flex gap-4">
                                                        <div className="flex flex-col items-center">
                                                            <div className="w-3 h-3 bg-teal-600 rounded-full"></div>
                                                            {index < timelineEvents.length - 1 && (
                                                                <div className="w-0.5 h-full bg-gray-300 mt-1"></div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 pb-6">
                                                            <p className="font-medium text-gray-900">{event.action}</p>
                                                            {event.description && (
                                                                <p className="text-sm text-gray-600">{event.description}</p>
                                                            )}
                                                            <p className="text-xs text-gray-400 mt-1">
                                                                {event.performedAt ? format(new Date(event.performedAt), 'dd MMM yyyy, hh:mm a') : '—'}
                                                                {event.performedBy && ` • by ${event.performedBy}`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Requests Tab */}
                            <TabsContent value="requests" className="space-y-6 mt-0">
                                <ProjectRequests projectId={id} isProjectCompleted={projectData.currentStage === StageName.ProjectCompleted} />
                            </TabsContent>

                            {/* Notifications Tab (Admin) */}
                            {isAdmin && (
                                <TabsContent value="notifications" className="space-y-6 mt-0">
                                    <ProjectNotifications projectId={id} />
                                </TabsContent>
                            )}
                        </div>
                    </div>
                </Tabs>
            </div >

            {/* Map Modal */}
            <Dialog open={mapModalOpen} onOpenChange={handleMapModalOpenChange}>
                <DialogContent
                    className="max-w-full sm:max-w-5xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto"
                    onInteractOutside={handleInteractOutside}
                >
                    <DialogHeader className="pr-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <DialogTitle className="flex items-center gap-2">
                                <MapPin className="w-5 h-5" />
                                Project Location
                            </DialogTitle>
                            <div className="flex flex-wrap items-center gap-2">
                                {isEditMode && canEdit && (
                                    <>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleClearLocation}
                                            disabled={!mapLoaded || (!projectData.latitude && !projectData.longitude)}
                                        >
                                            Clear
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={getUserLocation}
                                            disabled={!mapLoaded}
                                        >
                                            <MapPin className="w-4 h-4 mr-1" />
                                            Use Live Location
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Site Address */}
                        <div className="space-y-2 field-row">
                            <Label htmlFor="edit-siteAddress">Site Address</Label>
                            <Input
                                id="edit-siteAddress"
                                value={projectData.location || ''}
                                onChange={(e) => setProjectData({ ...projectData, location: e.target.value })}
                                placeholder="Enter project site address"
                                readOnly={!isEditMode || !canEdit}
                                className={!isEditMode || !canEdit ? 'bg-gray-50' : ''}
                            />
                        </div>

                        {/* Location Search */}
                        {isEditMode && canEdit && (
                            <div className="space-y-2 field-row">
                                <Label htmlFor="edit-location-search">Search Location</Label>
                                <div className="relative" style={{ zIndex: 1 }}>
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <Input
                                        id="edit-location-search"
                                        placeholder="Search for a place..."
                                        className="pl-10"
                                        disabled={!mapLoaded}
                                        autoComplete="off"
                                    />
                                </div>
                                <p className="text-xs text-gray-500">
                                    Search for a location or click on the map to set project location
                                </p>
                            </div>
                        )}

                        {/* Map Container */}
                        <div>
                            <div className="relative w-full">
                                <div
                                    id="edit-project-map"
                                    className="w-full rounded-lg border border-gray-300"
                                    style={{ minHeight: '400px', height: '60vh' }}
                                >
                                    {!mapLoaded && (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
                                            <p className="text-gray-500">Loading map...</p>
                                        </div>
                                    )}
                                </div>
                                {/* Map Type Toggle Button */}
                                {mapLoaded && isEditMode && canEdit && (
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        className="absolute top-2 left-2 z-10 shadow-md"
                                        onClick={toggleMapType}
                                    >
                                        {mapType === 'roadmap' ? 'Satellite' : 'Roadmap'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* GPS Coordinates */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2 field-row">
                                <Label htmlFor="edit-latitude">Latitude {isEditMode && canEdit ? '(Auto-filled)' : ''}</Label>
                                <Input
                                    id="edit-latitude"
                                    value={projectData.latitude || ''}
                                    placeholder="19.076090"
                                    readOnly
                                    className="bg-gray-50 cursor-not-allowed"
                                />
                            </div>
                            <div className="space-y-2 field-row">
                                <Label htmlFor="edit-longitude">Longitude {isEditMode && canEdit ? '(Auto-filled)' : ''}</Label>
                                <Input
                                    id="edit-longitude"
                                    value={projectData.longitude || ''}
                                    placeholder="72.877426"
                                    readOnly
                                    className="bg-gray-50 cursor-not-allowed"
                                />
                            </div>
                        </div>

                        {/* View only: Link to Google Maps */}
                        {!isEditMode && projectData.latitude && projectData.longitude && (
                            <a
                                href={`https://www.google.com/maps?q=${projectData.latitude},${projectData.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700"
                            >
                                <Eye className="w-4 h-4" />
                                Open in Google Maps
                            </a>
                        )}

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setMapModalOpen(false)}>
                                Close
                            </Button>
                            {isEditMode && canEdit && (
                                <Button
                                    className="bg-teal-600 hover:bg-teal-700"
                                    onClick={handleSaveLocation}
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    Done
                                </Button>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog >

            {/* Upload Documents Dialog */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[85dvh] sm:max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Upload className="w-5 h-5" />
                            Upload Documents
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 mt-4">
                        {/* File Input */}
                        <div className="space-y-2 field-row">
                            <Label htmlFor="upload-file-input">Select Files</Label>
                            <Input
                                ref={uploadFileInputRef}
                                id="upload-file-input"
                                type="file"
                                multiple
                                onChange={handleUploadFileChange}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.bmp,.svg,.xls,.xlsx"
                            />
                            <p className="text-xs text-gray-500">
                                Supported: PDF, DOC, DOCX, Images (JPG, PNG, GIF, WebP, BMP, SVG), Excel (Max 10MB per file)
                            </p>
                        </div>

                        {/* Selected Files List */}
                        {filesToUpload.length > 0 && (
                            <div className="space-y-2 field-row">
                                <Label>Selected Files ({filesToUpload.length})</Label>
                                <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                                    {filesToUpload.map((fileObj, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                                            <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {fileObj.file.name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {(fileObj.file.size / 1024).toFixed(2)} KB
                                                </p>
                                            </div>
                                            <div className="flex-shrink-0 w-48">
                                                <Label htmlFor={`upload-category-${index}`} className="text-xs">Document Category</Label>
                                                <Input
                                                    id={`upload-category-${index}`}
                                                    type="text"
                                                    value={fileObj.subfolder}
                                                    onChange={(e) => {
                                                        const updated = [...filesToUpload];
                                                        updated[index].subfolder = e.target.value;
                                                        setFilesToUpload(updated);
                                                    }}
                                                    placeholder="Enter category..."
                                                    className="mt-1 text-sm"
                                                />
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removeUploadFile(index)}
                                                className="flex-shrink-0"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-2 pt-4 border-t">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setUploadDialogOpen(false);
                                    setFilesToUpload([]);
                                    if (uploadFileInputRef.current) {
                                        uploadFileInputRef.current.value = '';
                                    }
                                }}
                                disabled={isUploading}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleUploadDocuments}
                                disabled={filesToUpload.length === 0 || isUploading}
                                className="bg-teal-600 hover:bg-teal-700"
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        Upload {filesToUpload.length > 0 ? `${filesToUpload.length} File(s)` : ''}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Document</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                            Are you sure you want to delete <span className="font-semibold">{documentToDelete?.fileName}</span>?
                            This action cannot be undone.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setDeleteConfirmOpen(false);
                                    setDocumentToDelete(null);
                                }}
                                disabled={deletingDocId !== null}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => {
                                    if (documentToDelete?.type === 'quotation') {
                                        handleDeleteQuotationDocument(documentToDelete?.id, documentToDelete?.fileName);
                                    } else {
                                        handleDeleteDocument(documentToDelete?.id, documentToDelete?.fileName);
                                    }
                                }}
                                disabled={deletingDocId !== null}
                            >
                                {deletingDocId ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Quotation Confirmation Dialog */}
            <Dialog open={quotationDeleteConfirmOpen} onOpenChange={setQuotationDeleteConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Quotation</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                            Are you sure you want to delete quotation <span className="font-semibold">{quotationToDelete?.quotationNumber}</span>?
                            This action cannot be undone.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setQuotationDeleteConfirmOpen(false);
                                    setQuotationToDelete(null);
                                }}
                                disabled={deletingQuotation}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-red-600 hover:bg-red-700"
                                onClick={handleDeleteQuotation}
                                disabled={deletingQuotation}
                            >
                                {deletingQuotation ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* SV Checklist Photo Viewer Dialog */}
            <Dialog open={!!svEnlargedPhoto} onOpenChange={(open) => { if (!open) setSvEnlargedPhoto(null); }}>
                <DialogContent className="max-w-[90vw] sm:max-w-3xl p-2">
                    <DialogHeader className="pb-1">
                        <DialogTitle className="text-sm truncate">{svEnlargedPhoto?.fileName}</DialogTitle>
                    </DialogHeader>
                    {svEnlargedPhoto && (
                        <div className="flex items-center justify-center">
                            <img
                                src={svEnlargedPhoto.url}
                                alt={svEnlargedPhoto.fileName || 'Photo'}
                                className="max-w-full max-h-[75vh] object-contain rounded-lg"
                            />
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Image Viewer with yet-another-react-lightbox */}
            <Lightbox
                open={imageViewerOpen}
                close={() => setImageViewerOpen(false)}
                index={currentImageIndex}
                slides={(() => {
                    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
                    const imageDocuments = documents.filter(d => imageTypes.includes(d.mimeType?.toLowerCase()));

                    return imageDocuments.map(doc => ({
                        src: imageUrls[doc.id] || '',
                        alt: doc.fileName,
                        title: doc.fileName,
                        description: `${doc.subfolder ? formatSubfolder(doc.subfolder) + ' • ' : ''}${formatFileSize(doc.fileSizeMB)}`
                    }));
                })()}
                on={{
                    view: ({ index }) => {
                        setCurrentImageIndex(index);
                        // Preload next image URL if not loaded
                        const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
                        const imageDocuments = documents.filter(d => imageTypes.includes(d.mimeType?.toLowerCase()));
                        const nextDoc = imageDocuments[index];
                        if (nextDoc && !imageUrls[nextDoc.id]) {
                            getDocumentSasUrl(nextDoc.id).then(url => {
                                setImageUrls(prev => ({ ...prev, [nextDoc.id]: url }));
                            }).catch(err => console.error('Failed to load image URL:', err));
                        }
                    }
                }}
                carousel={{
                    finite: true
                }}
            />

            {/* Quotation Preparation Modal */}
            <QuotationPreparationModal
                open={quotationModalOpen}
                onOpenChange={(open) => {
                    setQuotationModalOpen(open);
                    if (!open) {
                        setEditingQuotation(null);
                    }
                }}
                project={projectData}
                userRole={user?.role}
                quotationType={quotationType}
                existingQuotation={editingQuotation}
                onQuotationCreated={() => {
                    // Refresh quotations list
                    if (id) {
                        api.get(`/api/projects/${id}/quotations`)
                            .then(response => {
                                setQuotations(response.quotations || []);
                                toast.success(editingQuotation ? 'Quotation updated successfully' : 'Quotation created successfully');
                            })
                            .catch(error => {
                                console.error('Error refreshing quotations:', error);
                            });
                    }
                    setEditingQuotation(null);
                }}
            />

            {/* Additional Work Modal */}
            <AdditionalWorkModal
                open={additionalWorkModalOpen}
                onOpenChange={(open) => {
                    setAdditionalWorkModalOpen(open);
                    if (!open) setEditingQuotation(null);
                }}
                project={projectData}
                existingQuotation={editingQuotation}
                onQuotationCreated={() => {
                    if (id) {
                        api.get(`/api/projects/${id}/quotations`)
                            .then(response => {
                                setQuotations(response.quotations || []);
                            })
                            .catch(error => {
                                console.error('Error refreshing quotations:', error);
                            });
                    }
                }}
            />

            {/* Quotation Details Dialog */}
            <Dialog open={quotationDetailsOpen} onOpenChange={setQuotationDetailsOpen}>
                <DialogContent className="max-w-2xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Quotation Details</DialogTitle>
                        <DialogDescription>
                            View detailed information about this quotation
                        </DialogDescription>
                    </DialogHeader>
                    {selectedQuotation && (
                        <div className="space-y-6">
                            {/* Header Information */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm text-gray-600">Quotation Number</Label>
                                    <p className="font-semibold">
                                        {selectedQuotation.quotationNumber}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-sm text-gray-600">Date</Label>
                                    <p className="font-semibold">
                                        {selectedQuotation.createdAt
                                            ? new Date(selectedQuotation.createdAt).toLocaleDateString()
                                            : selectedQuotation.date}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-sm text-gray-600">Type</Label>
                                    <div className="mt-1">
                                        <Badge variant={selectedQuotation.quotationType === 'base_work' ? 'default' : 'secondary'}>
                                            {selectedQuotation.quotationType === 'base_work' ? 'Base Work' : 'Additional Work'}
                                        </Badge>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-sm text-gray-600">Status</Label>
                                    <div className="mt-1">
                                        <Badge className={getStatusColor(selectedQuotation.status)}>
                                            {formatQuotationStatus(selectedQuotation.status)}
                                        </Badge>
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* Amount Breakdown */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">Amount Breakdown</Label>
                                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Subtotal:</span>
                                        <span className="font-semibold">
                                            ₹{(selectedQuotation.totalAmount || selectedQuotation.amount)?.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">GST ({selectedQuotation.gstPercentage}%):</span>
                                        <span className="font-semibold">
                                            ₹{(selectedQuotation.taxAmount || 0)?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                    <Separator />
                                    <div className="flex justify-between text-lg">
                                        <span className="font-semibold">Grand Total:</span>
                                        <span className="font-bold text-teal-600">
                                            ₹{(selectedQuotation.grandTotal || selectedQuotation.amount)?.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Entity-Wise Pricing Breakdown */}
                            {selectedQuotation.quotationItems && Array.isArray(selectedQuotation.quotationItems) && selectedQuotation.quotationItems.length > 0 && (
                                <>
                                    <Separator />
                                    <div className="space-y-3">
                                        <Label className="text-base font-semibold">Entity-Wise Pricing Breakdown</Label>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm border border-gray-200">
                                                <thead>
                                                    <tr className="bg-gray-50">
                                                        <th className="p-2 border text-left">Entity</th>
                                                        <th className="p-2 border text-right">Unit Price (₹)</th>
                                                        <th className="p-2 border text-right">Sqft</th>
                                                        <th className="p-2 border text-right">Total (₹)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedQuotation.quotationItems.map((item, idx) => (
                                                        <tr key={idx} className="border-b hover:bg-gray-50">
                                                            <td className="p-2 border font-medium">{item.entityName || item.EntityName || 'Entity'}</td>
                                                            <td className="p-2 border text-right">{(item.unitPrice ?? item.UnitPrice ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                                            <td className="p-2 border text-right">{(item.sqft ?? item.Sqft ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                                            <td className="p-2 border text-right font-semibold">₹{(item.totalPrice ?? item.TotalPrice ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Line Items Summary */}
                            {(selectedQuotation.baseWorkAmount || selectedQuotation.extraWorkAmount > 0) && (
                                <>
                                    <Separator />
                                    <div className="space-y-3">
                                        <Label className="text-base font-semibold">Amount Details</Label>
                                        <div className="space-y-3">
                                            {/* Base Work */}
                                            {selectedQuotation.baseWorkAmount > 0 && (
                                                <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
                                                    <div className="flex items-center justify-between">
                                                        <h4 className="font-semibold text-gray-900">{selectedQuotation.baseWorkDescription || 'Base Work'}</h4>
                                                        <span className="text-lg font-bold text-blue-700">
                                                            ₹{selectedQuotation.baseWorkAmount?.toLocaleString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Extra Work */}
                                            {selectedQuotation.extraWorkAmount > 0 && (
                                                <div className="border border-gray-200 rounded-lg p-4 bg-purple-50">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="font-semibold text-gray-900">{selectedQuotation.extraWorkDescription || 'Extra Work'}</h4>
                                                        <span className="text-lg font-bold text-purple-700">
                                                            ₹{selectedQuotation.extraWorkAmount?.toLocaleString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Discount */}
                                            {selectedQuotation.discountAmount > 0 && (
                                                <div className="border border-gray-200 rounded-lg p-4 bg-green-50">
                                                    <div className="flex items-center justify-between">
                                                        <h4 className="font-semibold text-gray-900">
                                                            Discount
                                                            {selectedQuotation.discountType === 'percentage' && (
                                                                <span className="ml-2 text-sm font-normal text-gray-600">
                                                                    ({selectedQuotation.discountValue}%)
                                                                </span>
                                                            )}
                                                        </h4>
                                                        <span className="text-lg font-bold text-green-700">
                                                            -₹{selectedQuotation.discountAmount?.toLocaleString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* GST */}
                                            {selectedQuotation.gstPercentage > 0 && (
                                                <div className="border border-gray-200 rounded-lg p-4 bg-orange-50">
                                                    <div className="flex items-center justify-between">
                                                        <h4 className="font-semibold text-gray-900">
                                                            GST ({selectedQuotation.gstPercentage}%)
                                                        </h4>
                                                        <span className="text-lg font-bold text-orange-700">
                                                            ₹{selectedQuotation.taxAmount?.toLocaleString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Notes/Description */}
                            {(selectedQuotation.description || selectedQuotation.notes || selectedQuotation.remarks) && (
                                <>
                                    <Separator />
                                    <div className="space-y-2 field-row">
                                        <Label className="text-base font-semibold">
                                            {selectedQuotation.notes ? 'Notes' : 'Description'}
                                        </Label>
                                        <p className="text-gray-700">
                                            {selectedQuotation.notes || selectedQuotation.description || selectedQuotation.remarks}
                                        </p>
                                    </div>
                                </>
                            )}

                            {/* Quotation Documents */}
                            <Separator />
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label className="text-base font-semibold">Quotation Documents</Label>
                                </div>
                                {loadingQuotationDocuments ? (
                                    <div className="text-center py-4">
                                        <p className="text-gray-500">Loading documents...</p>
                                    </div>
                                ) : quotationDocuments.length > 0 ? (
                                    <div className="space-y-2 field-row">
                                        {quotationDocuments.map((doc) => (
                                            <div key={doc.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <FileText className="w-8 h-8 text-blue-600" />
                                                        <div>
                                                            <p className="font-medium text-gray-900">{doc.fileName}</p>
                                                            <p className="text-sm text-gray-500">
                                                                {doc.fileSizeMB.toFixed(2)} MB • Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleViewQuotationDocument(doc.id)}
                                                        >
                                                            <Eye className="w-4 h-4 mr-2" />
                                                            View
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleDownloadQuotationDocument(doc.id, doc.fileName)}
                                                        >
                                                            <Download className="w-4 h-4 mr-2" />
                                                            Download
                                                        </Button>
                                                        {canDeleteDocuments && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => confirmDeleteQuotationDocument(doc.id, doc.fileName)}
                                                                disabled={deletingDocId === doc.id}
                                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            >
                                                                {deletingDocId === doc.id ? (
                                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                ) : (
                                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                                )}
                                                                Remove
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
                                        <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                        <p className="text-sm text-gray-500">No documents uploaded yet</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setQuotationDetailsOpen(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Quotation Document Upload Dialog */}
            <Dialog open={quotationDocumentUploadOpen} onOpenChange={setQuotationDocumentUploadOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Upload Quotation Document</DialogTitle>
                        <DialogDescription>
                            Upload a PDF document for quotation {quotationForUpload?.quotationNumber}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="quotation-doc-upload">Select PDF File</Label>
                            <Input
                                ref={quotationDocFileInputRef}
                                id="quotation-doc-upload"
                                type="file"
                                accept=".pdf"
                                onChange={handleQuotationDocumentUpload}
                                disabled={uploadingQuotationDoc}
                                className="mt-2"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Only PDF files are allowed (Max 10MB)
                            </p>
                        </div>
                        {uploadingQuotationDoc && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Uploading document...
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setQuotationDocumentUploadOpen(false);
                                setQuotationForUpload(null);
                                if (quotationDocFileInputRef.current) {
                                    quotationDocFileInputRef.current.value = '';
                                }
                            }}
                            disabled={uploadingQuotationDoc}
                        >
                            Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Quotation Approval Dialog */}
            <QuotationApprovalDialog
                open={quotationApprovalOpen}
                onOpenChange={setQuotationApprovalOpen}
                quotation={quotationForApproval}
                projectId={projectData?.id}
                showFullDetails={true}
                onApprovalComplete={async () => {
                    setQuotationApprovalOpen(false);
                    try {
                        const response = await api.get(`/api/projects/${id}/quotations`);
                        setQuotations(response.quotations || []);
                    } catch (error) {
                        console.error('Error fetching quotations:', error);
                    }
                    fetchRealProjectData();
                    fetchSiteVisits();
                }}
            />

            {/* Generate Payment Link Modal */}
            <Dialog open={generateLinkOpen} onOpenChange={setGenerateLinkOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Generate Payment Link</DialogTitle>
                        <DialogDescription>
                            Create a Razorpay payment link to send to the client/OEM.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2 field-row">
                            <Label htmlFor="link-amount">Amount (₹) *</Label>
                            <Input
                                id="link-amount"
                                type="number"
                                min="1"
                                step="0.01"
                                placeholder="Enter amount"
                                value={linkForm.amount}
                                onChange={(e) => setLinkForm({ ...linkForm, amount: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2 field-row">
                            <Label htmlFor="link-description">Description (optional)</Label>
                            <Input
                                id="link-description"
                                placeholder="e.g., First installment payment"
                                value={linkForm.description}
                                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2 field-row">
                            <Label htmlFor="link-expiry">Link Expiry (hours)</Label>
                            <Input
                                id="link-expiry"
                                type="number"
                                min="1"
                                max="720"
                                value={linkForm.expiryHours}
                                onChange={(e) => setLinkForm({ ...linkForm, expiryHours: parseInt(e.target.value) || 72 })}
                            />
                            <p className="text-xs text-gray-500">Default: 72 hours (3 days)</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setGenerateLinkOpen(false)} disabled={generatingLink}>
                            Cancel
                        </Button>
                        <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleGeneratePaymentLink} disabled={generatingLink}>
                            {generatingLink && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Generate Link
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Record Payment Modal */}
            <Dialog open={recordPaymentOpen} onOpenChange={setRecordPaymentOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Record Payment</DialogTitle>
                        <DialogDescription>
                            Record an offline payment (cash, NEFT, cheque, or UPI).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4 max-h-[85dvh] sm:max-h-[60vh] overflow-y-auto">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2 field-row">
                                <Label htmlFor="pay-amount">Amount (₹) *</Label>
                                <Input
                                    id="pay-amount"
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    placeholder="Enter amount"
                                    value={paymentForm.amount}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2 field-row">
                                <Label htmlFor="pay-date">Payment Date *</Label>
                                <Input
                                    id="pay-date"
                                    type="date"
                                    value={paymentForm.paymentDate}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="space-y-2 field-row">
                            <Label>Payment Type *</Label>
                            <Select value={paymentForm.paymentType} onValueChange={(val) => setPaymentForm({ ...paymentForm, paymentType: val })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="neft_online">NEFT / Online Transfer</SelectItem>
                                    <SelectItem value="cash">Cash</SelectItem>
                                    <SelectItem value="cheque">Cheque</SelectItem>
                                    <SelectItem value="upi">UPI</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 field-row">
                            <Label htmlFor="pay-ref">Transaction Reference</Label>
                            <Input
                                id="pay-ref"
                                placeholder="Transaction ID / Reference number"
                                value={paymentForm.transactionReference}
                                onChange={(e) => setPaymentForm({ ...paymentForm, transactionReference: e.target.value })}
                            />
                        </div>

                        {/* Cheque-specific fields */}
                        {paymentForm.paymentType === 'cheque' && (
                            <div className="space-y-4 p-3 bg-gray-50 rounded-lg">
                                <Label className="font-semibold text-sm text-gray-700">Cheque Details</Label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2 field-row">
                                        <Label htmlFor="cheque-number">Cheque Number</Label>
                                        <Input
                                            id="cheque-number"
                                            placeholder="Cheque #"
                                            value={paymentForm.chequeNumber}
                                            onChange={(e) => setPaymentForm({ ...paymentForm, chequeNumber: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2 field-row">
                                        <Label htmlFor="cheque-date">Cheque Date</Label>
                                        <Input
                                            id="cheque-date"
                                            type="date"
                                            value={paymentForm.chequeDate}
                                            onChange={(e) => setPaymentForm({ ...paymentForm, chequeDate: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2 field-row">
                                    <Label htmlFor="cheque-bank">Bank Name</Label>
                                    <Input
                                        id="cheque-bank"
                                        placeholder="Bank name"
                                        value={paymentForm.chequeBankName}
                                        onChange={(e) => setPaymentForm({ ...paymentForm, chequeBankName: e.target.value })}
                                    />
                                </div>
                            </div>
                        )}

                        {/* UPI-specific fields */}
                        {paymentForm.paymentType === 'upi' && (
                            <div className="space-y-2 field-row">
                                <Label htmlFor="upi-id">UPI ID</Label>
                                <Input
                                    id="upi-id"
                                    placeholder="e.g., name@upi"
                                    value={paymentForm.upiId}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, upiId: e.target.value })}
                                />
                            </div>
                        )}

                        <div className="space-y-2 field-row">
                            <Label htmlFor="pay-notes">Notes</Label>
                            <Textarea
                                id="pay-notes"
                                placeholder="Any additional notes..."
                                value={paymentForm.notes}
                                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRecordPaymentOpen(false)} disabled={recordingPayment}>
                            Cancel
                        </Button>
                        <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleRecordPayment} disabled={recordingPayment}>
                            {recordingPayment && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Record Payment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit SV1 Dialog */}
            <Dialog open={editSv1Open} onOpenChange={setEditSv1Open}>
                <DialogContent className="sm:max-w-[550px] max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit SV1 Booking</DialogTitle>
                        <DialogDescription>
                            {siteVisitsData?.sv1?.startedAt
                                ? 'Site visit is in progress. You can only change the supervisor.'
                                : 'Update date, time slot, or supervisor for Site Visit 1.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5 py-4">
                        {/* Current Info */}
                        {siteVisitsData?.sv1 && (
                            <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                                <p><span className="text-gray-500">Current Date:</span> <span className="font-medium">{new Date(siteVisitsData.sv1.scheduledDate).toLocaleDateString()}</span></p>
                                <p><span className="text-gray-500">Current Slot:</span> <span className="font-medium">{siteVisitsData.sv1.slotName || 'N/A'}</span></p>
                                <p><span className="text-gray-500">Current SuperAligner:</span> <span className="font-medium">{siteVisitsData.sv1.supervisorName || 'N/A'}</span></p>
                            </div>
                        )}

                        {/* Step 1: Calendar Date Picker — hidden when in progress */}
                        {!siteVisitsData?.sv1?.startedAt && (
                            <div>
                                <Label className="text-sm font-medium">Step 1: Select Date *</Label>
                                <Popover open={editSv1CalendarOpen} onOpenChange={setEditSv1CalendarOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className={cn(
                                                "w-full justify-start text-left font-normal mt-1",
                                                !editSv1Date && "text-gray-500"
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {editSv1Date ? format(editSv1Date, 'PPP') : 'Select date'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={editSv1Date}
                                            onSelect={(date) => {
                                                setEditSv1Date(date);
                                                setEditSv1SlotId('');
                                                setEditSv1SupervisorId('');
                                                setEditSv1Supervisors([]);
                                                setEditSv1CalendarOpen(false);
                                                fetchEditSv1Slots(date);
                                            }}
                                            disabled={(date) => date < new Date() || isHoliday(date)}
                                            month={editSv1CalendarMonth}
                                            onMonthChange={(month) => {
                                                setEditSv1CalendarMonth(month);
                                                fetchEditSv1MonthAvailability(month);
                                                onHolidayMonthChange(month);
                                            }}
                                            modifiers={{
                                                available: editSv1MonthAvailability.availableDates,
                                                unavailable: editSv1MonthAvailability.unavailableDates,
                                                holiday: holidayDates
                                            }}
                                            modifiersClassNames={{
                                                available: 'bg-green-100 text-green-900 hover:bg-green-200',
                                                unavailable: 'bg-red-100 text-red-900 hover:bg-red-200',
                                                holiday: 'holiday-day'
                                            }}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        )}

                        {/* Step 2: Slot Selection Cards — hidden when in progress */}
                        {!siteVisitsData?.sv1?.startedAt && editSv1Date && (
                            <div>
                                <Label className="text-sm font-medium">Step 2: Select Time Slot *</Label>
                                {editSv1LoadingSlots ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-600">Loading slot availability...</span>
                                    </div>
                                ) : editSv1Slots.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                                        <p>No slots available for the selected date</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 mt-2">
                                        {editSv1Slots.map((slot) => (
                                            <div
                                                key={slot.slotId}
                                                className={cn(
                                                    "p-3 border-2 rounded-lg cursor-pointer transition-all flex items-center justify-between",
                                                    editSv1SlotId === slot.slotId.toString()
                                                        ? "border-blue-500 bg-blue-50"
                                                        : "border-gray-200 hover:border-gray-300",
                                                    !slot.isAvailable && "opacity-50 cursor-not-allowed"
                                                )}
                                                onClick={() => handleEditSv1SlotSelect(slot)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Clock className="h-4 w-4 text-gray-500" />
                                                    <div>
                                                        <p className="font-medium text-sm">{slot.slotName}</p>
                                                        <p className="text-xs text-gray-500">{slot.startTime} - {slot.endTime}</p>
                                                    </div>
                                                </div>
                                                {getEditSv1SlotBadge(slot)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 3: Supervisor Selection Cards */}
                        {(editSv1SlotId || siteVisitsData?.sv1?.startedAt) && (
                            <div>
                                <Label className="text-sm font-medium">Step 3: Select SuperAligner *</Label>
                                {editSv1LoadingSupervisors ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-600">Loading available supervisors...</span>
                                    </div>
                                ) : editSv1Supervisors.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                                        <p>No supervisors available for this slot</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 mt-2">
                                        {editSv1Supervisors.map((supervisor) => (
                                            <div
                                                key={supervisor.supervisorId}
                                                className={cn(
                                                    "p-3 border-2 rounded-lg cursor-pointer transition-all",
                                                    editSv1SupervisorId === supervisor.supervisorId
                                                        ? "border-blue-500 bg-blue-50"
                                                        : "border-gray-200 hover:border-gray-300"
                                                )}
                                                onClick={() => setEditSv1SupervisorId(supervisor.supervisorId)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <User className="h-4 w-4 text-blue-600" />
                                                    <div className="flex-1">
                                                        <p className="font-medium text-sm">{supervisor.name}</p>
                                                        <div className="flex gap-3 mt-1 text-xs text-gray-500">
                                                            {supervisor.designation && <span>📋 {supervisor.designation}</span>}
                                                            {supervisor.skillLevel && <span>⭐ {supervisor.skillLevel}</span>}
                                                            {supervisor.email && <span>📧 {supervisor.email}</span>}
                                                        </div>
                                                    </div>
                                                    {editSv1SupervisorId === supervisor.supervisorId && (
                                                        <CheckCircle2 className="h-5 w-5 text-blue-600" />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditSv1Open(false)}>Cancel</Button>
                        <Button
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={handleEditSv1Save}
                            disabled={editSv1Saving || !editSv1Date || !editSv1SlotId || !editSv1SupervisorId}
                        >
                            {editSv1Saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Update SV1
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit SV2 Dialog */}
            {/* Propose SV2 Dialog */}
            <Dialog open={proposeSv2Open} onOpenChange={setProposeSv2Open}>
                <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Propose Site Visit 2</DialogTitle>
                        <DialogDescription>
                            Propose a date, time slot and supervisor for SV2. OEM will need to approve.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4 pb-2">
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>Note:</strong> Site Visit 2 should be scheduled 2-3 days before the proposed start date,
                                or on confirmation of material arrival from OEM.
                            </p>
                        </div>

                        {/* Date picker */}
                        <div className="space-y-2 field-row">
                            <Label>Select Date *</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {proposeSv2Date ? format(proposeSv2Date, 'PPP') : 'Select a date'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={proposeSv2Date}
                                        onSelect={(date) => {
                                            setProposeSv2Date(date);
                                            if (date) fetchProposeSv2Slots(date);
                                        }}
                                        disabled={(date) => date < new Date() || isHoliday(date)}
                                        month={proposeSv2CalendarMonth}
                                        onMonthChange={(month) => {
                                            setProposeSv2CalendarMonth(month);
                                            fetchProposeSv2MonthAvailability(month);
                                            onHolidayMonthChange(month);
                                        }}
                                        modifiers={{
                                            available: proposeSv2MonthAvailability.availableDates,
                                            unavailable: proposeSv2MonthAvailability.unavailableDates,
                                            holiday: holidayDates
                                        }}
                                        modifiersClassNames={{
                                            available: 'bg-green-100 text-green-900 hover:bg-green-200',
                                            unavailable: 'bg-red-100 text-red-900 hover:bg-red-200',
                                            holiday: 'holiday-day'
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Time slots */}
                        {proposeSv2Date && (
                            <div className="space-y-2 field-row">
                                <Label>Select Time Slot *</Label>
                                {proposeSv2LoadingSlots ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Loading slots...
                                    </div>
                                ) : proposeSv2Slots.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        {proposeSv2Slots.map(slot => (
                                            <Button
                                                key={slot.slotId}
                                                variant="outline"
                                                className={`flex-col h-auto py-2 items-center ${proposeSv2SlotId === slot.slotId.toString()
                                                    ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                                                    : slot.isAvailable
                                                        ? 'border-green-400 hover:bg-green-50'
                                                        : 'border-red-300 bg-red-50 opacity-60 cursor-not-allowed'
                                                    }`}
                                                onClick={() => handleProposeSv2SlotSelect(slot)}
                                                disabled={!slot.isAvailable}
                                            >
                                                <span className="font-medium text-sm">{slot.slotName?.split('(')[0]?.trim()}</span>
                                                <Badge className={`mt-1 text-xs ${slot.isAvailable ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-red-500 text-white'}`}>
                                                    {slot.isAvailable ? `${slot.availableSupervisors} available` : 'Fully Booked'}
                                                </Badge>
                                            </Button>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">No slots available for this date</p>
                                )}
                            </div>
                        )}

                        {/* Supervisor selection */}
                        {proposeSv2SlotId && (
                            <div className="space-y-2 field-row">
                                <Label>Select SuperAligner *</Label>
                                {proposeSv2LoadingSupervisors ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Loading supervisors...
                                    </div>
                                ) : proposeSv2Supervisors.length > 0 ? (
                                    <Select value={proposeSv2SupervisorId} onValueChange={(val) => setProposeSv2SupervisorId(val)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Choose a supervisor" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {proposeSv2Supervisors.map(sup => (
                                                <SelectItem key={String(sup.supervisorId)} value={String(sup.supervisorId)}>
                                                    {sup.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <p className="text-sm text-red-600">No supervisors available for this slot</p>
                                )}
                            </div>
                        )}

                        <Button
                            className="w-full bg-sky-600 hover:bg-sky-700"
                            onClick={handleProposeSv2Submit}
                            disabled={!proposeSv2Date || !proposeSv2SlotId || !proposeSv2SupervisorId || proposeSv2Submitting}
                        >
                            {proposeSv2Submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            Propose Site Visit 2
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={editSv2Open} onOpenChange={setEditSv2Open}>
                <DialogContent className="sm:max-w-[550px] max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit SV2 Booking</DialogTitle>
                        <DialogDescription>
                            {siteVisitsData?.sv2?.startedAt
                                ? 'Site visit is in progress. You can only change the supervisor.'
                                : 'Update date, time slot, or supervisor for Site Visit 2.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5 py-4">
                        {/* Current Info */}
                        {siteVisitsData?.sv2 && (
                            <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                                <p><span className="text-gray-500">Current Date:</span> <span className="font-medium">{new Date(siteVisitsData.sv2.scheduledDate).toLocaleDateString()}</span></p>
                                <p><span className="text-gray-500">Current Slot:</span> <span className="font-medium">{siteVisitsData.sv2.slotName || 'N/A'}</span></p>
                                <p><span className="text-gray-500">Current SuperAligner:</span> <span className="font-medium">{siteVisitsData.sv2.supervisorName || 'N/A'}</span></p>
                            </div>
                        )}

                        {/* Step 1: Calendar Date Picker — hidden when in progress */}
                        {!siteVisitsData?.sv2?.startedAt && (
                            <div>
                                <Label className="text-sm font-medium">Step 1: Select Date *</Label>
                                <Popover open={editSv2CalendarOpen} onOpenChange={setEditSv2CalendarOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className={cn(
                                                "w-full justify-start text-left font-normal mt-1",
                                                !editSv2Date && "text-gray-500"
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {editSv2Date ? format(editSv2Date, 'PPP') : 'Select date'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={editSv2Date}
                                            onSelect={(date) => {
                                                setEditSv2Date(date);
                                                setEditSv2SlotId('');
                                                setEditSv2SupervisorId('');
                                                setEditSv2Supervisors([]);
                                                setEditSv2CalendarOpen(false);
                                                fetchEditSv2Slots(date);
                                            }}
                                            disabled={(date) => date < new Date() || isHoliday(date)}
                                            month={editSv2CalendarMonth}
                                            onMonthChange={(month) => {
                                                setEditSv2CalendarMonth(month);
                                                fetchEditSv2MonthAvailability(month);
                                                onHolidayMonthChange(month);
                                            }}
                                            modifiers={{
                                                available: editSv2MonthAvailability.availableDates,
                                                unavailable: editSv2MonthAvailability.unavailableDates,
                                                holiday: holidayDates
                                            }}
                                            modifiersClassNames={{
                                                available: 'bg-green-100 text-green-900 hover:bg-green-200',
                                                unavailable: 'bg-red-100 text-red-900 hover:bg-red-200',
                                                holiday: 'holiday-day'
                                            }}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        )}

                        {/* Step 2: Slot Selection Cards — hidden when in progress */}
                        {!siteVisitsData?.sv2?.startedAt && editSv2Date && (
                            <div>
                                <Label className="text-sm font-medium">Step 2: Select Time Slot *</Label>
                                {editSv2LoadingSlots ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-600">Loading slot availability...</span>
                                    </div>
                                ) : editSv2Slots.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                                        <p>No slots available for the selected date</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 mt-2">
                                        {editSv2Slots.map((slot) => (
                                            <div
                                                key={slot.slotId}
                                                className={cn(
                                                    "p-3 border-2 rounded-lg cursor-pointer transition-all flex items-center justify-between",
                                                    editSv2SlotId === slot.slotId.toString()
                                                        ? "border-blue-500 bg-blue-50"
                                                        : "border-gray-200 hover:border-gray-300",
                                                    !slot.isAvailable && "opacity-50 cursor-not-allowed"
                                                )}
                                                onClick={() => handleEditSv2SlotSelect(slot)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Clock className="h-4 w-4 text-gray-500" />
                                                    <div>
                                                        <p className="font-medium text-sm">{slot.slotName}</p>
                                                        <p className="text-xs text-gray-500">{slot.startTime} - {slot.endTime}</p>
                                                    </div>
                                                </div>
                                                {getEditSv2SlotBadge(slot)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 3: Supervisor Selection Cards */}
                        {(editSv2SlotId || siteVisitsData?.sv2?.startedAt) && (
                            <div>
                                <Label className="text-sm font-medium">Step 3: Select SuperAligner *</Label>
                                {editSv2LoadingSupervisors ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-600">Loading available supervisors...</span>
                                    </div>
                                ) : editSv2Supervisors.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                                        <p>No supervisors available for this slot</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 mt-2">
                                        {editSv2Supervisors.map((supervisor) => (
                                            <div
                                                key={supervisor.supervisorId}
                                                className={cn(
                                                    "p-3 border-2 rounded-lg cursor-pointer transition-all",
                                                    editSv2SupervisorId === supervisor.supervisorId
                                                        ? "border-blue-500 bg-blue-50"
                                                        : "border-gray-200 hover:border-gray-300"
                                                )}
                                                onClick={() => setEditSv2SupervisorId(supervisor.supervisorId)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <User className="h-4 w-4 text-blue-600" />
                                                    <div className="flex-1">
                                                        <p className="font-medium text-sm">{supervisor.name}</p>
                                                        <div className="flex gap-3 mt-1 text-xs text-gray-500">
                                                            {supervisor.designation && <span>📋 {supervisor.designation}</span>}
                                                            {supervisor.skillLevel && <span>⭐ {supervisor.skillLevel}</span>}
                                                            {supervisor.email && <span>📧 {supervisor.email}</span>}
                                                        </div>
                                                    </div>
                                                    {editSv2SupervisorId === supervisor.supervisorId && (
                                                        <CheckCircle2 className="h-5 w-5 text-blue-600" />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditSv2Open(false)}>Cancel</Button>
                        <Button
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={handleEditSv2Save}
                            disabled={editSv2Saving || !editSv2Date || !editSv2SlotId || !editSv2SupervisorId}
                        >
                            {editSv2Saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Update SV2
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sign-off Enlarged Photo Dialog */}
            {signoffEnlargedPhoto && (
                <Dialog open={!!signoffEnlargedPhoto} onOpenChange={() => setSignoffEnlargedPhoto(null)}>
                    <DialogContent className="max-w-[95vw] sm:max-w-3xl p-2">
                        <DialogHeader>
                            <DialogTitle className="text-sm">{signoffEnlargedPhoto.fileName}</DialogTitle>
                        </DialogHeader>
                        <img
                            src={signoffEnlargedPhoto.url}
                            alt={signoffEnlargedPhoto.fileName}
                            className="w-full max-h-[75vh] object-contain rounded"
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div >
    );
};

export default ProjectViewEdit;
